export type { Locale } from "./text";

/** 资源库（/resource）三语文案分片：按功能区拆分维护，统一聚合进 dict */
import { resPartPage } from "./strings-resource-page";
import { resPartUpload } from "./strings-resource-upload";
import { resPartGallery } from "./strings-resource-gallery";
import { resPartPreview } from "./strings-resource-preview";
import { resPartProduct } from "./strings-resource-product";
import { resPartDh } from "./strings-resource-dh";

export type MsgKey = keyof typeof dict;

/** 界面文案：英文默认；中文 / 印尼语仅手动切换后使用 */
export const dict = {
  navInspiration: { en: "Inspiration", zh: "灵感中心", id: "Inspirasi" },
  navFeatured: { en: "Featured", zh: "精选", id: "Pilihan" },
  navForYou: { en: "For You", zh: "推荐", id: "Untukmu" },
  navDrama: { en: "Drama", zh: "短剧", id: "Drama" },
  navCreate: { en: "Create", zh: "创作", id: "Buat" },
  navLive: { en: "AI Live", zh: "AI 直播", id: "AI Live" },
  navFollowing: { en: "Following", zh: "关注", id: "Mengikuti" },
  navMe: { en: "Me", zh: "我的", id: "Saya" },
  /** 管理员专属：订单（线下充值确认到账入口） */
  navOrders: { en: "Orders", zh: "订单", id: "Pesanan" },
  /** 资产库：素材 / 商品 / 数字人统一管理入口 */
  navAssetLibrary: { en: "Assets", zh: "资产库", id: "Aset" },
  openMenu: { en: "Menu", zh: "菜单", id: "Menu" },
  liveRetry: { en: "Retry", zh: "重试", id: "Coba lagi" },
  liveFrameFailed: { en: "Couldn't open this live page", zh: "直播页打开失败", id: "Gagal membuka halaman siaran" },
  liveAccessLoading: { en: "Checking access...", zh: "正在确认资格...", id: "Memeriksa akses..." },
  liveAccessFailed: { en: "Couldn't check AI Live access", zh: "暂时无法确认 AI 直播资格", id: "Gagal memeriksa akses AI Live" },
  liveLevelLow: {
    en: "Your membership level cannot use AI Live.",
    zh: "会员等级不足，不能使用 AI 直播",
    id: "Level keanggotaan Anda tidak dapat menggunakan AI Live.",
  },
  /** 会员子账户等级不足：不能自行升级，引导用主账号 */
  liveLevelLowSubAccount: {
    en: "Please upgrade membership with the owner account.",
    zh: "请使用主账号升级会员",
    id: "Silakan tingkatkan keanggotaan dengan akun pemilik.",
  },
  liveNotDesignated: {
    en: "This account has not been enabled for AI Live. Please contact support to apply.",
    zh: "当前账号尚未开通 AI直播账号，请联系客服进行申请。",
    id: "Akun ini belum diaktifkan untuk AI Live. Hubungi dukungan untuk mengajukan.",
  },
  /** 会员子账户未开通：不能自行发申请邮件，引导用主账号联系客服 */
  liveNotDesignatedSubAccount: {
    en: "Please contact support with the owner account.",
    zh: "请使用主账号联系客服",
    id: "Silakan hubungi dukungan dengan akun pemilik.",
  },
  liveProvisioning: {
    en: "Your AI Live account is being set up. Please wait.",
    zh: "AI直播账号开通中，请耐心等候",
    id: "Akun AI Live sedang disiapkan. Harap tunggu.",
  },
  /** 未开通资格页：联系客服申请开通 */
  liveContactSupport: { en: "Contact support", zh: "联系客服", id: "Hubungi dukungan" },
  /** 申请邮件发送成功后的提示 */
  liveApplySubmitted: {
    en: "Your application has been submitted. Please wait for customer support.",
    zh: "申请已提交，请等待客服处理",
    id: "Pengajuan telah dikirim. Harap tunggu layanan pelanggan.",
  },
  liveApplyDialogTitle: {
    en: "Apply for AI Live",
    zh: "申请开通 AI 直播",
    id: "Ajukan AI Live",
  },
  liveApplyEmailLabel: { en: "Your email", zh: "联系邮箱", id: "Email Anda" },
  liveApplyEmailPlaceholder: {
    en: "Enter your email",
    zh: "请输入您的邮箱",
    id: "Masukkan email Anda",
  },
  liveApplyConfirm: { en: "Confirm application", zh: "确认申请", id: "Konfirmasi pengajuan" },
  liveApplyEmailRequired: {
    en: "Please enter your email.",
    zh: "请输入联系邮箱",
    id: "Silakan masukkan email Anda.",
  },
  liveApplyEmailInvalid: {
    en: "Please enter a valid email.",
    zh: "请输入正确的邮箱格式",
    id: "Masukkan email yang valid.",
  },
  liveUpgrade: { en: "Upgrade", zh: "去升级", id: "Tingkatkan" },
  liveOperators: { en: "Manage live accounts", zh: "管理直播子账号", id: "Kelola akun live" },
  liveOperatorYes: { en: "Designated", zh: "已指定", id: "Ditunjuk" },
  liveOperatorNo: { en: "Not designated", zh: "未指定", id: "Belum ditunjuk" },
  liveOperatorEditTitle: { en: "Set live account", zh: "设置子账号", id: "Atur subakun" },
  liveOperatorAccount: { en: "Account", zh: "账号", id: "Akun" },
  liveOperatorNickname: { en: "Nickname", zh: "昵称", id: "Nama" },
  liveOperatorUserId: { en: "User ID", zh: "用户 ID", id: "ID pengguna" },
  liveOperatorApplyEmail: { en: "Apply email", zh: "申请邮箱", id: "Email pengajuan" },
  liveOperatorDesignated: { en: "Operator", zh: "是否运营", id: "Operator" },
  liveOperatorByteplus: { en: "BytePlus account", zh: "BytePlus 子账号", id: "Akun BytePlus" },
  liveOperatorByteplusConfig: {
    en: "Config BytePlus subAccount",
    zh: "配置 BytePlus 子账号",
    id: "Konfigurasi subakun BytePlus",
  },
  liveOperatorAction: { en: "Action", zh: "操作", id: "Aksi" },
  liveOperatorNameInvalid: {
    en: "Use 1-128 characters without spaces or & = # ?.",
    zh: "子账号须为 1～128 位，且不能包含空格、&、=、#、?",
    id: "Gunakan 1-128 karakter tanpa spasi atau & = # ?.",
  },
  liveOperatorNoLevel: {
    en: "No membership tier is enabled.",
    zh: "当前没有启用中的会员等级",
    id: "Belum ada tingkat keanggotaan yang aktif.",
  },
  liveOperatorEmpty: {
    en: "No members are in the highest tier.",
    zh: "还没有最高等级会员",
    id: "Belum ada anggota di tingkat tertinggi.",
  },
  liveOperatorLoadFailed: { en: "Couldn't load sub-accounts", zh: "子账号列表加载失败", id: "Gagal memuat subakun" },
  liveOperatorSaveFailed: { en: "Couldn't save. Please try again.", zh: "保存失败，请稍后重试", id: "Gagal menyimpan. Coba lagi." },
  liveConsoleLoading: { en: "Opening the console...", zh: "正在打开控制台...", id: "Membuka konsol..." },
  liveConsoleRoom: { en: "AI Live rooms", zh: "AI 直播间", id: "Ruang AI Live" },
  liveConsoleTraining: { en: "Model training", zh: "模型训练", id: "Pelatihan model" },
  liveConsoleAvatar: { en: "Avatar", zh: "虚拟形象", id: "Avatar" },
  liveConsoleVoice: { en: "Voice", zh: "语音", id: "Suara" },
  liveConsoleProducts: { en: "Product library", zh: "产品库", id: "Pustaka produk" },
  liveConsoleMaterials: { en: "Material library", zh: "素材库", id: "Pustaka materi" },
  liveConsoleKnowledge: { en: "Knowledge base", zh: "知识库", id: "Basis pengetahuan" },
  liveConsoleService: { en: "Service center", zh: "服务中心", id: "Pusat layanan" },

  searchPlaceholder: { en: "Search", zh: "搜索你感兴趣的内容", id: "Cari yang kamu suka" },
  searchBtn: { en: "Search", zh: "搜索", id: "Cari" },
  noResults: { en: "No results", zh: "没有找到结果", id: "Tidak ada hasil" },
  searchHint: {
    en: "Search videos, dramas, and people",
    zh: "搜索视频、短剧和用户",
    id: "Cari video, drama, dan pengguna",
  },
  searchResultCount: { en: "{n} results", zh: "{n} 条结果", id: "{n} hasil" },
  searchEmptyVideo: { en: "No matching videos", zh: "没有找到相关视频", id: "Tidak ada video yang cocok" },
  searchEmptyDrama: { en: "No matching dramas", zh: "没有找到相关短剧", id: "Tidak ada drama yang cocok" },
  searchEmptyUser: { en: "No matching people", zh: "没有找到相关用户", id: "Tidak ada pengguna yang cocok" },
  searchSeeAll: { en: "See all results", zh: "查看全部结果", id: "Lihat semua hasil" },
  searchYou: { en: "This is you", zh: "这是你自己", id: "Ini kamu" },
  trending: { en: "Trending", zh: "热门", id: "Populer" },
  goRecommend: { en: "See For You", zh: "去推荐看看", id: "Lihat Untukmu" },
  goFeatured: { en: "See Featured", zh: "去精选看看", id: "Lihat Pilihan" },
  kindVideo: { en: "Video", zh: "视频", id: "Video" },
  kindDrama: { en: "Drama", zh: "短剧", id: "Drama" },
  kindUser: { en: "User", zh: "用户", id: "Pengguna" },
  clickFollow: { en: "Tap to follow", zh: "点击关注", id: "Ketuk untuk mengikuti" },

  notice: { en: "Notifications", zh: "通知", id: "Notifikasi" },
  message: { en: "Messages", zh: "消息", id: "Pesan" },
  comments: { en: "Comments", zh: "评论", id: "Komentar" },
  themeLight: { en: "Light", zh: "浅色", id: "Terang" },
  themeDark: { en: "Dark", zh: "深色", id: "Gelap" },
  language: { en: "Language", zh: "语言", id: "Bahasa" },
  logout: { en: "Log out", zh: "退出", id: "Keluar" },
  login: { en: "Log in", zh: "登录", id: "Masuk" },

  noticeSystem: { en: "System", zh: "系统通知", id: "Sistem" },
  noticeLike: { en: "Like", zh: "点赞", id: "Suka" },
  noticeFollow: { en: "Follow", zh: "关注", id: "Ikuti" },
  noticeComment: { en: "Comment", zh: "评论", id: "Komentar" },
  noticeWelcome: {
    en: "Welcome to TidakTidur. New accounts get 200 Dream Beans.",
    zh: "欢迎来到 TidakTidur，注册即赠 200 梦想豆。",
    id: "Selamat datang di TidakTidur. Akun baru mendapat 200 Kacang Impian.",
  },
  noticeLikedBody: {
    en: "Glow Daily liked your work.",
    zh: "小美妆日常 赞了你的作品。",
    id: "Glow Daily menyukai karyamu.",
  },
  noticeFollowBody: {
    en: "Travel Sense followed you.",
    zh: "旅行的意义 关注了你。",
    id: "Travel Sense mengikutimu.",
  },
  noticeCommentBody: {
    en: "Night Walker commented: nice shot.",
    zh: "城市夜行者 评论：这个镜头很好看。",
    id: "Night Walker berkomentar: pengambilan gambarnya bagus.",
  },
  noticeDoneBody: {
    en: "Your creation is ready in My Works.",
    zh: "你的创作任务已完成，可在我的作品查看。",
    id: "Karyamu sudah siap di Karya Saya.",
  },
  noticeLikedTpl: {
    en: "{name} liked your work.",
    zh: "{name} 赞了你的作品。",
    id: "{name} menyukai karyamu.",
  },
  noticeFollowTpl: {
    en: "{name} followed you.",
    zh: "{name} 关注了你。",
    id: "{name} mengikutimu.",
  },
  noticeCommentTpl: {
    en: "{name} commented: {text}",
    zh: "{name} 评论：{text}",
    id: "{name} berkomentar: {text}",
  },
  noticeSomeone: { en: "Someone", zh: "有人", id: "Seseorang" },
  noticeEmpty: { en: "No notifications", zh: "暂无通知", id: "Belum ada notifikasi" },

  supportName: { en: "TidakTidur Support", zh: "TidakTidur 客服", id: "Dukungan TidakTidur" },
  msgOrder: { en: "Hi, want me to check your order?", zh: "你好，需要帮你看订单吗？", id: "Hai, perlu saya cek pesananmu?" },
  msgRecharge: {
    en: "To add Dream Beans, open plans and contact support.",
    zh: "充值请在套餐页联系客户经理。",
    id: "Untuk menambah Kacang Impian, buka paket dan hubungi dukungan.",
  },
  msgDownload: { en: "Your video is ready to download.", zh: "成片已可下载。", id: "Videomu siap diunduh." },
  msgUnlock: {
    en: "Episode 4 stays available after unlock.",
    zh: "短剧第 4 集解锁后永久可看。",
    id: "Episode 4 tetap bisa ditonton setelah dibuka.",
  },
  msgReply: { en: "Reply here if you need help.", zh: "有问题直接在这里回复。", id: "Balas di sini jika butuh bantuan." },
  timeJustNow: { en: "Now", zh: "刚刚", id: "Baru saja" },
  timeYesterday: { en: "Yesterday", zh: "昨天", id: "Kemarin" },
  timeMonday: { en: "Mon", zh: "周一", id: "Sen" },
  timeSunday: { en: "Sun", zh: "周日", id: "Min" },
  supportHello: {
    en: "Welcome to TidakTidur. Stay up a little. New accounts get 200 Dream Beans for picks, short dramas, and a video of your own.",
    zh: "欢迎来到 TidakTidur，先别睡。新账号已获赠 200 星豆，可以刷精选、追短剧，或做一条自己的片子。",
    id: "Selamat datang di TidakTidur. Jangan tidur dulu. Akun baru mendapat 200 Kacang Impian untuk tontonan pilihan, drama, dan videomu sendiri.",
  },
  sendMessage: { en: "Send a message", zh: "发送消息", id: "Kirim pesan" },
  send: { en: "Send", zh: "发送", id: "Kirim" },
  dm: { en: "Message", zh: "私信", id: "Pesan" },
  /** 登录人与作品作者是同一人时，点私信的提示 */
  dmCannotMessageSelf: {
    en: "You cannot message yourself.",
    zh: "不能发送私信给自己",
    id: "Kamu tidak bisa mengirim pesan ke dirimu sendiri.",
  },
  msgUserCollab: {
    en: "Can we collab on a shot like this?",
    zh: "这个镜头可以合作一条吗？",
    id: "Bisa kolaborasi untuk shot seperti ini?",
  },
  msgUserThanks: {
    en: "Thanks for the follow.",
    zh: "谢谢你的关注～",
    id: "Makasih sudah follow.",
  },
  msgUserAsk: {
    en: "When is the next series out?",
    zh: "下一个系列什么时候出？",
    id: "Kapan seri berikutnya rilis?",
  },
  msgUserNice: {
    en: "This clip is so steady.",
    zh: "你这条拍得太稳了。",
    id: "Klip ini sangat stabil.",
  },
  msgUserShop: {
    en: "Where was this filmed?",
    zh: "这条是在哪里拍的？",
    id: "Ini diambil di mana?",
  },
  msgUserStart: {
    en: "Say hello",
    zh: "打个招呼吧",
    id: "Sapa dulu",
  },
  myReplySupport: {
    en: "Got it, I'll check the plans page.",
    zh: "好的，我去套餐页看。",
    id: "Oke, saya cek halaman paket.",
  },
  myReplyCollab: {
    en: "Sure, I'll send you the details.",
    zh: "可以啊，我把细节发你。",
    id: "Bisa, nanti saya kirim detailnya.",
  },
  myReplyThanks: {
    en: "You're welcome.",
    zh: "不客气～",
    id: "Sama-sama.",
  },
  myReplyAsk: {
    en: "The next one is coming soon.",
    zh: "下一期很快就更。",
    id: "Yang berikutnya segera rilis.",
  },
  myReplyNice: {
    en: "Glad you liked it.",
    zh: "谢谢喜欢。",
    id: "Senang kamu suka.",
  },
  myReplyShop: {
    en: "It was filmed in Kuala Lumpur.",
    zh: "在吉隆坡拍的。",
    id: "Diambil di Kuala Lumpur.",
  },
  videoLabel: { en: "Video {id}", zh: "视频 {id}", id: "Video {id}" },
  presetComment: {
    en: "Night Walker: Steady shot. I want the next one.",
    zh: "城市夜行者：画面很稳，想看下一集。",
    id: "Night Walker: Gambarnya stabil. Mau episode berikutnya.",
  },
  mePrefix: { en: "Me: {text}", zh: "我：{text}", id: "Saya: {text}" },
  commentPlaceholder: { en: "Add a comment", zh: "发一条评论", id: "Tulis komentar" },
  commentRequired: { en: "Please enter a comment", zh: "请输入评论", id: "Masukkan komentar" },
  contentMissing: { en: "Content not found", zh: "内容不存在", id: "Konten tidak ditemukan" },

  catAll: { en: "All", zh: "全部", id: "Semua" },
  seriesDrama: { en: "Drama", zh: "短剧", id: "Drama" },
  seriesComic: { en: "Comic", zh: "漫剧", id: "Komik" },
  emptyDramaKind: { en: "No titles here yet.", zh: "该分类暂无内容。", id: "Belum ada judul di sini." },
  catLife: { en: "Life", zh: "生活", id: "Hidup" },
  catFamily: { en: "Family", zh: "亲子", id: "Keluarga" },
  catCar: { en: "Cars", zh: "汽车", id: "Mobil" },
  catBeauty: { en: "Beauty", zh: "美妆", id: "Kecantikan" },
  catFood: { en: "Food", zh: "美食", id: "Kuliner" },
  catTravel: { en: "Travel", zh: "旅行", id: "Wisata" },
  catFashion: { en: "Fashion", zh: "穿搭", id: "Fashion" },
  catSport: { en: "Sports", zh: "运动", id: "Olahraga" },
  catPet: { en: "Pets", zh: "宠物", id: "Hewan" },
  catDigital: { en: "Digital", zh: "数码", id: "Digital" },
  catKnowledge: { en: "Knowledge", zh: "知识", id: "Pengetahuan" },
  catComedy: { en: "Comedy", zh: "搞笑", id: "Komedi" },
  catHome: { en: "Home", zh: "家居", id: "Rumah" },
  catAppliance: { en: "Appliances", zh: "家电", id: "Elektronik" },
  catGame: { en: "Games", zh: "游戏", id: "Game" },
  catOther: { en: "Other", zh: "其他", id: "Lainnya" },

  follow: { en: "Follow", zh: "关注", id: "Ikuti" },
  followOk: { en: "Followed", zh: "关注成功", id: "Berhasil mengikuti" },
  likeOk: { en: "Liked", zh: "点赞成功", id: "Berhasil menyukai" },
  collectOk: { en: "Saved", zh: "收藏成功", id: "Berhasil disimpan" },
  openComments: { en: "Open comments", zh: "打开评论面板", id: "Buka komentar" },
  videoInfo: { en: "Details", zh: "详情", id: "Detail" },
  related: { en: "Related", zh: "相关推荐", id: "Terkait" },
  authorWorks: { en: "More from this creator", zh: "作者其他作品", id: "Karya lain kreator" },
  emptyAuthorWorks: { en: "No more videos", zh: "暂无更多作品", id: "Tidak ada video lain" },
  presetCommentShort: { en: "Preset comment: steady shot.", zh: "预置评论：画面很稳。", id: "Komentar bawaan: gambarnya stabil." },

  episodeCount: { en: "{n} eps", zh: "{n} 集", id: "{n} eps" },
  epLocked: { en: "Episode {n} is locked", zh: "第 {n} 集未解锁", id: "Episode {n} terkunci" },
  epName: { en: "Ep. {n}", zh: "第 {n} 集", id: "Eps. {n}" },
  epProgress: { en: "Ep. {n}/{total}", zh: "第{n}集/共{total}集", id: "Eps. {n}/{total}" },
  episodeList: { en: "Episodes", zh: "剧集", id: "Episode" },
  unlockTitle: { en: "Unlock episode {n}", zh: "解锁第 {n} 集", id: "Buka episode {n}" },
  unlockDesc: {
    en: "It stays unlocked after you confirm.",
    zh: "解锁后永久可看。",
    id: "Setelah dibuka, tetap bisa ditonton.",
  },
  unlockThisEpisode: { en: "Unlock this episode", zh: "解锁本集", id: "Buka episode ini" },
  unlockAllEpisodes: { en: "Unlock all episodes", zh: "解锁全部剧集", id: "Buka semua episode" },
  /** 单集解锁成功 */
  unlockEpisodeOk: { en: "Episode {n} unlocked", zh: "第 {n} 集已解锁", id: "Episode {n} telah dibuka" },
  /** 整剧解锁成功 */
  unlockAllOk: { en: "All episodes unlocked", zh: "全部剧集已解锁", id: "Semua episode telah dibuka" },
  unlockPriceBeans: { en: "{n} Dream Beans", zh: "{n} 梦想豆", id: "{n} Kacang Impian" },
  currentBalance: { en: "Balance {n}", zh: "当前余额 {n}", id: "Saldo {n}" },
  dramaPreparing: { en: "Preparing, please wait", zh: "正在准备，请稍候", id: "Sedang disiapkan, mohon tunggu" },
  dramaPlayFailed: { en: "This episode can't be played right now", zh: "暂时无法播放", id: "Episode ini belum bisa diputar" },
  /** 短剧播放页浏览器标题 `/drama/:id` */
  titleDramaPlay: { en: "Watch Drama", zh: "短剧播放", id: "Putar Drama" },
  /** 视频详情页浏览器标题 `/watch/:id` */
  titleWatchDetail: { en: "Video Details", zh: "视频详情", id: "Detail Video" },
  cancel: { en: "Cancel", zh: "取消", id: "Batal" },
  confirm: { en: "Confirm", zh: "确认", id: "Konfirmasi" },

  noFollowing: { en: "Not following anyone yet", zh: "还没有关注", id: "Belum mengikuti siapa pun" },
  followSearchPlaceholder: { en: "Search username or ID", zh: "搜索用户名或账号", id: "Cari nama atau ID" },
  myFollowing: { en: "Following ({n})", zh: "我的关注({n})", id: "Mengikuti ({n})" },
  addFollow: { en: "Add", zh: "添加", id: "Tambah" },
  userHandle: { en: "ID: {id}", zh: "账号：{id}", id: "ID: {id}" },
  likesReceived: { en: "{n} likes", zh: "获赞{n}", id: "{n} suka" },
  followerCount: { en: "{n} followers", zh: "粉丝{n}", id: "{n} pengikut" },
  unfollow: { en: "Unfollow", zh: "取消关注", id: "Berhenti mengikuti" },
  unfollowConfirmTitle: { en: "Unfollow this user?", zh: "确认取消关注？", id: "Berhenti mengikuti?" },
  unfollowConfirmDesc: {
    en: "Unfollow {name}? They will be removed from your Following list.",
    zh: "确定取消关注「{name}」？取消后将从「我的关注」中移除。",
    id: "Berhenti mengikuti {name}? Pengguna akan dihapus dari daftar Mengikuti.",
  },
  noUserVideos: { en: "This user has no videos", zh: "该用户还没有视频", id: "Pengguna ini belum punya video" },
  noFollowVideos: { en: "No videos yet", zh: "暂无视频", id: "Belum ada video" },
  followLoadFailed: { en: "Failed to load", zh: "加载失败", id: "Gagal memuat" },
  followRetry: { en: "Retry", zh: "重试", id: "Coba lagi" },
  loginToSeeMe: { en: "Log in to view Me", zh: "登录后查看我的", id: "Masuk untuk melihat Saya" },
  loginToSeeFollowing: { en: "Log in to view Following", zh: "登录后查看关注", id: "Masuk untuk melihat Mengikuti" },
  /** 游客在灵感中心的引导（与关注、我的同款，见 views/inspiration/index.vue） */
  loginToSeeInspiration: { en: "Log in to view Inspiration", zh: "登录后查看灵感中心", id: "Masuk untuk melihat Inspirasi" },
  /** 游客在推荐页的引导（与「我的」页同款，见 views/recommend/index.vue） */
  loginToSeeRecommend: { en: "Log in to view For You", zh: "登录后查看推荐", id: "Masuk untuk melihat Untukmu" },
  /** 游客在创作页的引导（与「我的」页同款，见 views/aiCreation/index.vue） */
  loginToSeeCreate: { en: "Log in to start creating", zh: "登录后开始创作", id: "Masuk untuk mulai berkarya" },
  /** 游客在订单页的引导（见 views/orders/index.vue） */
  loginToSeeOrders: { en: "Log in to view your orders", zh: "登录后查看你的订单", id: "Masuk untuk melihat pesanan Anda" },
  /** 游客在资产库的引导（与「我的」页同款，见 views/resource/index.vue） */
  loginToSeeResource: { en: "Log in to view your assets", zh: "登录后查看你的资产", id: "Masuk untuk melihat aset Anda" },
  /** 游客在精选页看到上限后，底部引导按钮 */
  loginToViewMore: {
    en: "Log in to see more",
    zh: "登录查看更多",
    id: "Masuk untuk melihat lebih banyak",
  },
  /** 游客点点赞 / 评论 / 收藏 / 私信 / 关注时的提示 */
  loginToInteract: {
    en: "Log in to like, comment and save",
    zh: "登录后可点赞、评论、收藏",
    id: "Masuk untuk menyukai, berkomentar, dan menyimpan",
  },
  /** 游客直连了「不在前 N 条窗口内」的详情页时的提示（替代「视频不可用」） */
  loginToViewFull: {
    en: "Log in to see all featured videos",
    zh: "登录后查看完整精选",
    id: "Masuk untuk melihat semua video pilihan",
  },
  accountLine: { en: "Following {n} · Account {name}", zh: "关注 {n} · 账号 {name}", id: "Mengikuti {n} · Akun {name}" },
  /** 「我的」页顶部社交数据标签 */
  statFollowing: { en: "Following", zh: "关注", id: "Mengikuti" },
  statFollowers: { en: "Followers", zh: "粉丝", id: "Pengikut" },
  statLikes: { en: "Likes", zh: "获赞", id: "Suka" },
  editProfile: { en: "Edit profile", zh: "编辑资料", id: "Edit profil" },
  beansBalance: { en: "Dream Beans {n}", zh: "梦想豆 {n}", id: "Kacang Impian {n}" },
  beans: { en: "Dream Beans", zh: "梦想豆", id: "Kacang Impian" },
  getBeans: { en: "Get Dream Beans", zh: "获取梦想豆", id: "Dapatkan Kacang Impian" },
  poolGift: { en: "Gift", zh: "赠送", id: "Hadiah" },
  poolMember: { en: "Membership", zh: "会员", id: "Keanggotaan" },
  poolRecharge: { en: "Recharge", zh: "充值", id: "Isi ulang" },
  beanSourceLegacy: { en: "Legacy", zh: "历史", id: "Lama" },
  poolNeverExpires: { en: "No expiry", zh: "不过期", id: "Tidak kedaluwarsa" },
  orders: { en: "Orders", zh: "我的订单", id: "Pesanan" },
  emptyOrders: { en: "No orders yet.", zh: "暂无订单。", id: "Belum ada pesanan." },
  selectPlan: { en: "Choose a plan", zh: "选择套餐", id: "Pilih paket" },
  tierStandard: { en: "Standard", zh: "标准", id: "Standar" },
  tierPopular: { en: "Popular", zh: "热门", id: "Populer" },
  tierValue: { en: "Value", zh: "超值", id: "Nilai" },
  tierBusiness: { en: "Business", zh: "企业推荐", id: "Bisnis" },
  tierBestDeal: { en: "Best deal", zh: "最优惠", id: "Terbaik" },
  payOffline: { en: "Pay via support", zh: "联系客服充值", id: "Bayar lewat dukungan" },
  orderId: { en: "Order ID", zh: "订单编号", id: "ID pesanan" },
  orderType: { en: "Order type", zh: "订单类型", id: "Jenis pesanan" },
  orderTypeTopup: { en: "Dream Beans top-up", zh: "充值梦想豆", id: "Isi ulang Kacang Impian" },
  amountDue: { en: "Amount due", zh: "应付金额", id: "Jumlah tagihan" },
  beansYouGet: { en: "Dream Beans you get", zh: "将获得梦想豆", id: "Kacang Impian yang didapat" },
  onlinePaySoon: {
    en: "Message support in-app and send your order ID.",
    zh: "私信联系客服，并告知订单编号。",
    id: "Hubungi dukungan lewat pesan, lalu beritahu nomor pesanan Anda.",
  },
  supportHours: {
    en: "Support hours: weekdays 9:00-18:00",
    zh: "客服在线时间：工作日 9:00-18:00",
    id: "Jam dukungan: hari kerja 09.00-18.00",
  },
  supportConfirmSla: {
    en: "Support will confirm payment within 1 business day.",
    zh: "客服将在 1 个工作日内确认到账",
    id: "Dukungan akan mengonfirmasi pembayaran dalam 1 hari kerja.",
  },
  csQrAlt: { en: "Support WeChat QR", zh: "客服二维码", id: "QR WeChat dukungan" },
  csQrMissing: {
    en: "Support QR is not configured. Cannot contact support here.",
    zh: "未配置客服微信二维码，无法联系客服。",
    id: "QR dukungan belum dikonfigurasi. Tidak dapat menghubungi dukungan di sini.",
  },
  contactHint: {
    en: "Contact support with your order ID. Dream Beans are added after confirmation.",
    zh: "请联系客服并告知订单编号，确认到账后梦想豆才会增加。",
    id: "Hubungi dukungan dengan ID pesanan Anda. Kacang Impian ditambahkan setelah konfirmasi.",
  },
  copied: { en: "Copied", zh: "已复制", id: "Disalin" },
  orderSubmitted: {
    en: "Order created. Dream Beans will update after support confirms.",
    zh: "订单已生成，客服确认到账后梦想豆才会增加。",
    id: "Pesanan dibuat. Kacang Impian akan diperbarui setelah dukungan mengonfirmasi.",
  },
  planBuyBeans: { en: "Top up", zh: "立即充值", id: "Isi ulang" },
  statusPending: { en: "Pending", zh: "待确认", id: "Menunggu" },
  statusConfirmed: { en: "Confirmed", zh: "已确认", id: "Dikonfirmasi" },
  demoConfirm: { en: "Demo: mark as paid", zh: "演示：模拟运营确认到账", id: "Demo: dukungan mengonfirmasi" },
  beansAdded: { en: "Dream Beans added", zh: "梦想豆已到账", id: "Kacang Impian ditambahkan" },
  tabWorks: { en: "Works", zh: "作品", id: "Karya" },
  tabFav: { en: "Saved", zh: "收藏", id: "Tersimpan" },
  tabDramaFav: { en: "Saved dramas", zh: "短剧收藏", id: "Drama tersimpan" },
  emptyContent: { en: "Nothing here", zh: "暂无内容", id: "Belum ada konten" },
  emptyWorks: { en: "No published works yet", zh: "还没有发布作品", id: "Belum ada karya" },
  emptyFav: { en: "Nothing saved yet", zh: "还没有收藏", id: "Belum ada yang disimpan" },
  // 精选页底部栏：加载更多 / 加载中 / 已到底（{n} = 作品总数）
  featuredLoadMore: { en: "Load more", zh: "加载更多", id: "Muat lagi" },
  featuredLoadingMore: { en: "Loading…", zh: "加载中…", id: "Memuat…" },
  featuredAllLoaded: {
    en: "You've reached the end · {n} works",
    zh: "已经到底啦，共 {n} 个作品",
    id: "Kamu sudah sampai akhir · {n} karya",
  },
  /** 推荐页上下滑没有上一条/下一条 */
  recommendEnd: {
    en: "You've reached the end",
    zh: "已经到底啦",
    id: "Kamu sudah sampai akhir",
  },
  // 精选页：加载失败 / 重试 / admin 入口
  featuredLoadFailed: { en: "Failed to load works", zh: "作品加载失败", id: "Gagal memuat karya" },
  featuredReload: { en: "Reload", zh: "重新加载", id: "Muat ulang" },
  featuredManageOfficial: { en: "Manage official videos", zh: "管理官方内置视频", id: "Kelola video resmi" },

  welcomeBack: { en: "Welcome back", zh: "欢迎回来", id: "Selamat datang kembali" },
  loginSubtitle: {
    en: "Sign in to keep creating",
    zh: "登录您的账户，继续精彩创作",
    id: "Masuk untuk lanjut membuat",
  },
  phoneLogin: { en: "Phone", zh: "手机登录", id: "Telepon" },
  accountLogin: { en: "Account", zh: "账号登录", id: "Akun" },
  phonePlaceholder: { en: "Phone number", zh: "请输入手机号", id: "Nomor telepon" },
  codePlaceholder: { en: "Verification code", zh: "请输入验证码", id: "Kode verifikasi" },
  getCode: { en: "Get code", zh: "获取验证码", id: "Kirim kode" },
  resendIn: { en: "Resend in {n}s", zh: "{n}s 后重发", id: "Kirim ulang {n}d" },
  autoRegister: {
    en: "A new account is created if this number is new.",
    zh: "未注册的手机号验证后将自动创建新账号",
    id: "Nomor baru akan membuat akun setelah diverifikasi.",
  },
  accountPlaceholder: { en: "Username", zh: "请输入账号", id: "Nama pengguna" },
  passwordPlaceholder: { en: "Password", zh: "请输入密码", id: "Kata sandi" },
  showPassword: { en: "Show password", zh: "显示密码", id: "Tampilkan kata sandi" },
  hidePassword: { en: "Hide password", zh: "隐藏密码", id: "Sembunyikan kata sandi" },
  rememberMe: { en: "Remember me", zh: "记住我", id: "Ingat saya" },
  forgotPassword: { en: "Forgot password?", zh: "忘记密码？", id: "Lupa kata sandi?" },
  resetPassword: { en: "Reset password", zh: "重置密码", id: "Atur ulang kata sandi" },
  resetPhonePlaceholder: { en: "Registered phone number", zh: "请输入注册手机号", id: "Nomor telepon terdaftar" },
  resetSmsPlaceholder: { en: "SMS code", zh: "请输入短信验证码", id: "Kode SMS" },
  resetNewPasswordPlaceholder: {
    en: "New password (6-16 characters)",
    zh: "请输入新密码（6-16位）",
    id: "Kata sandi baru (6-16 karakter)",
  },
  resetConfirmPasswordPlaceholder: {
    en: "Enter the new password again",
    zh: "请再次输入新密码",
    id: "Masukkan ulang kata sandi baru",
  },
  confirmReset: { en: "Reset", zh: "确认重置", id: "Atur ulang" },
  passwordLen616: {
    en: "Password must be 6-16 characters",
    zh: "密码长度为6-16位",
    id: "Kata sandi harus 6-16 karakter",
  },
  passwordResetOk: {
    en: "Password reset. Sign in with the new password.",
    zh: "密码已重置，请使用新密码登录",
    id: "Kata sandi diatur ulang. Masuk dengan kata sandi baru.",
  },
  loginOrRegister: { en: "Log in", zh: "登录", id: "Masuk" },
  // ── 邮箱注册（登录页第三个 Tab） ──
  emailRegisterTab: { en: "Sign up", zh: "注册", id: "Daftar" },
  registerTitle: { en: "Create account", zh: "创建账号", id: "Buat akun" },
  registerSubtitle: {
    en: "Sign up with your email and start creating",
    zh: "使用邮箱注册，立即开始创作",
    id: "Daftar dengan email dan mulai berkarya",
  },
  inviteCodePlaceholder: {
    en: "Invite code (optional)",
    zh: "邀请码（选填）",
    id: "Kode undangan (opsional)",
  },
  emailPlaceholder: { en: "Email address", zh: "请输入邮箱地址", id: "Alamat email" },
  emailCodePlaceholder: { en: "Email verification code", zh: "请输入邮箱验证码", id: "Kode verifikasi email" },
  registerPasswordPlaceholder: {
    en: "Password (6-16 characters)",
    zh: "设置密码（6-16位）",
    id: "Kata sandi (6-16 karakter)",
  },
  registerConfirmPasswordPlaceholder: {
    en: "Confirm password",
    zh: "请再次输入密码",
    id: "Konfirmasi kata sandi",
  },
  registerSubmit: { en: "Sign up", zh: "注册并登录", id: "Daftar" },
  registering: { en: "Signing up...", zh: "注册中...", id: "Mendaftar..." },
  emailRegisterHint: {
    en: "You will be signed in automatically after signing up",
    zh: "注册成功后将自动登录",
    id: "Anda akan masuk otomatis setelah mendaftar",
  },
  haveAccount: { en: "Already have an account?", zh: "已有账号？", id: "Sudah punya akun?" },
  goLogin: { en: "Log in", zh: "去登录", id: "Masuk" },
  noAccount: { en: "Don't have an account?", zh: "还没有账号？", id: "Belum punya akun?" },
  goRegister: { en: "Sign up", zh: "立即注册", id: "Daftar" },
  invalidEmail: { en: "Enter a valid email address", zh: "请输入正确的邮箱格式", id: "Masukkan alamat email yang valid" },
  enterEmail: { en: "Enter your email", zh: "请输入邮箱地址", id: "Masukkan email Anda" },
  enterConfirmPassword: { en: "Confirm your password", zh: "请再次输入密码", id: "Konfirmasi kata sandi" },
  emailCodeSent: {
    en: "Code sent to your email",
    zh: "验证码已发送到您的邮箱",
    id: "Kode telah dikirim ke email Anda",
  },
  agreePrefix: { en: "By continuing you agree to the", zh: "登录即表示您同意", id: "Dengan masuk, Anda setuju pada" },
  terms: { en: "Terms", zh: "服务条款", id: "Ketentuan" },
  and: { en: "and", zh: "和", id: "dan" },
  privacy: { en: "Privacy Policy", zh: "隐私政策", id: "Kebijakan Privasi" },
  close: { en: "Close", zh: "关闭", id: "Tutup" },
  agreeContinue: { en: "Agree and continue", zh: "同意并继续", id: "Setuju dan lanjut" },
  updatedOn: { en: "Last updated: 11 Jun 2026", zh: "最后更新日期：2026 年 6 月 11 日", id: "Terakhir diperbarui: 11 Jun 2026" },

  back: { en: "Back", zh: "返回", id: "Kembali" },
  next: { en: "Next", zh: "下一步", id: "Berikutnya" },
  prev: { en: "Previous", zh: "上一步", id: "Sebelumnya" },
  done: { en: "Done", zh: "完成", id: "Selesai" },
  detail: { en: "Detail", zh: "详情", id: "Detail" },
  edit: { en: "Edit", zh: "编辑", id: "Edit" },
  download: { en: "Download", zh: "下载", id: "Unduh" },
  downloadOk: { en: "Downloaded", zh: "下载成功", id: "Berhasil diunduh" },
  downloadStarted: { en: "Download started", zh: "已开始下载", id: "Pengunduhan dimulai" },
  rename: { en: "Rename", zh: "重命名", id: "Ganti nama" },
  delete: { en: "Delete", zh: "删除", id: "Hapus" },
  remove: { en: "Remove", zh: "移除", id: "Hapus" },
  preview: { en: "Preview", zh: "预览", id: "Pratinjau" },
  nameLabel: { en: "Name", zh: "名称", id: "Nama" },
  actionMenu: { en: "Actions", zh: "操作菜单", id: "Menu operasi" },
  storage: { en: "Storage", zh: "存储空间", id: "Penyimpanan" },
  status: { en: "Status", zh: "状态", id: "Status" },
  allStatuses: { en: "All statuses", zh: "全部状态", id: "Semua status" },
  inProgress: { en: "In progress", zh: "进行中", id: "Berlangsung" },
  statusDone: { en: "Done", zh: "已完成", id: "Selesai" },
  selectStatus: { en: "Status", zh: "选择状态", id: "Pilih status" },

  createTitle: { en: "Creation", zh: "自主创作", id: "Kreasi Mandiri" },
  createNew: { en: "New creation", zh: "新建创作", id: "Buat baru" },
  createNewHint: {
    en: "Click to start a new AI video",
    zh: "点击开始新的 AI 视频创作",
    id: "Klik untuk memulai kreasi video AI baru",
  },
  createSearch: { en: "Search creations...", zh: "搜索创作...", id: "Cari riwayat kreasi..." },
  recycleBin: { en: "Recycle bin", zh: "回收站", id: "Tempat sampah" },
  recycleHint: {
    en: "Items are permanently deleted after 7 days",
    zh: "回收站内容将在 7 天后自动永久删除",
    id: "Item dihapus permanen setelah 7 hari",
  },
  recycleEmpty: { en: "Recycle bin is empty", zh: "回收站为空", id: "Tempat sampah kosong" },
  recycleSelectAll: { en: "Select all", zh: "全选", id: "Pilih semua" },
  recycleInvert: { en: "Invert", zh: "反选", id: "Balik pilih" },
  recycleSelected: { en: "{n} selected", zh: "已选 {n} 项", id: "{n} dipilih" },
  recycleRestoreBatch: { en: "Restore", zh: "批量恢复", id: "Pulihkan" },
  recycleDeleteBatch: { en: "Delete", zh: "批量删除", id: "Hapus" },
  recycleDaysLeft: { en: "{n} days", zh: "{n} 天", id: "{n} hari" },
  recycleSelect: { en: "Select", zh: "选中", id: "Pilih" },
  recycleUnselect: { en: "Deselect", zh: "取消", id: "Batal pilih" },
  recycleRestore: { en: "Restore", zh: "恢复", id: "Pulihkan" },
  recyclePurge: { en: "Delete forever", zh: "永久删除", id: "Hapus permanen" },
  recyclePurgeDesc: {
    en: "Permanently delete {n} item(s)? This cannot be undone.",
    zh: "确定要永久删除所选的 {n} 项内容吗？此操作无法撤销。",
    id: "Hapus permanen {n} item? Tindakan ini tidak bisa dibatalkan.",
  },

  createStepScript: { en: "Script", zh: "脚本", id: "Naskah" },
  createStepEdit: { en: "Edit", zh: "编辑", id: "Edit" },
  createStepClips: { en: "Storyboard", zh: "分镜", id: "Storyboard" },
  createStepCompose: { en: "Compose", zh: "合成", id: "Sintesis" },

  createUploadScript: { en: "Upload script", zh: "上传脚本", id: "Unggah naskah" },
  createGenerateScript: { en: "Generate script", zh: "生成脚本", id: "Buat naskah" },
  createProductName: { en: "Product:", zh: "产品名称：", id: "Nama produk:" },
  createTarget: { en: "Audience:", zh: "目标人群：", id: "Target:" },
  createEnter: { en: "Enter", zh: "请输入", id: "Masukkan" },
  createIdeaPlaceholder: {
    en: "Describe your idea: what product, who is the audience, which selling points, how long, what style? AI will write a full script.",
    zh: "描述你的想法，例如：你要推广什么产品？目标用户是谁？想突出什么卖点？视频多长？喜欢什么风格？AI 将为你生成完整脚本。",
    id: "Jelaskan idemu: produk apa, siapa target, nilai jual apa, berapa lama, gaya seperti apa? AI akan membuat naskah lengkap.",
  },
  createIdeaPlaceholderShort: { en: "Describe your idea...", zh: "描述你的想法...", id: "Jelaskan idemu..." },
  createType: { en: "Type", zh: "类型", id: "Tipe" },
  createRatio: { en: "Ratio", zh: "画幅", id: "Rasio" },
  createDuration: { en: "Duration", zh: "时长", id: "Durasi" },
  createResolution: { en: "Resolution", zh: "分辨率", id: "Resolusi" },
  createTypeColon: { en: "Type:", zh: "类型：", id: "Tipe:" },
  createRatioColon: { en: "Ratio:", zh: "比例：", id: "Rasio:" },
  createDurationColon: { en: "Duration:", zh: "时长：", id: "Durasi:" },
  createSelectType: { en: "Select type", zh: "选择类型", id: "Pilih tipe" },
  createSelectRatio: { en: "Select ratio", zh: "选择比例", id: "Pilih rasio" },
  createSelectDuration: { en: "Select duration", zh: "选择时长", id: "Pilih durasi" },
  createFreeCreate: { en: "Free create", zh: "自由创作", id: "Kreasi bebas" },
  createCustom: { en: "Custom", zh: "自定义", id: "Kustom" },
  createCustomColon: { en: "Custom:", zh: "自定义：", id: "Kustom:" },
  createDurationSec: { en: "sec", zh: "秒", id: "detik" },
  createGenerate: { en: "Generate", zh: "智能生成", id: "Buat cerdas" },
  createUploadDoc: { en: "Upload doc", zh: "上传文档", id: "Unggah dokumen" },
  /** 已有脚本模式右上角：标准导入模板下载 */
  createTemplateWord: { en: "Word template", zh: "Word 模板", id: "Templat Word" },
  createTemplateExcel: { en: "Excel template", zh: "Excel 模板", id: "Templat Excel" },
  createUploadHint: {
    en: "Upload a script file or paste text. AI will parse it.",
    zh: "上传脚本文档，或粘贴已有脚本内容，AI 将自动为您解析分镜",
    id: "Unggah dokumen naskah atau tempel teks. AI akan mengurainya.",
  },
  createEditScriptPlaceholder: {
    en: "Edit your script here...",
    zh: "在此编辑您的脚本内容...",
    id: "Edit isi naskah di sini...",
  },
  createClearContent: { en: "Clear", zh: "清空", id: "Bersihkan" },
  createPasteText: { en: "Paste text", zh: "粘贴文本", id: "Tempel teks" },
  createPasteScript: { en: "Paste script", zh: "粘贴脚本", id: "Tempel naskah" },
  createPastePlaceholder: {
    en: "Paste your script. AI will split paragraphs and scenes...",
    zh: "粘贴脚本，AI 将自动拆分段落与分镜…",
    id: "Tempel naskah. AI akan mengurai paragraf dan adegan...",
  },
  createStartParse: { en: "Parse", zh: "开始解析", id: "Mulai urai" },
  createUploadOk: { en: "Uploaded", zh: "上传成功", id: "Unggah berhasil" },
  createUploading: { en: "Uploading... {n}%", zh: "上传中... {n}%", id: "Mengunggah... {n}%" },
  createAnalyzing: {
    en: "Writing the script from your description. About 1 min…",
    zh: "正在根据描述智能生成脚本，约 1 分钟…",
    id: "Membuat naskah dari deskripsimu, sekitar 1 menit…",
  },
  createAnalyzeStep1: {
    en: "Analyze selling points, use cases, and your brief",
    zh: "分析产品卖点、使用场景与创作需求",
    id: "Analisis nilai jual, skenario, dan kebutuhan kreasi",
  },
  createAnalyzeStep2: {
    en: "Build story logic and shot structure",
    zh: "梳理故事逻辑，搭建视频结构与分镜段落",
    id: "Susun logika cerita dan struktur shot",
  },
  createAnalyzeStep3: {
    en: "Align shot scripts, on-screen text, and layout",
    zh: "匹配分镜脚本、画面文案与排版",
    id: "Cocokkan naskah shot, teks layar, dan tata letak",
  },
  createAnalyzeStep4: {
    en: "Assemble the structured script",
    zh: "整合结构化视频脚本",
    id: "Susun naskah video terstruktur",
  },
  createBannedTitle: { en: "Restricted wording", zh: "检测到违禁词", id: "Terdeteksi kata terlarang" },
  createBannedHint: {
    en: "Change [{word}] to [{suggest}]",
    zh: "建议将 [{word}] 改为 [{suggest}]",
    id: "Ubah [{word}] menjadi [{suggest}]",
  },
  createChooseNext: { en: "Choose next step", zh: "选择下一步", id: "Pilih langkah berikutnya" },
  createGenStoryboard: { en: "Generate storyboard", zh: "生成分镜", id: "Buat storyboard" },
  createStoryboardHint: {
    en: "See each clip’s boards. Edit or regenerate per clip.",
    zh: "查看每段分镜画面，可按片段调整和重新生成",
    id: "Lihat papan tiap klip. Bisa atur dan buat ulang per klip.",
  },
  createSkipStoryboard: { en: "Skip storyboard preview", zh: "跳过分镜预览", id: "Lewati pratinjau storyboard" },
  createGoCompose: { en: "Go straight to compose", zh: "直接进入合成", id: "Langsung ke halaman sintesis" },
  createComposeVideo: { en: "Compose video", zh: "合成视频", id: "Sintesis video" },
  createComposeBtn: { en: "Compose", zh: "去合成", id: "Buat video" },
  createFinish: { en: "Finish", zh: "完成创作", id: "Selesai kreasi" },
  composeTitle: { en: "Confirm compose?", zh: "确认合成？", id: "Konfirmasi sintesis?" },
  composeDesc: {
    en: "After compose, content cannot be edited. Check boards, subtitles, and audio first.",
    zh: "合成后内容不可再改。请确认画面、字幕和音频无误。",
    id: "Setelah sintesis, konten tidak bisa diubah. Pastikan layar, subtitle, dan audio sudah benar.",
  },
  composeConfirm: { en: "Compose", zh: "确认合成", id: "Konfirmasi sintesis" },
  createQueued: {
    en: "Queued. Track it in Task Management.",
    zh: "任务已加入队列，可在任务管理查看。",
    id: "Tugas masuk antrean, lihat di Manajemen Tugas.",
  },
  createComposeOk: {
    en: "Compose succeeded. The clip is in My Works.",
    zh: "合成成功，成片已在「我的作品」。",
    id: "Sintesis berhasil. Video ada di Karya Saya.",
  },

  typeCatEcommerce: { en: "Selling video", zh: "带货视频", id: "Video jualan" },
  typeCatCommercial: { en: "Commercial", zh: "商业广告", id: "Iklan komersial" },
  typePainPoint: { en: "Pain point", zh: "痛点型", id: "Tipe masalah" },
  typeWelfare: { en: "Benefit hook", zh: "利益钩子", id: "Hook manfaat" },
  typeContrast: { en: "Contrast", zh: "对比型", id: "Tipe kontras" },
  typeImmersive: { en: "Immersive rec", zh: "沉浸种草", id: "Rekomendasi imersif" },
  typeStory: { en: "Story", zh: "故事型", id: "Tipe cerita" },
  typeReview: { en: "Unboxing review", zh: "开箱测评", id: "Unboxing review" },
  typeList: { en: "List", zh: "清单合集", id: "Koleksi daftar" },
  typeTutorial: { en: "Tutorial", zh: "教程", id: "Tutorial" },
  teachingVideo: { en: "Tutorial video", zh: "教学视频", id: "Video tutorial" },
  teachingVideoLoadFailed: {
    en: "Could not load the video. Please try again later.",
    zh: "视频加载失败，请稍后重试",
    id: "Video gagal dimuat. Coba lagi nanti.",
  },
  teachingVideoPrevious: { en: "Previous video", zh: "上一个视频", id: "Video sebelumnya" },
  teachingVideoNext: { en: "Next video", zh: "下一个视频", id: "Video berikutnya" },
  teachingVideoEmpty: {
    en: "No tutorial videos are available yet. Please check back later.",
    zh: "暂无教学视频，请等待运维人员配置后查看",
    id: "Belum ada video tutorial. Silakan coba lagi nanti.",
  },
  teachingVideoWatchAgain: {
    en: "You can watch it again from the Tutorial video button in the top-right corner.",
    zh: "如需再次观看，可点击右上角「教学视频」",
    id: "Untuk menonton lagi, gunakan tombol Video tutorial di kanan atas.",
  },
  typeFastCut: { en: "Fast cut", zh: "快切", id: "Potongan cepat" },
  typeSlowMo: { en: "Slow mood", zh: "慢氛围", id: "Suasana lambat" },
  typeAccelerate: { en: "Speed-up", zh: "加速推进", id: "Akselerasi maju" },
  typeLoop: { en: "Loop hook", zh: "循环钩子", id: "Hook looping" },
  typeProblemSolve: { en: "Problem-solve", zh: "解决问题", id: "Solusi masalah" },
  typeProductDemo: { en: "Product demo", zh: "产品演示", id: "Demo produk" },
  typeNarrative: { en: "Narrative", zh: "叙事", id: "Narasi cerita" },
  typeSliceOfLife: { en: "Slice of life", zh: "生活切片", id: "Klip kehidupan" },
  typeSuspense: { en: "Twist", zh: "悬念反转", id: "Twist misteri" },
  typeTestimonial: { en: "Testimonial", zh: "口碑", id: "Testimoni" },
  typeCelebrity: { en: "Endorsement", zh: "明星背书", id: "Endorse tokoh" },
  typeMontage: { en: "Montage", zh: "蒙太奇", id: "Montase cepat" },
  typeDocumentary: { en: "Documentary", zh: "纪录风", id: "Gaya dokumenter" },
  typeMetaphor: { en: "Metaphor", zh: "概念隐喻", id: "Metafora konsep" },
  typeComedy: { en: "Comedy", zh: "幽默", id: "Komedi humor" },

  createSelectAssets: { en: "Select assets", zh: "选择素材", id: "Pilih materi" },
  createSelectAssetsHint: { en: "Add assets for script editing", zh: "添加素材供脚本编辑使用", id: "Tambah materi untuk edit naskah" },
  createBindElements: { en: "Bind elements", zh: "批量绑定元素", id: "Ikat elemen" },
  createEditScriptText: { en: "Edit script", zh: "编辑脚本文本", id: "Edit teks naskah" },
  createAiChat: { en: "AI chat", zh: "AI 对话", id: "Obrolan AI" },
  createElTypeCharacter: { en: "Character", zh: "人物", id: "Karakter" },
  createElTypeProduct: { en: "Product", zh: "产品", id: "Produk" },
  createElTypeScene: { en: "Scene", zh: "场景", id: "Adegan" },
  createAiUpdating: { en: "AI is updating your script…", zh: "AI 正在更新你的脚本…", id: "AI sedang memperbarui naskah…" },
  createAiWait: { en: "This only takes a few seconds", zh: "请稍候，只需几秒钟", id: "Tunggu sebentar, hanya beberapa detik" },
  createAiChatHint: { en: "Tell me how you want to change it", zh: "在这里告诉我你想怎么改", id: "Tulis perubahan yang kamu inginkan" },
  createAiChatExample: {
    en: "e.g. Make scene 1 voiceover more urgent",
    zh: "例如：把第 1 段口播写得更紧急",
    id: "Mis. buat lisan klip 1 lebih mendesak",
  },
  createAiChatPlaceholder: {
    en: "Tell AI how you want to change it...",
    zh: "告诉AI您想怎么修改...",
    id: "Katakan pada AI apa yang ingin diubah...",
  },
  createAiApplied: { en: "Changes applied.", zh: "已应用修改。", id: "Perubahan diterapkan." },
  createBindHint: {
    en: "Bound assets replace matching text in the script",
    zh: "绑定素材后将批量替换脚本中的对应文本",
    id: "Materi terikat mengganti teks yang cocok di naskah",
  },
  createEditScriptHint: {
    en: "Edit text directly or @ mention assets",
    zh: "可以直接编辑文本或 @ 指定素材",
    id: "Edit teks langsung atau @ sebut materi",
  },
  createMaterialCount: { en: "{n} images", zh: "{n} 张", id: "{n} gambar" },
  createCharacterCount: { en: "{n} characters", zh: "{n} 个", id: "{n} karakter" },
  createPendingVoice: { en: "Voice not bound", zh: "待绑定音色", id: "Suara belum terikat" },
  createBindVoice: { en: "Bind voice", zh: "绑定音色", id: "Ikat suara" },
  createBatchBindExpand: { en: "Expand asset binding", zh: "展开素材选择", id: "Bentangkan pengikatan materi" },
  createBatchBindCollapse: { en: "Collapse asset binding", zh: "折叠素材选择", id: "Tutup pengikatan materi" },
  createElementExtracting: {
    en: "Detecting characters, products, and scenes in the script…",
    zh: "正在识别脚本中的人物、产品和场景…",
    id: "Mendeteksi karakter, produk, dan adegan dalam naskah…",
  },
  createElementExtractFailed: {
    en: "Could not detect script elements. Submit the script again and retry.",
    zh: "脚本元素识别失败，请重新提交脚本后再试",
    id: "Gagal mendeteksi elemen naskah. Kirim ulang naskah lalu coba lagi.",
  },
  createElementExtractPartial: {
    en: "Some script elements could not be detected",
    zh: "部分内容未识别",
    id: "Sebagian elemen naskah tidak terdeteksi",
  },
  createNoBindableElements: {
    en: "No characters, products, or scenes found to bind",
    zh: "暂未识别到可绑定的人物、产品或场景",
    id: "Tidak ada karakter, produk, atau adegan untuk diikat",
  },
  createScriptElementNotFound: { en: "Element not found in the script", zh: "脚本中未找到", id: "Elemen tidak ditemukan di naskah" },
  createRemoveAsset: { en: "Remove asset", zh: "移除该素材", id: "Hapus materi" },
  createContinueBindImage: { en: "Bind another image", zh: "继续绑定图片", id: "Ikat gambar lain" },
  createReplaceCharacter: { en: "Replace character", zh: "更换数字人", id: "Ganti karakter" },
  createBindCharacter: { en: "Bind character", zh: "点击绑定数字人", id: "Ikat karakter" },
  createDurationLabel: { en: "Duration", zh: "时长", id: "Durasi" },
  createSeconds: { en: "sec", zh: "秒", id: "detik" },
  createImageCount: { en: "Images {n}/9", zh: "图片 {n}/9", id: "Gambar {n}/9" },
  createCharacterCountLimit: { en: "Characters {n}/3", zh: "数字人 {n}/3", id: "Karakter {n}/3" },
  createChatRestriction: {
    en: "Only script text can be changed. The storyboard structure, clip names, shot names, and bound assets stay unchanged.",
    zh: "只能修改脚本内容，不能修改视频拆解框架、片段名称、分镜名称和绑定元素",
    id: "Hanya teks naskah yang dapat diubah. Struktur storyboard, nama klip, nama shot, dan materi terikat tetap sama.",
  },
  createMentionNoImages: { en: "No valid image assets", zh: "暂无有效图片素材", id: "Tidak ada gambar valid" },
  createMentionNoCharacters: { en: "No character assets", zh: "暂无数字人素材", id: "Tidak ada materi karakter" },
  createAuthExpired: {
    en: "Your session has expired. Please sign in again and retry.",
    zh: "登录已失效，请重新登录后重试",
    id: "Sesi Anda berakhir. Masuk lagi lalu coba.",
  },
  createPermissionFailed: {
    en: "Authentication failed. Please sign in again and retry.",
    zh: "请求认证或权限校验失败，请重新登录后重试",
    id: "Autentikasi gagal. Masuk lagi lalu coba.",
  },
  createDuplicateImage: {
    en: "This image is already selected and cannot replace the current one.",
    zh: "该图片素材已经选择过了，不能重复替换",
    id: "Gambar ini sudah dipilih dan tidak bisa menggantikan yang sekarang.",
  },
  createDuplicateCharacter: {
    en: "This character is already selected and cannot replace the current one.",
    zh: "该数字人已经选择过了，不能重复替换",
    id: "Karakter ini sudah dipilih dan tidak bisa menggantikan yang sekarang.",
  },
  createElementMissing: {
    en: 'Element "{name}" is no longer in the script. Add it before binding.',
    zh: "脚本中已不存在“{name}”，请先在脚本中添加该元素后再绑定",
    id: 'Elemen "{name}" tidak lagi ada di naskah. Tambahkan sebelum mengikat.',
  },
  createElementBindingCancelled: {
    en: 'Element "{name}" is no longer in the script. Binding was cancelled.',
    zh: "脚本中已不存在“{name}”，本次绑定已取消",
    id: 'Elemen "{name}" tidak lagi ada di naskah. Pengikatan dibatalkan.',
  },
  createAssetReuseConfirm: {
    en: "Some assets are already bound to {names}. Reuse them?",
    zh: "有素材已经关联到{names}，是否继续复用？",
    id: "Beberapa materi sudah terikat ke {names}. Gunakan kembali?",
  },
  createAssetInUseTitle: { en: "Asset already in use", zh: "素材已被使用", id: "Materi sudah digunakan" },
  createContinueReuse: { en: "Reuse", zh: "继续关联", id: "Gunakan kembali" },
  createAssetLimitWarning: {
    en: "Some assets were not added because the clip limit was reached: {names} (up to {images} images per clip, including characters; up to {voices} voiced characters)",
    zh: "以下素材因额度已满未添加：{names}（每段最多 {images} 张图，带音色数字人最多 {voices} 个）",
    id: "Materi berikut tidak ditambahkan karena batas klip tercapai: {names} (maks. {images} gambar dan {voices} karakter bersuara per klip)",
  },
  createDeletedCharacterWarning: {
    en: "References to deleted characters were removed from the script. Review it before continuing.",
    zh: "脚本文案中引用的已删除数字人已自动移除，请确认脚本后再次点击下一步",
    id: "Referensi karakter yang dihapus telah dihapus dari naskah. Tinjau sebelum lanjut.",
  },
  createImageLimitWarning: {
    en: "A clip can reference up to {n} images",
    zh: "当前片段最多关联{n}张图片",
    id: "Satu klip dapat merujuk hingga {n} gambar",
  },
  createAiNoContent: {
    en: "AI did not return valid content. Please try again later.",
    zh: "AI 未能返回有效内容，请稍后重试。",
    id: "AI tidak mengembalikan konten yang valid. Coba lagi nanti.",
  },
  createAiEditDone: { en: "Changes applied as requested", zh: "已按要求修改完成", id: "Perubahan diterapkan sesuai permintaan" },
  createAiQueryDone: { en: "Query completed.", zh: "已完成查询。", id: "Pertanyaan selesai." },
  createAiEditFailed: { en: "Edit failed: {message}", zh: "修改失败：{message}", id: "Edit gagal: {message}" },
  retryLater: { en: "Please try again later.", zh: "请稍后重试", id: "Coba lagi nanti." },
  createFrameworkRejected: {
    en: "Edit failed. Only script text can be changed; the storyboard structure, clip names, shot names, and bound assets cannot be changed.",
    zh: "修改失败，只能修改脚本内容，不能修改视频拆解框架、片段名称、分镜名称和绑定元素。",
    id: "Edit gagal. Hanya teks naskah yang dapat diubah; struktur storyboard, nama klip, nama shot, dan materi terikat tidak dapat diubah.",
  },
  createPendingAiChangeDesc: {
    en: "AI finished editing the script, but the current script changed while it was processing. Apply the AI result?",
    zh: "AI 已完成脚本修改，但检测到等待期间当前脚本发生了变化。是否应用 AI 修改结果？",
    id: "AI selesai mengedit naskah, tetapi naskah saat ini berubah selama proses. Terapkan hasil AI?",
  },
  createPendingAiChangeTitle: { en: "Unapplied AI edit", zh: "发现未应用的 AI 修改", id: "Edit AI belum diterapkan" },
  createApplyAiResult: { en: "Apply AI result", zh: "应用 AI 结果", id: "Terapkan hasil AI" },
  createKeepCurrentScript: { en: "Keep current script", zh: "保留当前脚本", id: "Pertahankan naskah saat ini" },
  createExpand: { en: "Expand", zh: "展开", id: "Bentangkan" },
  createCollapse: { en: "Collapse", zh: "收起", id: "Tutup" },
  createImageLibrary: { en: "Image library", zh: "图片素材库", id: "Pustaka gambar" },
  createPersonLibrary: { en: "Character library", zh: "人物素材库", id: "Pustaka karakter" },
  createDigitalHumanLibrary: { en: "Character library", zh: "数字人库", id: "Pustaka karakter" },
  createAudioLibrary: { en: "Audio library", zh: "音频素材库", id: "Pustaka audio" },
  createSelectDh: { en: "Select character", zh: "选择人物", id: "Pilih karakter" },
  createSearchDh: { en: "Search characters...", zh: "搜索人物...", id: "Cari karakter..." },
  createImageAsset: { en: "Image", zh: "图片素材", id: "Gambar" },
  createDhAsset: { en: "Character", zh: "人物", id: "Karakter" },
  createReplaceAsset: { en: "Replace asset", zh: "替换素材", id: "Ganti materi" },
  createNoMaterialsYet: {
    en: "No assets yet. Add some on the left first.",
    zh: "左侧尚未添加素材，请先添加",
    id: "Belum ada materi. Tambah di kiri dulu.",
  },
  createUntitledImage: { en: "Untitled image", zh: "未命名图片", id: "Gambar tanpa nama" },
  createImageAssets: { en: "Images", zh: "图片素材", id: "Gambar" },
  createAddImageName: { en: "Add image name...", zh: "添加图片名称...", id: "Tambah nama gambar..." },
  createAddImageDesc: { en: "Add image description...", zh: "添加图片描述...", id: "Tambah deskripsi gambar..." },
  createImageNameLabel: { en: "Image name", zh: "图片名称", id: "Nama gambar" },
  createImageDescLabel: {
    en: "Image description (visual keywords)",
    zh: "图片描述（画面提示词）",
    id: "Deskripsi gambar (kata kunci visual)",
  },
  createImageNamePh: { en: "e.g. product close-up", zh: "例如：商品特写", id: "Mis. close-up produk" },
  createImageDescPh: {
    en: "Describe what should appear in the image...",
    zh: "描述图片中需要呈现的具体内容...",
    id: "Jelaskan apa yang harus muncul di gambar...",
  },
  createSelectImage: { en: "Select image", zh: "选择图片", id: "Pilih gambar" },
  createUploadImage: { en: "Upload image", zh: "上传图片", id: "Unggah gambar" },
  createCreateDh: { en: "Create character", zh: "创建数字人", id: "Buat karakter" },
  createDhDialogTitle: { en: "Create character", zh: "创建数字人", id: "Buat karakter" },
  createDhClose: { en: "Close", zh: "关闭", id: "Tutup" },
  createDhModeText: { en: "Text prompt", zh: "文字生成", id: "Dari teks" },
  createDhModeTextDesc1: { en: "Describe the character", zh: "根据文字描述", id: "Deskripsikan karakter" },
  createDhModeTextDesc2: { en: "Generate a character image", zh: "生成数字人形象", id: "Buat gambar karakter" },
  createDhModePhoto: { en: "Photo reference", zh: "照片生成", id: "Dari foto" },
  createDhModePhotoDesc1: { en: "Upload 1-3 reference photos", zh: "上传 1-3 张人物照片", id: "Unggah 1-3 foto referensi" },
  createDhModePhotoDesc2: { en: "Generate a character from photos", zh: "基于照片生成数字人形象", id: "Buat karakter dari foto" },
  createDhGenModeText: { en: "Generated from text", zh: "文字描述生成", id: "Dibuat dari teks" },
  createDhGenModePhoto: { en: "Generated from photo", zh: "照片生成", id: "Dibuat dari foto" },
  createDhModeDerive: { en: "Character variation", zh: "形象衍生", id: "Variasi karakter" },
  createDhModeDeriveDesc1: { en: "Choose an existing character", zh: "选择已有数字人", id: "Pilih karakter yang ada" },
  createDhModeDeriveDesc2: { en: "Create new outfits and states", zh: "衍生更多服装与状态形象", id: "Buat pakaian dan gaya baru" },
  createDhName: { en: "Character name", zh: "数字人名称", id: "Nama karakter" },
  createDhNamePlaceholder: { en: "Give this character a name", zh: "为这位数字人起一个名字", id: "Beri nama karakter ini" },
  createDhDescription: { en: "Character description", zh: "数字人描述", id: "Deskripsi karakter" },
  createDhStyle: { en: "Character style", zh: "形象风格", id: "Gaya karakter" },
  createDhGender: { en: "Gender", zh: "性别", id: "Jenis kelamin" },
  createDhAgeGroup: { en: "Age group", zh: "年龄段", id: "Kelompok usia" },
  createDhSelectPlaceholder: { en: "Select", zh: "请选择", id: "Pilih" },
  createDhStyleRealistic: { en: "Real person", zh: "真人", id: "Orang nyata" },
  createDhStyleAnime: { en: "Anime character", zh: "动漫角色", id: "Karakter anime" },
  createDhStyleAnimal: { en: "Animal", zh: "动物", id: "Hewan" },
  createDhGenderMale: { en: "Male", zh: "男", id: "Pria" },
  createDhGenderFemale: { en: "Female", zh: "女", id: "Wanita" },
  createDhAgeChild: { en: "Child", zh: "儿童", id: "Anak-anak" },
  createDhAgeTeen: { en: "Teen", zh: "少年", id: "Remaja" },
  createDhAgeAdult: { en: "Adult", zh: "成年", id: "Dewasa" },
  createDhAgeElderly: { en: "Older adult", zh: "老年", id: "Lansia" },
  createDhComplianceWarning: {
    en: "Restricted wording detected. Please revise it.",
    zh: "检测到违禁词，请注意合规",
    id: "Terdeteksi kata terlarang. Harap ubah.",
  },
  createDhRendering: {
    en: "Rendering character... About 1 minute remaining",
    zh: "数字人渲染中，预计耗时约1 分钟~",
    id: "Merender karakter... Sekitar 1 menit",
  },
  createDhRenderingHint: {
    en: "You can leave this page and continue other tasks.",
    zh: "可退出页面处理其他操作，无需等待。",
    id: "Anda dapat meninggalkan halaman ini dan mengerjakan hal lain.",
  },
  createDhPending: {
    en: 'Saved to the character list. Close this dialog and click "Voice not bound" to continue.',
    zh: "已保存至数字人列表，关闭弹窗后点击「待绑定音色」继续完成创建",
    id: 'Tersimpan di daftar karakter. Tutup dialog lalu klik "Suara belum terikat" untuk melanjutkan.',
  },
  createDhWait: { en: "Keep waiting", zh: "继续等待", id: "Tunggu" },
  createDhFailedHint: {
    en: "Generation failed. Update the details and try again without leaving this dialog.",
    zh: "生成失败，可修改左侧信息后重新生成，无需退出当前界面",
    id: "Pembuatan gagal. Ubah detail lalu coba lagi tanpa menutup dialog.",
  },
  createDhRegenerate: { en: "Regenerate", zh: "重新生成", id: "Buat ulang" },
  createDhReadyHint: {
    en: "Complete the details on the left, then generate a character image.",
    zh: "完善左侧信息后，点击生成专属数字人形象",
    id: "Lengkapi detail di kiri, lalu buat gambar karakter.",
  },
  createDhGenerate: { en: "Generate character", zh: "生成形象", id: "Buat karakter" },
  createDhMentionNoImages: {
    en: "Upload an image before referencing it with @",
    zh: "请先上传图片后再 @ 引用",
    id: "Unggah gambar sebelum merujuknya dengan @",
  },
  createDhRemoveImage: { en: "Remove selection", zh: "取消选择", id: "Hapus pilihan" },
  createDhPhotoTip: {
    en: "Use photos with natural light and a plain background for the best result.",
    zh: "建议使用自然光、纯色背景的照片以获得最佳生成效果",
    id: "Gunakan foto dengan cahaya alami dan latar polos untuk hasil terbaik.",
  },
  createDhImageSlot: { en: "Image {n}", zh: "图{n}", id: "Gambar {n}" },
  createDhUploadImageFirst: { en: "Upload Image 1 first", zh: "请上传图1", id: "Unggah Gambar 1 terlebih dahulu" },
  createDhUploadingPhoto: {
    en: "Photos are still uploading. Please wait.",
    zh: "照片上传中，请稍候",
    id: "Foto masih diunggah. Harap tunggu.",
  },
  createDhMissingImageRefs: {
    en: "Your description references images that are not uploaded: {refs}",
    zh: "描述引用了未上传的{refs}",
    id: "Deskripsi merujuk gambar yang belum diunggah: {refs}",
  },
  createDhVoiceBind: { en: "Bind a voice", zh: "点击绑定音色", id: "Ikat suara" },
  createDhVoiceHint: {
    en: "Choose from the library, upload a voice, or record one online",
    zh: "从素材库选择、上传音色或在线录音",
    id: "Pilih dari pustaka, unggah suara, atau rekam secara online",
  },
  createDhVoiceUnbind: { en: "Unbind", zh: "取消绑定", id: "Lepas ikatan" },
  createDhVoiceRebind: { en: "Change voice", zh: "重新绑定", id: "Ganti suara" },
  createDhVoicePlay: { en: "Play preview", zh: "试听音色", id: "Putar pratinjau" },
  createDhVoicePause: { en: "Pause", zh: "暂停", id: "Jeda" },
  createDhVoiceDrawerTitle: { en: "Bind voice", zh: "绑定音色", id: "Ikat suara" },
  createDhVoiceInherited: { en: "Inherited voice from", zh: "已继承基底音色 ·", id: "Suara diwarisi dari" },
  createDhVoiceRechoose: { en: "Choose another voice below", zh: "可在下方重新选择", id: "Pilih suara lain di bawah" },
  createDhVoiceLibraryTab: { en: "Choose from library", zh: "素材库选择", id: "Pilih dari pustaka" },
  createDhVoiceUploadTab: { en: "Upload voice", zh: "上传音色", id: "Unggah suara" },
  createDhVoiceRecordTab: { en: "Record online", zh: "在线录音", id: "Rekam online" },
  createDhVoiceName: { en: "Voice name", zh: "音色名称", id: "Nama suara" },
  createDhVoiceNamePlaceholder: { en: "Give this voice a name", zh: "为这段音色起一个名字", id: "Beri nama suara ini" },
  createDhVoiceDescription: { en: "Voice description", zh: "音色描述", id: "Deskripsi suara" },
  createDhVoiceDescriptionPlaceholder: {
    en: "Describe the voice's use or qualities",
    zh: "描述音色的适用场景或特点",
    id: "Jelaskan penggunaan atau karakteristik suara",
  },
  createDhUploadAudio: { en: "Click to upload an audio file", zh: "点击上传音频文件", id: "Klik untuk mengunggah file audio" },
  createDhAudioFormat: {
    en: "MP3 and WAV supported, duration {duration}",
    zh: "支持 MP3、WAV 格式，时长 {duration}",
    id: "Mendukung MP3 dan WAV, durasi {duration}",
  },
  createDhUploading: { en: "Uploading...", zh: "上传中...", id: "Mengunggah..." },
  createDhChooseFile: { en: "Choose file", zh: "选择文件", id: "Pilih file" },
  createDhBinding: { en: "Binding...", zh: "绑定中...", id: "Mengikat..." },
  createDhConfirmBind: { en: "Confirm binding", zh: "确认绑定", id: "Konfirmasi ikatan" },
  createDhVoiceLibraryTitle: { en: "Voice library folders", zh: "素材库目录", id: "Folder pustaka suara" },
  createDhSystemPreset: { en: "System presets", zh: "系统预设", id: "Preset sistem" },
  createDhAllFiles: { en: "All files", zh: "全部文件", id: "Semua file" },
  createDhLoadingFolders: { en: "Loading folders...", zh: "加载目录中...", id: "Memuat folder..." },
  createDhSearchVoices: { en: "Search voice names...", zh: "搜索音色名称...", id: "Cari nama suara..." },
  createDhNoVoiceAssets: { en: "No audio assets", zh: "暂无音频素材", id: "Tidak ada aset audio" },
  createDhNoMatching: { en: "No matching results", zh: "没有匹配的内容", id: "Tidak ada hasil yang cocok" },
  createDhNoVoiceFolders: { en: "No folders or voices", zh: "暂无文件夹或音色", id: "Tidak ada folder atau suara" },
  createDhNoLibraryFolders: { en: "No asset library folders", zh: "暂无素材库目录", id: "Tidak ada folder pustaka aset" },
  createDhSearchLibraryVoices: { en: "Search folders or voices...", zh: "搜索文件夹或音色...", id: "Cari folder atau suara..." },
  createDhClearSearch: { en: "Clear search", zh: "清空搜索", id: "Hapus pencarian" },
  createDhFilterType: { en: "Type", zh: "类型", id: "Jenis" },
  createDhFilterSort: { en: "Sort by", zh: "排序", id: "Urutkan" },
  createDhFilterOrder: { en: "Order", zh: "顺序", id: "Urutan" },
  createDhAscending: { en: "Ascending", zh: "正序", id: "Naik" },
  createDhDescending: { en: "Descending", zh: "倒序", id: "Turun" },
  createDhFolder: { en: "Folder", zh: "文件夹", id: "Folder" },
  createDhVoice: { en: "Voice", zh: "音色", id: "Suara" },
  createDhSortName: { en: "Name", zh: "按名称", id: "Nama" },
  createDhSortTime: { en: "Date", zh: "按时间", id: "Tanggal" },
  createDhSystemPresetVoice: { en: "System preset voice", zh: "系统预设音色", id: "Suara preset sistem" },
  createDhLibraryVoice: { en: "Library voice", zh: "素材库音色", id: "Suara pustaka" },
  createDhVoiceCannotPreview: { en: "This voice cannot be previewed", zh: "该音色暂无法试听", id: "Suara ini tidak dapat dipratinjau" },
  createDhVoiceReadFailed: { en: "Could not load this voice", zh: "无法读取该音色", id: "Gagal memuat suara ini" },
  createDhLoading: { en: "Loading...", zh: "加载中…", id: "Memuat..." },
  createDhLoadMore: { en: "Load more...", zh: "加载更多…", id: "Muat lagi..." },
  createDhPlay: { en: "Play", zh: "播放", id: "Putar" },
  createDhNoPreview: { en: "No playable voice", zh: "暂无可以试听的音色", id: "Tidak ada suara yang dapat diputar" },
  createDhPlayFailed: { en: "Voice preview failed", zh: "音色试听失败", id: "Pratinjau suara gagal" },
  createDhUploadVoiceMeta: { en: "Uploaded voice", zh: "上传音色", id: "Suara yang diunggah" },
  createDhRecordVoiceMeta: { en: "Online recording", zh: "在线录音", id: "Rekaman online" },
  createDhStartRecording: {
    en: "Start recording (at least {min} sec, up to {max} sec; stops automatically)",
    zh: "点击开始录音（至少 {min} 秒，最长 {max} 秒，到时自动停止）",
    id: "Mulai merekam (minimal {min} dtk, maksimal {max} dtk; berhenti otomatis)",
  },
  createDhRecording: { en: "Recording...", zh: "录音中...", id: "Merekam..." },
  createDhRecordingDone: { en: "Recording complete", zh: "录音完成", id: "Rekaman selesai" },
  createDhAudioGenerated: { en: "Audio file ready ({duration})", zh: "已生成音频文件 ({duration})", id: "File audio siap ({duration})" },
  createDhRecordAgain: { en: "Record again", zh: "重新录制", id: "Rekam lagi" },
  createDhPlayPreview: { en: "Play preview", zh: "播放试听", id: "Putar pratinjau" },
  createDhPausePreview: { en: "Pause preview", zh: "暂停试听", id: "Jeda pratinjau" },
  createDhRecordHint: {
    en: "For the best recording, read at a natural pace in a quiet environment. Duration {duration}",
    zh: "为确保录制效果，请在安静环境中以正常语速朗读，时长 {duration}",
    id: "Untuk hasil terbaik, baca dengan kecepatan normal di lingkungan tenang. Durasi {duration}",
  },
  createDhDerivePick: { en: "Choose a character", zh: "点击选择数字人", id: "Pilih karakter" },
  createDhSelectBaseTitle: { en: "Select character", zh: "选择数字人", id: "Pilih karakter" },
  createDhNoCharacterFolders: { en: "No character folders", zh: "暂无数字人目录", id: "Tidak ada folder karakter" },
  createDhSearchCharactersAndFolders: {
    en: "Search folders or characters...",
    zh: "搜索文件夹或数字人...",
    id: "Cari folder atau karakter...",
  },
  createDhNoCharacterItems: { en: "No folders or characters", zh: "暂无文件夹或数字人", id: "Tidak ada folder atau karakter" },
  createDhNoDescription: { en: "No description", zh: "暂无描述", id: "Tidak ada deskripsi" },
  createDhCharacterGenerating: { en: "This character is still being generated", zh: "数字人正在生成中", id: "Karakter ini masih dibuat" },
  createDhSelectCharacterRequired: { en: "Select a character", zh: "请选择数字人", id: "Pilih karakter" },
  createDhDerivePickHint: {
    en: "Choose an existing character from the asset library as the base",
    zh: "从资产库中选择一位已有数字人作为衍生基底",
    id: "Pilih karakter yang ada dari pustaka aset sebagai dasar",
  },
  createDhDeriveCancel: { en: "Clear selection", zh: "取消选择", id: "Hapus pilihan" },
  createDhDeriveReselect: { en: "Choose again", zh: "重新选择", id: "Pilih lagi" },
  createDhDeriveName: { en: "Variation name", zh: "衍生名称", id: "Nama variasi" },
  createDhDeriveNamePlaceholder: {
    en: "Name this variation, e.g. business suit or casual homewear",
    zh: "为这个衍生形象起一个名字，如：商务正装、居家休闲",
    id: "Beri nama variasi ini, mis. setelan bisnis atau pakaian santai",
  },
  createDhDeriveDescription: { en: "Variation description", zh: "衍生描述", id: "Deskripsi variasi" },
  createDhDeriveDescriptionPlaceholder: {
    en: "Describe the outfit, state, or scene, e.g. a dark suit in an office",
    zh: "描述想要衍生的服装、状态或场景，例如：换上深色西装、切换为居家休闲状态、置身办公室背景等",
    id: "Jelaskan pakaian, gaya, atau adegan, mis. setelan gelap di kantor",
  },
  createDhDeriveDescriptionHint: {
    en: "Create a new image from the selected character while keeping the identity consistent. More detail gives more stable results.",
    zh: "基于所选数字人衍生新形象，仅改变服装 / 状态 / 场景，保持人物一致性。描述越具体，效果越稳定。",
    id: "Buat gambar baru dari karakter yang dipilih dengan identitas tetap konsisten. Detail lebih lengkap memberi hasil lebih stabil.",
  },
  createDhDeriveReadyHint: {
    en: "Choose a base character and complete the variation description, then generate a new image.",
    zh: "选择基底数字人并填写衍生描述后，点击生成新形象",
    id: "Pilih karakter dasar dan lengkapi deskripsi variasi, lalu buat gambar baru.",
  },
  createDhDeriveGenerate: { en: "Generate variation", zh: "生成新形象", id: "Buat variasi" },
  createDhInheritedVoice: { en: "Inherited base voice", zh: "继承基底音色", id: "Suara dasar diwarisi" },
  createDhFolderCount: { en: "{count} assets", zh: "{count} 个素材", id: "{count} aset" },
  createDhVoiceNotBound: { en: "Voice not bound", zh: "待绑定音色", id: "Suara belum terikat" },
  createDhCancel: { en: "Cancel", zh: "取消", id: "Batal" },
  createDhFinish: { en: "Finish", zh: "完成创建", id: "Selesai" },
  createDhCreating: { en: "Creating...", zh: "创建中...", id: "Membuat..." },
  createDhContinueTitle: { en: "Continue creating character", zh: "继续创建数字人", id: "Lanjutkan membuat karakter" },
  createDhContinueSubtitle: {
    en: "Bind a voice to finish creating this character",
    zh: "绑定专属音色以完成创建",
    id: "Ikat suara khusus untuk menyelesaikan pembuatan",
  },
  createDhChangeVoiceTitle: { en: "Change voice", zh: "更换音色", id: "Ganti suara" },
  createDhUnnamedVoice: { en: "Unnamed voice", zh: "未命名音色", id: "Suara tanpa nama" },
  createDhCurrentVoiceReplace: {
    en: "Current voice: {name}. Selecting a new voice will replace the current one.",
    zh: "当前音色：{name}。选择新音色后将替换当前绑定。",
    id: "Suara saat ini: {name}. Memilih suara baru akan menggantikan ikatan saat ini.",
  },
  createDhConfirmChange: { en: "Confirm change", zh: "确认更换", id: "Konfirmasi penggantian" },
  createDhSaving: { en: "Saving...", zh: "保存中...", id: "Menyimpan..." },
  createDhChangeVoiceNotReady: {
    en: "This character is not ready yet, so the voice cannot be changed",
    zh: "数字人尚未生成成功，无法更换音色",
    id: "Karakter ini belum siap, sehingga suaranya tidak dapat diganti",
  },
  createDhBindVoiceFirst: {
    en: "This character has no voice yet. Bind a voice first.",
    zh: "该数字人尚未绑定音色，请先完成绑定",
    id: "Karakter ini belum memiliki suara. Ikat suara terlebih dahulu.",
  },
  createDhVoiceAlreadyBound: {
    en: "This character already has a voice bound",
    zh: "该数字人已绑定音色",
    id: "Karakter ini sudah memiliki suara",
  },
  createDhVoiceUpdated: { en: "Voice updated", zh: "音色已更新", id: "Suara diperbarui" },
  createDhAvatarImageAlt: { en: "Character image", zh: "数字人形象", id: "Gambar karakter" },
  createDhDurationRange: { en: "{min}–{max} sec", zh: "{min}～{max} 秒", id: "{min}–{max} dtk" },
  createDhVoiceDurationUnreadable: {
    en: "Could not read the audio duration",
    zh: "无法读取音频时长",
    id: "Tidak dapat membaca durasi audio",
  },
  createDhVoiceDurationOutOfRange: {
    en: "Audio must be between {range}. Current: {current}s",
    zh: "音频时长需在 {range} 之间，当前：{current}s",
    id: "Durasi audio harus antara {range}. Saat ini: {current} dtk",
  },
  createDhFieldTooLong: {
    en: "{label} cannot exceed {max} characters",
    zh: "{label}不能超过 {max} 个字符",
    id: "{label} tidak boleh lebih dari {max} karakter",
  },
  createDhRecordNamePrefix: { en: "Recording", zh: "录音", id: "Rekaman" },
  createDhStartRecord: { en: "Start recording", zh: "开始录音", id: "Mulai merekam" },
  createDhRecordStartFailed: { en: "Could not start recording", zh: "录音启动失败", id: "Gagal memulai rekaman" },
  createDhRecordFailed: { en: "Recording failed", zh: "录音失败", id: "Rekaman gagal" },
  createDhRecordTooShort: {
    en: "Recording must be at least {min} sec. Current length is about {current} sec.",
    zh: "录音时长至少 {min} 秒，当前约 {current} 秒",
    id: "Rekaman minimal {min} dtk. Durasi saat ini sekitar {current} dtk.",
  },
  createDhMicUnsupported: {
    en: "This browser does not support microphone recording",
    zh: "当前浏览器不支持麦克风录音",
    id: "Browser ini tidak mendukung rekaman mikrofon",
  },
  createDhMicDenied: {
    en: "Microphone access was denied. Allow it in your browser settings.",
    zh: "麦克风权限被拒绝，请在浏览器设置中允许访问",
    id: "Akses mikrofon ditolak. Izinkan di pengaturan browser.",
  },
  createDhMicUnavailable: {
    en: "Could not access the microphone. Check the device and permissions.",
    zh: "无法访问麦克风，请检查设备与权限",
    id: "Tidak dapat mengakses mikrofon. Periksa perangkat dan izin.",
  },
  createDhRecordEmpty: {
    en: "Recording failed. No audio was captured.",
    zh: "录音失败，未获取到音频数据",
    id: "Rekaman gagal. Tidak ada data audio.",
  },
  createDhRecordScript1: { en: "Let me tell you", zh: "我跟你说", id: "Mau kukasih tahu" },
  createDhRecordScript2: { en: "this really works well", zh: "这个真的很好用", id: "ini benar-benar berguna" },
  createDhRecordScript3: { en: "see the results for yourself", zh: "效果你们自己看", id: "lihat sendiri hasilnya" },
  createDhGenerationTaskMissing: {
    en: "Generation task not found. Please generate again.",
    zh: "生成任务不存在，请重新生成",
    id: "Tugas pembuatan tidak ditemukan. Silakan buat lagi.",
  },
  createDhStatusQueryFailed: {
    en: "Could not query generation status. Please try again.",
    zh: "查询生成状态失败，请重试",
    id: "Gagal memeriksa status pembuatan. Coba lagi.",
  },
  createDhSubmitFailed: {
    en: "Could not submit generation. Please try again.",
    zh: "提交生成失败，请重试",
    id: "Gagal mengirim pembuatan. Coba lagi.",
  },
  createDhGenerationFailed: { en: "Generation failed", zh: "生成失败", id: "Pembuatan gagal" },
  createDhInvalidCreateState: {
    en: "Creation details are incomplete. Go back and generate the image again.",
    zh: "创建信息不完整，请返回上一步重新生成形象",
    id: "Detail pembuatan belum lengkap. Kembali dan buat gambar lagi.",
  },
  createDhVoiceSelectRequired: { en: "Select a voice", zh: "请选择音色", id: "Pilih suara" },
  createDhUploadAudioRequired: { en: "Upload an audio file", zh: "请上传音频文件", id: "Unggah file audio" },
  createDhVoiceNameRequired: { en: "Enter a voice name", zh: "请填写音色名称", id: "Masukkan nama suara" },
  createDhRecordRequired: { en: "Complete a recording first", zh: "请先完成录音", id: "Selesaikan rekaman terlebih dahulu" },
  createDhVoiceCreateFailed: { en: "Could not create voice", zh: "音色创建失败", id: "Gagal membuat suara" },
  createDhVoiceFormat: { en: "Voices support MP3 and WAV only", zh: "音色仅支持 MP3、WAV 格式", id: "Suara hanya mendukung MP3 dan WAV" },
  createDhVoiceUploadFailed: { en: "Upload failed", zh: "上传失败", id: "Unggah gagal" },
  createDhVoicePlayError: { en: "Audio could not be played", zh: "音频无法播放", id: "Audio tidak dapat diputar" },
  createDhVoiceAddressError: { en: "Could not load audio", zh: "音频地址获取失败", id: "Gagal memuat audio" },
  createBindDh: { en: "Tap to bind character", zh: "点击绑定人物", id: "Ketuk untuk ikat karakter" },
  createBindImage: { en: "Tap to bind image", zh: "点击绑定图片", id: "Ketuk untuk ikat gambar" },
  createImagesLabel: { en: "Images ", zh: "图片 ", id: "Gambar " },
  createDhLabel: { en: "Characters ", zh: "人物 ", id: "Karakter " },
  createNoValidImages: { en: "No valid image assets", zh: "暂无有效图片素材", id: "Tidak ada gambar valid" },
  createNoDhMaterials: { en: "No character assets", zh: "暂无人物素材", id: "Tidak ada karakter" },
  createPickerCharacterMultipleHint: {
    en: "Select multiple character images to use as replacement assets",
    zh: "可多选数字人形象作为替换素材",
    id: "Pilih beberapa gambar karakter sebagai aset pengganti",
  },
  createPickerCharacterSingleHint: {
    en: "Select one generated character image to use as a replacement asset",
    zh: "选择一个已生成的数字人形象作为替换素材",
    id: "Pilih satu gambar karakter yang telah dibuat sebagai aset pengganti",
  },
  createPickerSearchCharacters: { en: "Search character names...", zh: "搜索数字人名称...", id: "Cari nama karakter..." },
  createRefresh: { en: "Refresh", zh: "刷新", id: "Segarkan" },
  createPickerNoMatchingCharacters: { en: "No matching characters", zh: "没有匹配的数字人", id: "Tidak ada karakter yang cocok" },
  createPickerNoCharacters: { en: "No character images", zh: "暂无数字人形象", id: "Belum ada gambar karakter" },
  createPickerCreateCharacter: {
    en: "Create a character in the character library first, or switch to another folder",
    zh: "请先在数字人库创建数字人，或切换其他目录",
    id: "Buat karakter di pustaka karakter terlebih dahulu, atau ganti folder lain",
  },
  createUntitledCharacter: { en: "Untitled character", zh: "未命名数字人", id: "Karakter tanpa nama" },
  createPendingVoiceCannotUse: {
    en: '"{name}" has no voice bound and cannot be selected',
    zh: '"{name}" 待绑定音色，无法选用',
    id: '"{name}" belum memiliki suara dan tidak dapat dipilih',
  },
  createPendingVoiceTitle: { en: "Voice not bound", zh: "待绑定音色", id: "Suara belum terikat" },
  createPendingVoiceDescription: {
    en: "This character has no voice bound. Bind a voice before selecting it.",
    zh: "该数字人尚未绑定音色，请先完成音色绑定后再选用。",
    id: "Karakter ini belum memiliki suara. Ikat suara sebelum memilihnya.",
  },
  createGoBind: { en: "Bind voice", zh: "去绑定", id: "Ikat suara" },
  createNoAvailableCharacters: { en: "No available characters", zh: "没有可用的数字人", id: "Tidak ada karakter yang tersedia" },
  createPickerLoadCharacterFoldersFailed: {
    en: "Failed to load character library folders",
    zh: "加载数字人目录失败",
    id: "Gagal memuat folder pustaka karakter",
  },
  createPickerLoadCharactersFailed: { en: "Failed to load characters", zh: "加载数字人列表失败", id: "Gagal memuat daftar karakter" },
  createUploadLocalImage: { en: "Upload local image", zh: "上传本地图片", id: "Unggah gambar lokal" },
  createAiGenerateImage: { en: "AI generate", zh: "AI 生成", id: "Buat dengan AI" },
  createAiImageTitle: { en: "Generate image with AI", zh: "AI 文本生成图片", id: "Buat gambar dengan AI" },
  createAiImageDescription: {
    en: "Enter an image name and description, then click Generate. The task runs in the background and the image will appear in the media library when ready.",
    zh: "填写图片名称与描述，点击「开始生成」后任务将在后台执行。可关闭弹窗，生成完成后图片会显示在素材列表中。",
    id: "Masukkan nama dan deskripsi gambar, lalu klik Buat. Tugas berjalan di latar belakang dan gambar akan muncul di pustaka media saat selesai.",
  },
  createCurrentCost: { en: "Cost", zh: "本次消耗", id: "Biaya" },
  createImageUploadGuide: {
    en: "Drag and drop a file here, or choose one from your device",
    zh: "拖拽文件到此处，或从本地选择",
    id: "Seret dan lepas file di sini, atau pilih dari perangkat Anda",
  },
  createImageUploadHint: {
    en: "Supported formats: JPG, PNG, WEBP | Max file size: 30 MB | Max dimensions: 6000 x 6000 px",
    zh: "支持上传格式：JPG、PNG、WEBP | 单文件大小上限：≤30MB | 最大宽高范围：6000px * 6000px",
    id: "Format yang didukung: JPG, PNG, WEBP | Ukuran file maks.: 30 MB | Dimensi maks.: 6000 x 6000 px",
  },
  createReplaceImage: { en: "Click to replace image", zh: "点击替换图片", id: "Klik untuk mengganti gambar" },
  createSelectedFileReplace: {
    en: "File selected. Click to replace",
    zh: "已选择文件，点击可替换",
    id: "File dipilih. Klik untuk mengganti",
  },
  createAiImageStart: { en: "Generate", zh: "开始生成", id: "Buat" },
  createImageDescriptionRequired: { en: "Enter an image description", zh: "请输入图片描述", id: "Masukkan deskripsi gambar" },
  createFieldMaxLength: {
    en: "{label} must be {count} characters or fewer",
    zh: "{label}不能超过 {count} 个字",
    id: "{label} maksimal {count} karakter",
  },
  createImageWidthInvalid: {
    en: "Image width must be between {min}px and {max}px. Current width: {value}px",
    zh: "图片宽度需在 {min}px ~ {max}px 之间，当前宽度：{value}px",
    id: "Lebar gambar harus antara {min}px dan {max}px. Lebar saat ini: {value}px",
  },
  createImageHeightInvalid: {
    en: "Image height must be between {min}px and {max}px. Current height: {value}px",
    zh: "图片高度需在 {min}px ~ {max}px 之间，当前高度：{value}px",
    id: "Tinggi gambar harus antara {min}px dan {max}px. Tinggi saat ini: {value}px",
  },
  createImagePixelsInvalid: {
    en: "Image resolution is too low. Total pixels must be at least {min} (about 640 x 640). Current: {width} x {height} = {pixels}",
    zh: "图片分辨率过低，总像素需 ≥ {min}（约640×640），当前：{width}×{height}={pixels}",
    id: "Resolusi gambar terlalu rendah. Total piksel minimal {min} (sekitar 640 x 640). Saat ini: {width} x {height} = {pixels}",
  },
  createImageRatioInvalid: {
    en: "Image aspect ratio must be between {min} and {max}. Current ratio: {value}",
    zh: "图片宽高比需在 {min} ~ {max} 之间，当前宽高比：{value}",
    id: "Rasio aspek gambar harus antara {min} dan {max}. Rasio saat ini: {value}",
  },
  createImageFormatInvalid: {
    en: "Unsupported image format ({ext}). Supported formats: {formats}",
    zh: "不支持的图片格式（{ext}），支持：{formats}",
    id: "Format gambar tidak didukung ({ext}). Format yang didukung: {formats}",
  },
  createFileSizeInvalid: {
    en: "File size exceeds the {size} MB limit",
    zh: "文件大小超过限制（{size}MB）",
    id: "Ukuran file melebihi batas {size} MB",
  },
  createAiImageSubmitted: {
    en: "Generation submitted. The image will appear in the media library when ready",
    zh: "已提交生成，完成后将显示在素材列表",
    id: "Pembuatan dikirim. Gambar akan muncul di pustaka media saat selesai",
  },
  createUploadLocalMusic: { en: "Upload local music", zh: "上传本地音乐", id: "Unggah musik lokal" },
  createVoiceover: { en: "Voiceover", zh: "口播", id: "Lisan" },

  rwPreview: { en: "Preview", zh: "预览", id: "Pratinjau" },
  rwImageAssets: { en: "Images", zh: "图片素材", id: "Gambar" },
  rwSelectedAssets: { en: "Selected assets", zh: "已选素材", id: "Materi terpilih" },
  rwSelectAssets: { en: "Select assets", zh: "素材选择", id: "Pilih materi" },
  rwViewBreakdown: { en: "View storyboard table", zh: "查看分镜拆解表", id: "Lihat tabel storyboard" },
  rwGenStoryboard: { en: "Generate storyboard", zh: "生成故事画板", id: "Buat papan cerita" },
  rwEditClipScript: { en: "Edit clip script", zh: "编辑片段脚本", id: "Edit naskah klip" },
  rwDigitalHuman: { en: "Character", zh: "数字人", id: "Karakter" },
  rwVideoPreview: { en: "Video preview", zh: "视频预览", id: "Pratinjau video" },
  rwBoardPreview: { en: "Board preview", zh: "画板预览", id: "Pratinjau papan" },
  rwBreakdownTable: { en: "Storyboard table", zh: "分镜拆解表", id: "Tabel storyboard" },
  rwNoImageAssets: { en: "No valid image assets", zh: "暂无有效图片素材", id: "Tidak ada gambar valid" },
  rwNoDhAssets: { en: "No character assets", zh: "暂无人物素材", id: "Tidak ada karakter" },
  rwSelect: { en: "Select", zh: "选择", id: "Pilih" },
  rwInUse: { en: "In use", zh: "当前使用", id: "Sedang dipakai" },
  rwClipAlt: { en: "Clip", zh: "片段", id: "Klip" },
  rwClip: { en: "Clip", zh: "片段", id: "Klip" },
  rwClipN: { en: "Clip {n}", zh: "片段 {n}", id: "Klip {n}" },
  rwClipName: { en: "Clip name", zh: "片段名称", id: "Nama klip" },
  rwShot: { en: "Shot", zh: "镜头", id: "Shot" },
  rwVisual: { en: "Visual", zh: "画面", id: "Visual" },
  rwScene: { en: "Scene", zh: "场景", id: "Adegan" },
  rwVoiceover: { en: "Voiceover", zh: "口播", id: "Lisan" },
  rwPlot: { en: "Story", zh: "剧情", id: "Cerita" },
  rwGenerateThisClip: { en: "Generate this clip", zh: "生成该片段", id: "Buat klip ini" },
  rwStoryboardHelp: {
    en: "Storyboards help lock key frames. You can skip them and generate the clip below.",
    zh: "生成故事画板可帮助确定片段关键画面，也可以不生成画板直接在下方生成片段。",
    id: "Papan cerita membantu kunci gambar. Bisa dilewati dan langsung buat klip di bawah.",
  },
  rwNoDhSelected: {
    en: "No character selected. Add one in Select assets.",
    zh: "左侧暂无已选数字人，请先在「素材选择」中添加",
    id: "Belum ada karakter. Tambah di Pilih materi.",
  },
  rwNoImageSelected: {
    en: "No image selected. Add one in Select assets.",
    zh: "左侧暂无已选图片，请先在「素材选择」中添加",
    id: "Belum ada gambar. Tambah di Pilih materi.",
  },

  createClipN: { en: "Clip {n}", zh: "片段 {n}", id: "Klip {n}" },
  createClipScript: { en: "Clip script", zh: "片段脚本", id: "Naskah klip" },
  createUsedAssets: { en: "Assets in use", zh: "使用素材", id: "Materi dipakai" },
  createRegenClip: { en: "Regenerate this clip", zh: "重新生成本段画面", id: "Buat ulang layar klip ini" },
  createRegenBusy: { en: "Regenerating...", zh: "重建中...", id: "Membangun ulang..." },
  createRegenOk: { en: "Scene regenerated", zh: "分镜已重新生成", id: "Adegan selesai dibuat ulang" },
  createRegenWait: { en: "AI is rebuilding the scene boards…", zh: "AI 正在重建分镜画面…", id: "AI sedang membangun ulang layar adegan…" },
  createScenePreview: { en: "Scene preview", zh: "分镜画面预览", id: "Pratinjau layar adegan" },
  createSavedInWorks: {
    en: "Saved in My Works. You can download it.",
    zh: "成片已保存在我的作品，可下载到本地",
    id: "Video tersimpan di Karya Saya, bisa diunduh.",
  },
  createKeepCreating: { en: "Keep creating", zh: "继续创作", id: "Lanjut buat" },
  createDownloadVideo: { en: "Download video", zh: "下载视频", id: "Unduh video" },
  createClose: { en: "Close", zh: "关闭", id: "Tutup" },
  createComposedFileName: { en: "video", zh: "成片", id: "video" },
  createVideoPreparing: {
    en: "The video is still preparing. Try again shortly.",
    zh: "视频地址准备中，请稍后重试",
    id: "Video masih disiapkan. Coba lagi sebentar.",
  },
  createTaskNotSaved: {
    en: "This creation has not been saved yet. Try again shortly.",
    zh: "当前自主创作任务尚未保存，请稍后重试",
    id: "Kreasi ini belum disimpan. Coba lagi sebentar.",
  },
  createEditorNotReady: { en: "The editor is not ready yet", zh: "编辑器未就绪", id: "Editor belum siap" },
  createComposing: { en: "Composing your video", zh: "正在合成您的成片", id: "Sedang menyusun video Anda" },
  createGenStep1: { en: "Assigning a queue slot...", zh: "任务排队分配中...", id: "Mengantrekan tugas..." },
  createGenStep2: { en: "Collect final scene data", zh: "收集最终分镜数据", id: "Kumpulkan data adegan akhir" },
  createGenStep3: { en: "Align audio and compose", zh: "音画对齐与合成", id: "Selaraskan audio dan sintesis" },
  createGenStep4: { en: "Render and split the final cut", zh: "成品渲染与分片", id: "Render dan pecah hasil akhir" },
  createGenStepScript: {
    en: "Read the final script and lock the shot order and structure",
    zh: "读取定稿视频脚本，锁定全篇镜头顺序与内容结构",
    id: "Baca naskah final dan kunci urutan serta struktur shot",
  },
  createGenStepAssets: {
    en: "Match product images and character assets to each shot",
    zh: "导入绑定的产品图片、人物形象素材匹配对应画面",
    id: "Cocokkan gambar produk dan aset karakter dengan tiap shot",
  },
  createGenStepVoice: {
    en: "Match the selected voice and generate the voiceover",
    zh: "匹配录入的专属音色，自动生成视频配音台词",
    id: "Cocokkan suara yang dipilih dan buat voiceover",
  },
  createGenStepRender: {
    en: "Render each shot and export the complete video",
    zh: "按照脚本分镜逐帧渲染整合，导出完整成片",
    id: "Render tiap shot dan ekspor video lengkap",
  },
  createGeneratingStatus: { en: "Generating...", zh: "生成中...", id: "Membuat..." },
  createGeneratedStatus: { en: "Generated", zh: "已生成", id: "Berhasil dibuat" },
  createGenerationFailed: { en: "Generation failed", zh: "生成失败", id: "Pembuatan gagal" },
  createRenderingClip: {
    en: "Rendering your video from the script, product, and character assets. Estimated time: about {minutes} minutes. You can leave this page and continue working.",
    zh: "正在结合脚本内容、产品及人物素材渲染视频，预计耗时约 {minutes} 分钟，可退出页面处理其他操作，无需等待",
    id: "Video sedang dirender dari naskah, produk, dan aset karakter. Perkiraan waktu sekitar {minutes} menit. Anda dapat meninggalkan halaman ini dan melanjutkan pekerjaan.",
  },
  createClipAssetsCount: { en: "Clip assets {count}/9", zh: "当前片段素材 {count}/9", id: "Aset klip {count}/9" },
  createSubmitting: { en: "Submitting...", zh: "提交中...", id: "Mengirim..." },
  createStoryboardPurposeLabel: { en: "Board purpose:", zh: "画板用途：", id: "Tujuan papan:" },
  createStoryboardPurpose: {
    en: "Confirm the characters, products, scenes, and composition for each shot in advance.",
    zh: "提前确认每个分镜的人物、产品、场景和画面构图。",
    id: "Konfirmasikan karakter, produk, adegan, dan komposisi setiap shot terlebih dahulu.",
  },
  createStoryboardUsageLabel: { en: "Used when:", zh: "使用场景：", id: "Digunakan saat:" },
  createStoryboardUsage: {
    en: "Clip generation prioritizes the board and combines it with the shot copy, characters, and voice. Use the board as the source of truth for product and scene visuals.",
    zh: "生成片段视频时优先参考画板，并结合分镜文案、人物和声音；产品、场景等视觉信息以画板为准。",
    id: "Pembuatan klip memprioritaskan papan dan menggabungkannya dengan naskah shot, karakter, dan suara. Gunakan papan sebagai acuan visual produk dan adegan.",
  },
  createStoryboardGenerating: { en: "Generating", zh: "生成中", id: "Membuat" },
  createNoStoryboard: {
    en: "No shots yet. The storyboard area is ready when you are.",
    zh: "当前片段暂无分镜，生成画板区域已预留",
    id: "Belum ada shot. Area storyboard siap digunakan.",
  },
  createQueuedStatus: { en: "Queued...", zh: "排队中...", id: "Dalam antrean..." },
  createCurrentUse: { en: "In use", zh: "当前使用", id: "Sedang dipakai" },
  createGenerationN: { en: "Generation {n}", zh: "生成 {n}", id: "Versi {n}" },
  createNoClips: {
    en: "No clip data. Edit the script in the previous step first.",
    zh: "暂无片段数据，请先在上一步编辑脚本",
    id: "Belum ada data klip. Edit naskah di langkah sebelumnya terlebih dahulu.",
  },
  createSelectClipFirst: { en: "Select a clip first", zh: "请先选择片段", id: "Pilih klip terlebih dahulu" },
  createNoStoryboardToGenerate: {
    en: "This clip has no storyboard available to generate",
    zh: "当前片段没有可生成的分镜画板",
    id: "Klip ini tidak memiliki storyboard yang dapat dibuat",
  },
  createSessionMissing: {
    en: "This creation session is missing. Refresh the page and try again.",
    zh: "当前创作会话缺失，请刷新页面重试",
    id: "Sesi kreasi ini tidak ditemukan. Muat ulang halaman lalu coba lagi.",
  },
  createSessionVideoMissing: {
    en: "This creation session is missing, so the clip video cannot be generated.",
    zh: "当前创作会话缺失，无法生成片段视频",
    id: "Sesi kreasi ini tidak ditemukan, sehingga video klip tidak dapat dibuat.",
  },
  createRatioMissing: {
    en: "Video ratio is missing. Return to step 1 and choose one.",
    zh: "视频比例缺失，请返回第一步选择",
    id: "Rasio video tidak ada. Kembali ke langkah 1 dan pilih rasio.",
  },
  createSaveScriptFailed: {
    en: "Could not save the latest script. Please try again later.",
    zh: "保存最新脚本失败，请稍后重试",
    id: "Gagal menyimpan naskah terbaru. Coba lagi nanti.",
  },
  createStoryboardLimit: {
    en: "A clip can reference up to 9 images",
    zh: "当前片段最多关联9张图片",
    id: "Satu klip dapat merujuk hingga 9 gambar",
  },
  createNoPendingStoryboards: {
    en: "This clip has no storyboards waiting to be generated",
    zh: "当前片段没有待生成的画板",
    id: "Klip ini tidak memiliki storyboard yang menunggu dibuat",
  },
  createStoryboardSubmitted: {
    en: "Submitted {count} storyboard generation task(s)",
    zh: "已提交 {count} 张画板生成任务",
    id: "{count} tugas pembuatan storyboard dikirim",
  },
  createStoryboardSkipped: {
    en: "The 9-image limit was reached. Skipped {count} storyboard(s).",
    zh: "当前片段最多关联9张图片，已跳过 {count} 张",
    id: "Batas 9 gambar tercapai. {count} storyboard dilewati.",
  },
  createDurationInvalid: {
    en: "The duration for {name} ({value}) cannot be used for video generation. Set it to an integer from 4 to 15 seconds and try again.",
    zh: "{name}的时长{value}无法用于视频生成，请将片段时长设置为4至15秒后重试",
    id: "Durasi {name} ({value}) tidak dapat digunakan untuk pembuatan video. Atur bilangan bulat 4-15 detik lalu coba lagi.",
  },
  createDurationInvalidLabel: { en: "Invalid duration", zh: "时长格式异常", id: "Durasi tidak valid" },
  createPreviousClipRequired: {
    en: "For continuity, generate clip {n} first.",
    zh: "为了您的视频连贯性，请您先生成片段{n}",
    id: "Untuk kesinambungan video, buat klip {n} terlebih dahulu.",
  },
  createVoiceLimit: {
    en: "Each clip can use at most {count} voices. Remove extra character voices or voiceovers and try again.",
    zh: "每个片段最多关联{count}个声音，请删除多余的数字人音色或配音后再生成",
    id: "Setiap klip dapat memakai maksimal {count} suara. Hapus suara karakter atau voiceover tambahan lalu coba lagi.",
  },
  createScriptMissing: { en: "The clip script could not be found.", zh: "当前片段脚本不存在", id: "Naskah klip tidak ditemukan." },
  createUnnamedAsset: { en: "Unnamed asset", zh: "未命名素材", id: "Aset tanpa nama" },
  createInvalidImageReference: {
    en: "These assets are videos and cannot be used as image references: {names}. Bind compatible assets and try again.",
    zh: "素材「{names}」是视频文件不能作为图片参考，请重新绑定可用素材后重试",
    id: "Aset berikut adalah video dan tidak dapat digunakan sebagai referensi gambar: {names}. Ikat aset yang sesuai lalu coba lagi.",
  },
  createTaskIdMissing: {
    en: "The video task was submitted but no task ID was returned.",
    zh: "Seedance 已提交但未返回任务ID",
    id: "Tugas video dikirim tetapi ID tugas tidak dikembalikan.",
  },
  createTaskResultMissing: {
    en: "The task finished but no video result was returned. Generate it again.",
    zh: "任务已完成但视频结果缺失，请重新生成",
    id: "Tugas selesai tetapi hasil video tidak ada. Buat ulang.",
  },
  createStoryboardFailed: {
    en: "Storyboard generation failed. Please try again.",
    zh: "画板生成失败，请重试",
    id: "Pembuatan storyboard gagal. Coba lagi.",
  },
  createStoryboardSuccess: { en: "Storyboard {ref} generated", zh: "分镜{ref}生成成功", id: "Storyboard {ref} berhasil dibuat" },
  createStoryboardTimeout: {
    en: "Storyboard generation timed out. Generate it again.",
    zh: "画板生成超时，请重新生成",
    id: "Pembuatan storyboard habis waktu. Buat ulang.",
  },
  createVideoFailed: { en: "Video generation failed", zh: "视频生成失败", id: "Pembuatan video gagal" },
  createVideoTimeout: {
    en: "Video generation timed out. Try again.",
    zh: "视频生成超时，请重试",
    id: "Pembuatan video habis waktu. Coba lagi.",
  },
  createGenerationRetry: {
    en: "Generation failed. Please try again later.",
    zh: "生成失败，请稍后重试",
    id: "Pembuatan gagal. Coba lagi nanti.",
  },
  createTaskStatusUnknown: {
    en: "The generation status could not be confirmed. Refresh or generate again.",
    zh: "生成状态未确认，请稍后刷新或重新生成",
    id: "Status pembuatan tidak dapat dikonfirmasi. Muat ulang atau buat ulang.",
  },
  createStoryboardStatusKept: {
    en: "Storyboard status could not be confirmed. The last successful image was kept.",
    zh: "画板生成状态未确认，已保留上一次成功图片",
    id: "Status storyboard tidak dapat dikonfirmasi. Gambar terakhir yang berhasil dipertahankan.",
  },
  createStoryboardStatusUnknown: {
    en: "Storyboard status could not be confirmed. Generate it again.",
    zh: "画板生成状态未确认，请重新生成",
    id: "Status storyboard tidak dapat dikonfirmasi. Buat ulang.",
  },
  createRegenerateStoryboardTitle: { en: "Regenerate storyboard", zh: "确认重新生成画板", id: "Buat ulang storyboard" },
  createRegenerateStoryboardDesc: {
    en: "The shot copy has not changed. Regenerating will overwrite the current keyframe. Continue?",
    zh: "当前分镜文案未做修改，重新生成将覆盖现有关键帧。请确认是否继续？",
    id: "Naskah shot belum berubah. Pembuatan ulang akan menimpa keyframe saat ini. Lanjutkan?",
  },
  createRetry: { en: "Retry", zh: "重试", id: "Coba lagi" },
  createOnlyQueuedCancel: {
    en: "Only queued tasks can be cancelled",
    zh: "仅排队中的任务可以取消",
    id: "Hanya tugas dalam antrean yang dapat dibatalkan",
  },
  createCancelTaskTitle: { en: "Cancel this task?", zh: "确定要取消该任务吗？", id: "Batalkan tugas ini?" },
  createTaskStartedCannotCancel: {
    en: "The task has started and cannot be cancelled",
    zh: "任务已开始生成，无法取消",
    id: "Tugas sudah dimulai dan tidak dapat dibatalkan",
  },
  createTaskCancelled: { en: "Task cancelled", zh: "任务已取消", id: "Tugas dibatalkan" },
  createCancelTaskFailed: {
    en: "Could not cancel the task. Please try again later.",
    zh: "取消任务失败，请稍后重试",
    id: "Gagal membatalkan tugas. Coba lagi nanti.",
  },
  createDownloadFailed: { en: "Download failed", zh: "下载失败", id: "Unduhan gagal" },
  createComposeSuccess: { en: "Video composed successfully", zh: "视频合成成功", id: "Video berhasil disusun" },
  createComposeFailed: { en: "Video composition failed", zh: "视频合成失败", id: "Penyusunan video gagal" },
  createComposeTimeout: {
    en: "Composition is taking longer than expected. Please try again later.",
    zh: "合成处理超时，请稍后重试",
    id: "Penyusunan video memerlukan waktu lebih lama. Coba lagi nanti.",
  },
  createComposeInProgress: {
    en: "A composition task is already queued or in progress.",
    zh: "合成任务已在排队或进行中",
    id: "Tugas penyusunan sudah diantrekan atau sedang berjalan.",
  },
  createComposeParamsMissing: {
    en: "Composition parameters are missing. Please start composition again.",
    zh: "合成参数丢失，请重新点击合成",
    id: "Parameter penyusunan tidak lengkap. Mulai lagi.",
  },
  createComposeLongRunning: {
    en: "Composition is taking longer than expected. You can check the result in History later.",
    zh: "合成处理时间较长，可稍后在历史列表中查看结果",
    id: "Penyusunan video memerlukan waktu lebih lama. Anda dapat melihat hasilnya di Riwayat nanti.",
  },
  createComposeSubmitted: { en: "Composition task submitted", zh: "视频合成任务已提交", id: "Tugas penyusunan video dikirim" },
  createComposeSubmitFailed: {
    en: "Could not submit the composition task. Please try again later.",
    zh: "提交合成任务失败，请稍后重试",
    id: "Gagal mengirim tugas penyusunan. Coba lagi nanti.",
  },
  createComposeCancelled: { en: "Composition cancelled", zh: "已取消合成", id: "Penyusunan dibatalkan" },
  createComposeCancelFailed: {
    en: "Could not cancel the task. It may already be processing.",
    zh: "取消失败，任务可能已在处理中",
    id: "Tugas tidak dapat dibatalkan dan mungkin sedang diproses.",
  },
  createQueueAdded: {
    en: "Task added to the queue. You can track it in Task Management.",
    zh: "任务已成功添加至队列中，可在队列管理中查看进度",
    id: "Tugas ditambahkan ke antrean. Pantau di Manajemen Tugas.",
  },
  createNeedVideo: {
    en: "Generate at least one clip video before composing",
    zh: "请至少生成1个片段视频后再进行合成",
    id: "Buat setidaknya satu video klip sebelum menyusun",
  },
  createScriptEmpty: { en: "Script content cannot be empty", zh: "脚本内容不能为空", id: "Isi naskah tidak boleh kosong" },
  createCancelCompose: { en: "Cancel compose", zh: "取消合成", id: "Batal sintesis" },
  createExitQueue: { en: "Leave (task added to queue)", zh: "退出(任务已添加至队列)", id: "Keluar (tugas sudah masuk antrean)" },
  createNoVideoYet: { en: "No video yet", zh: "尚未生成视频", id: "Belum ada video dibuat" },
  createLoopClip: { en: "Loop this clip", zh: "循环本片段", id: "Ulangi klip ini" },
  createNoVideoContent: { en: "No video content yet", zh: "尚未生成视频内容", id: "Belum ada konten video" },
  createPreviewingAsset: { en: "Previewing asset", zh: "正在预览素材", id: "Mempratinjau aset" },
  createReturnTimeline: { en: "Back to timeline", zh: "返回时间轴", id: "Kembali ke timeline" },
  createApplyGlobal: { en: "Apply to all", zh: "应用到全局", id: "Terapkan ke semua" },
  createAudioTrack: { en: "Audio", zh: "音轨设置", id: "Audio" },
  createVideoTrackName: { en: "Video", zh: "视频", id: "Video" },
  createSubtitleTrackName: { en: "Subtitles", zh: "字幕", id: "Subtitle" },
  createAudioTrackName: { en: "Audio {count}", zh: "音频{count}", id: "Audio {count}" },
  createOriginalAudioTrackName: { en: "Original audio", zh: "原声", id: "Audio asli" },
  createAudioClipName: { en: "Audio track", zh: "音轨", id: "Trek audio" },
  createVideoClipName: { en: "Video clip", zh: "视频片段", id: "Klip video" },
  createAppliedAllVideo: { en: "Applied to all video clips", zh: "已应用到全部视频片段", id: "Diterapkan ke semua klip video" },
  createAppliedAllAudio: { en: "Applied to all audio clips", zh: "已应用到全部音频片段", id: "Diterapkan ke semua klip audio" },
  createAppliedAllSubtitle: { en: "Applied to all subtitles", zh: "已应用到全部字幕", id: "Diterapkan ke semua subtitle" },
  createAudioAddedToTimeline: {
    en: "Added {count} audio clip(s) to the timeline",
    zh: "已添加 {count} 段音频到时间轴",
    id: "Menambahkan {count} klip audio ke timeline",
  },
  createEnterSubtitle: { en: "Enter subtitle text", zh: "输入字幕内容", id: "Masukkan teks subtitle" },
  createSelectSubtitleFirst: {
    en: "Select a subtitle on the timeline first",
    zh: "请先在时间轴选中一条字幕",
    id: "Pilih subtitle di timeline terlebih dahulu",
  },
  createFontSize: { en: "Font size", zh: "字号", id: "Ukuran font" },
  createApplySubtitleCurrent: {
    en: "Changes apply to the selected subtitle track",
    zh: "完成后将应用到当前字幕轨",
    id: "Perubahan diterapkan ke trek subtitle yang dipilih",
  },
  createApplySubtitleAll: {
    en: "Changes apply to all subtitle tracks",
    zh: "完成后将应用到全部字幕轨",
    id: "Perubahan diterapkan ke semua trek subtitle",
  },
  createHideSubtitle: { en: "Hide subtitles", zh: "隐藏字幕", id: "Sembunyikan subtitle" },
  createRecognizingSubtitle: { en: "Recognizing…", zh: "识别中…", id: "Mengenali…" },
  createRegenerateSubtitle: { en: "Regenerate subtitles", zh: "重新识别字幕", id: "Buat ulang subtitle" },
  createTextTemplate: { en: "Text templates", zh: "文字模板", id: "Template teks" },
  createTextStickerProps: { en: "Sticker properties", zh: "贴图属性", id: "Properti stiker" },
  createTextLabel: { en: "Text", zh: "文案", id: "Teks" },
  createTextTemplateLoadFailed: { en: "Failed to load text templates", zh: "文字模板加载失败", id: "Gagal memuat template teks" },
  createNoTextTemplates: { en: "No text templates", zh: "暂无文字模板", id: "Belum ada template teks" },
  createNoHistoryClips: { en: "No generated clips yet", zh: "暂无历史生成片段", id: "Belum ada klip yang dibuat" },
  createVersionLabel: { en: "Version", zh: "版本", id: "Versi" },
  createAddToTimeline: { en: "Add to timeline", zh: "添加到时间轴", id: "Tambahkan ke timeline" },
  createOnTimeline: { en: "On timeline", zh: "已在时间轴", id: "Ada di timeline" },
  createSelected: { en: "Selected", zh: "已选中", id: "Dipilih" },
  createRemoveFromList: { en: "Remove from list", zh: "从列表移除", id: "Hapus dari daftar" },
  createNoAvailableVideoVersion: {
    en: "This version has no playable video",
    zh: "该版本暂无可用视频",
    id: "Versi ini tidak memiliki video yang dapat diputar",
  },
  createAlreadyOnTimeline: {
    en: "This version is already on the timeline",
    zh: "该版本已在时间轴中",
    id: "Versi ini sudah ada di timeline",
  },
  createAddedToTimeline: { en: "Added to timeline", zh: "已添加到时间轴", id: "Ditambahkan ke timeline" },
  createRemoveClipTitle: { en: "Remove clip", zh: "移除片段", id: "Hapus klip" },
  createRemoveClipConfirm: {
    en: "This clip is already on the timeline. Remove it there too?",
    zh: "该片段已在时间轴中，是否一并移除？",
    id: "Klip ini sudah ada di timeline. Hapus juga dari sana?",
  },
  createRemovedFromList: { en: "Removed from clip list", zh: "已从片段列表移除", id: "Dihapus dari daftar klip" },
  createSubtitleEmpty: { en: "Subtitle text cannot be empty", zh: "字幕内容不能为空", id: "Teks subtitle tidak boleh kosong" },
  createVideoDurationLoading: {
    en: "Video duration is still loading. Try again shortly.",
    zh: "视频时长加载中，请稍候再识别字幕",
    id: "Durasi video masih dimuat. Coba lagi sebentar.",
  },
  createTranscribing: {
    en: "Recognizing subtitles. Please wait.",
    zh: "正在识别字幕，请稍候",
    id: "Subtitle sedang dikenali. Mohon tunggu.",
  },
  createNoPlayableContent: {
    en: "No video clips yet. Generate clips first.",
    zh: "尚未生成视频内容，请先完成片段生成",
    id: "Belum ada klip video. Buat klip terlebih dahulu.",
  },
  createUnreadyClips: {
    en: "Some video clips are not ready. Return to the previous step and check them.",
    zh: "存在未就绪的视频片段，请返回上一步检查",
    id: "Beberapa klip video belum siap. Kembali ke langkah sebelumnya untuk memeriksanya.",
  },
  createCannotGetVideoUrl: { en: "Could not get the video URL", zh: "无法获取视频地址", id: "URL video tidak dapat diperoleh" },
  createCannotRecognizeAsset: {
    en: "Could not identify the dropped asset",
    zh: "无法识别拖入的素材",
    id: "Aset yang dijatuhkan tidak dapat dikenali",
  },
  createDetachAudio: { en: "Detach audio", zh: "分离音轨", id: "Pisahkan audio" },
  createDetachingAudio: { en: "Detaching…", zh: "分离中…", id: "Memisahkan…" },
  createDetachingSuffix: { en: " · Detaching…", zh: " · 分离中…", id: " · Memisahkan…" },
  createAudioDetached: { en: "Audio detached", zh: "音轨已分离", id: "Audio dipisahkan" },
  createNoEmbeddedAudio: { en: "This video has no audio track", zh: "该视频无音轨", id: "Video ini tidak memiliki trek audio" },
  createRetryDetachAudio: { en: "Retry detaching", zh: "重试分离", id: "Coba pisahkan lagi" },
  createMuteTrack: { en: "Mute track", zh: "静音整轨", id: "Bisukan trek" },
  createUnmuteTrack: { en: "Unmute track", zh: "取消静音", id: "Nyalakan suara trek" },
  createAlignTrack: { en: "Align clips to start", zh: "向前对齐", id: "Sejajarkan klip ke awal" },
  createTranscriptionFailed: { en: "Clip {n} transcription failed", zh: "片段 {n} 转录失败", id: "Transkripsi klip {n} gagal" },
  createSubtitlesRegenerated: { en: "Subtitles regenerated", zh: "字幕重新生成成功", id: "Subtitle berhasil dibuat ulang" },
  createUploadClipTitle: { en: "Add video clips", zh: "添加视频片段", id: "Tambahkan klip video" },
  createUploadClipTab: { en: "Upload clip", zh: "上传片段", id: "Unggah klip" },
  createVideoName: { en: "Video name", zh: "视频名称", id: "Nama video" },
  createVideoNameRequired: { en: "Enter a video name", zh: "请输入视频名称", id: "Masukkan nama video" },
  createVideoNamePlaceholder: { en: "Enter a video name...", zh: "输入视频名称...", id: "Masukkan nama video..." },
  createVideoDescription: { en: "Video description", zh: "视频描述", id: "Deskripsi video" },
  createVideoDescriptionPlaceholder: { en: "Describe the video content...", zh: "描述视频内容...", id: "Deskripsikan isi video..." },
  createReplaceFile: { en: "Click to replace", zh: "点击更换", id: "Klik untuk mengganti" },
  createVideoDuration: { en: "Duration {seconds}s", zh: "时长 {seconds}s", id: "Durasi {seconds} dtk" },
  createUploadVideo: { en: "Click to upload video", zh: "点击上传视频", id: "Klik untuk mengunggah video" },
  createVideoFormats: {
    en: "MP4, MOV, AVI and other common formats",
    zh: "支持 MP4, MOV, AVI 等常见格式",
    id: "Format umum seperti MP4, MOV, AVI",
  },
  createChooseLibrary: { en: "Choose from media library", zh: "从素材库选择", id: "Pilih dari pustaka media" },
  createLibraryDirectory: { en: "Media library", zh: "素材库目录", id: "Direktori pustaka media" },
  createRootDirectory: { en: "Root folder", zh: "根目录", id: "Folder root" },
  createSearchVideoAssets: { en: "Search folders or video assets...", zh: "搜索文件夹或视频素材...", id: "Cari folder atau aset video..." },
  createSortDefault: { en: "Recommended", zh: "综合排序", id: "Rekomendasi" },
  createSortName: { en: "Name", zh: "按名称", id: "Nama" },
  createSortTime: { en: "Time", zh: "按时间", id: "Waktu" },
  createSortSize: { en: "Size", zh: "按大小", id: "Ukuran" },
  createAscending: { en: "Ascending", zh: "正序", id: "Naik" },
  createDescending: { en: "Descending", zh: "倒序", id: "Turun" },
  createNoMatchingVideo: { en: "No matching video assets", zh: "没有匹配的视频素材", id: "Tidak ada aset video yang cocok" },
  createNoVideoAssets: { en: "No video assets", zh: "暂无视频素材", id: "Belum ada aset video" },
  createSearchAgain: { en: "Try another keyword", zh: "换个关键词再试试", id: "Coba kata kunci lain" },
  createUploadVideoFirst: {
    en: "Upload a video to the media library first",
    zh: "请先在素材库上传视频",
    id: "Unggah video ke pustaka media terlebih dahulu",
  },
  createSelectedCount: { en: "{count} selected", zh: "已选 {count} 项", id: "{count} dipilih" },
  createMultiSelect: { en: "Multiple selection", zh: "可多选", id: "Bisa pilih beberapa" },
  createConfirmSelection: { en: "Confirm selection", zh: "确认选择", id: "Konfirmasi pilihan" },
  createLoadVideoFailed: { en: "Failed to load video assets", zh: "加载素材视频失败", id: "Gagal memuat aset video" },
  createProductLibrary: { en: "Product library", zh: "商品库", id: "Pustaka produk" },
  createPickerProductHint: {
    en: "Click a product to view and select its images",
    zh: "点击商品查看并选择其中的图片",
    id: "Klik produk untuk melihat dan memilih gambarnya",
  },
  createPickerProductImagesHint: {
    en: "Click to select product images. You can select multiple images and confirm.",
    zh: "点击选择商品图片，可多选后确认",
    id: "Klik untuk memilih gambar produk. Anda dapat memilih beberapa gambar lalu mengonfirmasi.",
  },
  createPickerNoProductImages: { en: "This product has no images", zh: "该商品暂无图片", id: "Produk ini tidak memiliki gambar" },
  createPickerImageLabel: { en: "Image {n}", zh: "图{n}", id: "Gambar {n}" },
  createPickerLoadingMore: { en: "Loading more…", zh: "加载更多…", id: "Memuat lagi…" },
  createPickerSearchProductImages: { en: "Search product images...", zh: "搜索商品图片...", id: "Cari gambar produk..." },
  createPickerSearchProducts: { en: "Search product names...", zh: "搜索商品名称...", id: "Cari nama produk..." },
  createPickerSearchImages: { en: "Search image assets...", zh: "搜索图片素材...", id: "Cari aset gambar..." },
  createPickerNoMatchingProducts: { en: "No matching products", zh: "没有匹配的商品", id: "Tidak ada produk yang cocok" },
  createPickerNoMatchingImages: { en: "No matching image assets", zh: "没有匹配的图片素材", id: "Tidak ada aset gambar yang cocok" },
  createPickerNoProducts: { en: "No products", zh: "暂无商品", id: "Belum ada produk" },
  createPickerNoImages: { en: "No image assets", zh: "暂无图片素材", id: "Belum ada aset gambar" },
  createPickerCreateProduct: {
    en: "Create a product in the product library first, or switch to another folder",
    zh: "请先在商品库创建商品，或切换其他目录",
    id: "Buat produk di pustaka produk terlebih dahulu, atau ganti folder lain",
  },
  createPickerUploadImagesFirst: {
    en: "Upload an image to the media library first",
    zh: "请先在素材库上传图片",
    id: "Unggah gambar ke pustaka media terlebih dahulu",
  },
  createPickerDurationHint: {
    en: "Only videos up to {seconds} seconds can be selected",
    zh: "仅可选择 {seconds} 秒及以内的视频",
    id: "Hanya video hingga {seconds} detik yang dapat dipilih",
  },
  createPickerNoVideosWithinDuration: {
    en: "No videos up to {seconds} seconds. Upload one or switch to another folder",
    zh: "暂无 {seconds} 秒及以内的视频，请先上传或换个目录",
    id: "Tidak ada video hingga {seconds} detik. Unggah video atau ganti folder lain",
  },
  createPickerLoadAssetLibraryFailed: {
    en: "Failed to load media library folders",
    zh: "加载资产库目录失败",
    id: "Gagal memuat folder pustaka media",
  },
  createPickerLoadMoreFailed: { en: "Failed to load more assets", zh: "加载更多素材失败", id: "Gagal memuat aset lainnya" },
  createPickerDurationLimit: {
    en: "Select a video no longer than {seconds} seconds",
    zh: "请选择时长不超过 {seconds} 秒的视频",
    id: "Pilih video dengan durasi tidak lebih dari {seconds} detik",
  },
  createSelectBackgroundMusic: { en: "Choose background music", zh: "选择背景音乐", id: "Pilih musik latar" },
  createLoadingDirectory: { en: "Loading folders…", zh: "加载目录…", id: "Memuat folder…" },
  createNoAssetDirectories: { en: "No media library folders", zh: "暂无素材库目录", id: "Tidak ada folder pustaka media" },
  createSearchFolders: { en: "Search folders...", zh: "搜索文件夹...", id: "Cari folder..." },
  createNoMatchingFolders: { en: "No matching folders", zh: "未找到相关文件夹", id: "Tidak ada folder yang cocok" },
  createLoadLibraryFoldersFailed: {
    en: "Failed to load library folders",
    zh: "加载素材库目录失败",
    id: "Gagal memuat folder pustaka",
  },
  createLoadLibraryAudioFailed: {
    en: "Failed to load library audio",
    zh: "加载素材库音频失败",
    id: "Gagal memuat audio pustaka",
  },
  createLoadingLibraryAudio: { en: "Loading library audio...", zh: "加载素材库音乐...", id: "Memuat audio pustaka..." },
  createNoLibraryAudio: { en: "No audio in the media library", zh: "素材库暂无音频", id: "Belum ada audio di pustaka media" },
  createPausePreview: { en: "Pause preview", zh: "暂停试听", id: "Jeda pratinjau" },
  createPreview: { en: "Preview", zh: "试听", id: "Pratinjau" },
  createPreviewProgress: { en: "Preview progress for {name}", zh: "试听进度 {name}", id: "Kemajuan pratinjau {name}" },
  createAudioCannotPlay: { en: "Audio could not be played", zh: "音频无法播放", id: "Audio tidak dapat diputar" },
  createUploadAudioTitle: { en: "Upload audio", zh: "上传音频", id: "Unggah audio" },
  confirmUpload: { en: "Upload", zh: "确认上传", id: "Unggah" },
  submitting: { en: "Submitting...", zh: "提交中...", id: "Mengirim..." },
  uploading: { en: "Uploading...", zh: "上传中...", id: "Mengunggah..." },
  createAudioName: { en: "Audio name", zh: "音频名称", id: "Nama audio" },
  createAudioNamePlaceholder: { en: "Enter an audio name...", zh: "输入音频名称...", id: "Masukkan nama audio..." },
  createAudioDescription: { en: "Audio description", zh: "音频描述", id: "Deskripsi audio" },
  createAudioDescriptionPlaceholder: {
    en: "Describe the audio content or style...",
    zh: "描述音频内容或风格...",
    id: "Deskripsikan isi atau gaya audio...",
  },
  createUploadAudioPrompt: { en: "Click or drag to upload audio", zh: "点击或拖拽上传音频", id: "Klik atau seret untuk mengunggah audio" },
  createAudioFormats: { en: "MP3 and WAV formats", zh: "支持 MP3, WAV 格式", id: "Format MP3 dan WAV" },
  createAudioNameRequired: { en: "Enter an audio name", zh: "请输入音频名称", id: "Masukkan nama audio" },
  createUploadSuccess: { en: "Upload successful", zh: "上传成功", id: "Unggah berhasil" },
  createUploadFailed: { en: "Upload failed. Please try again.", zh: "上传失败，请重试", id: "Unggah gagal. Coba lagi." },
  createPickAudio: { en: "Choose audio", zh: "选择音频", id: "Pilih audio" },
  createUploadAudio: { en: "Upload audio", zh: "上传音频", id: "Unggah audio" },
  createVolume: { en: "Volume", zh: "音量", id: "Volume" },
  createVideoVolume: { en: "Video volume", zh: "视频音量", id: "Volume video" },
  createBgmLabel: { en: "BGM", zh: "BGM", id: "BGM" },
  createOriginalAudioLabel: { en: "Original audio", zh: "原声", id: "Audio asli" },
  createOrigVideo: { en: "Original", zh: "原视频", id: "Video asli" },
  createSubtitle: { en: "Subtitles", zh: "字幕", id: "Subtitle" },
  createStyle: { en: "Style", zh: "样式", id: "Gaya" },
  createSize: { en: "Size", zh: "字号", id: "Ukuran" },
  createFontNormal: { en: "Regular", zh: "常规", id: "Reguler" },
  createFontBold: { en: "Bold", zh: "加粗", id: "Tebal" },
  createFontItalic: { en: "Italic", zh: "斜体", id: "Miring" },
  createSubPos: { en: "Adjust subtitle position", zh: "调整字幕位置", id: "Atur posisi subtitle" },
  createShowSubtitle: { en: "Show subtitles", zh: "显示字幕", id: "Tampilkan subtitle" },
  createVideoTrack: { en: "Video track", zh: "视频轨道", id: "Trek video" },
  createAddVideo: { en: "Add video", zh: "添加视频", id: "Tambah video" },
  createPickLibrary: { en: "Media library", zh: "选择素材库", id: "Pilih pustaka materi" },
  createUploadLocal: { en: "Upload local", zh: "上传本地", id: "Unggah lokal" },
  createSubtitleContent: { en: "Subtitle text", zh: "字幕内容", id: "Konten subtitle" },
  createAddSubtitle: { en: "Add subtitle", zh: "添加字幕", id: "Tambah subtitle" },
  createNewSubtitle: { en: "New subtitle", zh: "新建字幕", id: "Subtitle baru" },
  createSubtitlePreview: { en: "Subtitle preview", zh: "字幕预览", id: "Pratinjau subtitle" },
  createSubtitlePh: { en: "Enter subtitle text...", zh: "输入字幕内容...", id: "Masukkan konten subtitle..." },
  createNoSubtitle: { en: "This clip has no subtitles", zh: "该片段暂无字幕", id: "Klip ini tidak ada subtitle" },
  createNewClip: { en: "New clip", zh: "新片段", id: "Klip baru" },
  createLocalVideo: { en: "Local video", zh: "本地视频", id: "Video lokal" },
  createLocalMusic: { en: "Local music", zh: "本地音乐", id: "Musik lokal" },
  createVideoClipsAdded: {
    en: "Added {count} videos to the clip list",
    zh: "已添加 {count} 个视频到片段列表",
    id: "Menambahkan {count} video ke daftar klip",
  },
  createVideoClipAdded: { en: "Added to the video clip list", zh: "已添加到视频片段列表", id: "Ditambahkan ke daftar klip video" },
  createMade: { en: "Ready", zh: "已生成", id: "Telah dibuat" },
  createPublish: { en: "Publish", zh: "发布", id: "Rilis" },
  createPublishing: { en: "Publishing...", zh: "发布中...", id: "Merilis..." },
  createPublishOk: { en: "Published", zh: "发布成功", id: "Berhasil rilis" },
  publishedBadge: { en: "Published", zh: "已发布", id: "Dipublikasikan" },
  viewInFeatured: { en: "View in Featured", zh: "去精选查看", id: "Lihat di Pilihan" },
  publishLiveOk: {
    en: "Published. Others can like, save, and comment in Featured.",
    zh: "已发布，其他用户可在精选中看到并点赞、收藏、评论。",
    id: "Dipublikasikan. Pengguna lain bisa menyukai, menyimpan, dan berkomentar di Pilihan.",
  },
  publishDialogTitle: { en: "Publish to Featured", zh: "发布到精选", id: "Rilis ke Pilihan" },
  publishDialogDesc: {
    en: "Fill in a title and description. Only published works appear in Featured.",
    zh: "填写标题与描述，发布成功后才会出现在精选页。",
    id: "Isi judul dan deskripsi. Hanya karya yang dirilis muncul di Pilihan.",
  },
  publishCoverLabel: { en: "Cover", zh: "封面", id: "Sampul" },
  publishTitleLabel: { en: "Title", zh: "标题", id: "Judul" },
  publishTitlePh: { en: "Enter a title, max 50 characters", zh: "请输入标题，最多 50 字", id: "Masukkan judul, maks 50 karakter" },
  publishDescLabel: { en: "Description", zh: "描述", id: "Deskripsi" },
  publishDescPh: {
    en: "Enter a description, max 500 characters",
    zh: "请输入描述，最多 500 字",
    id: "Masukkan deskripsi, maks 500 karakter",
  },
  publishTitleRequired: { en: "Please enter a title", zh: "请填写标题", id: "Harap masukkan judul" },
  publishTitleTooLong: { en: "Title cannot exceed 50 characters", zh: "标题不能超过 50 个字符", id: "Judul maksimal 50 karakter" },
  publishCategoryLabel: { en: "Category", zh: "视频分类", id: "Kategori" },
  publishCategoryNone: { en: "Not classified yet", zh: "未识别", id: "Belum diklasifikasi" },
  publishCategoryRunning: { en: "Classifying", zh: "识别中", id: "Mengklasifikasi" },
  publishCategoryFailed: { en: "Classification failed", zh: "识别失败", id: "Klasifikasi gagal" },
  publishCategoryRetry: { en: "Classify again", zh: "重新识别", id: "Klasifikasi ulang" },
  publishCategoryRetryOk: { en: "Classification started", zh: "已开始识别分类", id: "Klasifikasi dimulai" },
  publishDescTooLong: {
    en: "Description cannot exceed 500 characters",
    zh: "描述不能超过 500 个字符",
    id: "Deskripsi maksimal 500 karakter",
  },
  publishFailed: { en: "Publish failed, please try again", zh: "发布失败，请稍后重试", id: "Gagal merilis, coba lagi" },
  publishRepublish: { en: "Update publish info", zh: "修改发布信息", id: "Ubah info rilis" },
  publishNoVideo: {
    en: "This work has no finished video yet",
    zh: "该作品尚未生成成片，暂不可发布",
    id: "Karya ini belum punya video jadi",
  },
  // —— 取消发布（与发布对偶） ——
  createUnpublish: { en: "Unpublish", zh: "取消发布", id: "Batalkan publikasi" },
  unpublishOk: { en: "Unpublished", zh: "已取消发布", id: "Publikasi dibatalkan" },
  unpublishFailed: {
    en: "Unpublish failed, please try again",
    zh: "取消发布失败，请稍后重试",
    id: "Gagal membatalkan publikasi, coba lagi",
  },
  unpublishConfirm: {
    en: "Unpublish this work from Featured? It will no longer be visible to others.",
    zh: "确定要取消发布吗？取消后将不再展示给其他用户。",
    id: "Batalkan publikasi karya ini dari Pilihan? Karya tidak akan terlihat oleh orang lain.",
  },
  workLikeDetail: { en: "Likes", zh: "点赞", id: "Suka" },
  workSaveDetail: { en: "Saves", zh: "收藏", id: "Simpan" },
  workCommentDetail: { en: "Comments", zh: "评论", id: "Komentar" },
  likedYourWork: { en: "liked your video", zh: "赞了你的作品", id: "menyukai video Anda" },
  savedYourWork: { en: "saved your video", zh: "收藏了你的作品", id: "menyimpan video Anda" },
  emptyWorkLikes: { en: "No likes yet", zh: "还没有人点赞", id: "Belum ada yang menyukai" },
  emptyWorkSaves: { en: "No saves yet", zh: "还没有人收藏", id: "Belum ada yang menyimpan" },
  emptyWorkComments: { en: "No comments yet", zh: "还没有评论", id: "Belum ada komentar" },

  veAddAsset: { en: "Add media", zh: "添加素材", id: "Tambah materi" },
  veExpandAssets: { en: "Expand assets", zh: "展开素材", id: "Bentangkan materi" },
  veCollapseAssets: { en: "Collapse assets", zh: "收起素材", id: "Tutup materi" },
  veSmartSubtitle: { en: "Smart subtitles", zh: "智能生成字幕", id: "Subtitle cerdas" },
  veRecognizing: { en: "Recognizing…", zh: "识别中…", id: "Mengenali…" },
  veMediaLibrary: { en: "Media library", zh: "素材库", id: "Pustaka materi" },
  veGoStart: { en: "Go to start", zh: "转到开始", id: "Ke awal" },
  vePreview: { en: "Preview", zh: "预览", id: "Pratinjau" },
  veClickClipToEdit: {
    en: "Tap a clip on the timeline to edit",
    zh: "点击轨道上的片段进行编辑",
    id: "Ketuk klip di timeline untuk mengedit",
  },
  veSplit: { en: "Split", zh: "分割", id: "Pisah" },
  veSpeed: { en: "Speed", zh: "变速", id: "Kecepatan" },
  veVolume: { en: "Volume", zh: "音量", id: "Volume" },
  veEmptyAssets: {
    en: "No assets. Tap Add media to upload or pick from the library.",
    zh: "暂无素材，点击「添加素材」上传或从素材库选择",
    id: "Belum ada materi. Ketuk Tambah materi untuk unggah atau pilih dari pustaka.",
  },
  veVideoTrack: { en: "Video track", zh: "视频轨道", id: "Trek video" },
  veAudioTrack: { en: "Audio track", zh: "音频轨道", id: "Trek audio" },
  veSubtitleTrack: { en: "Subtitle track", zh: "字幕轨道", id: "Trek subtitle" },
  veImageTrack: { en: "Image track", zh: "图片轨道", id: "Trek gambar" },
  veTabVideo: { en: "Video", zh: "视频", id: "Video" },
  veTabAudio: { en: "Audio", zh: "音频", id: "Audio" },
  veTabImage: { en: "Image", zh: "图片", id: "Gambar" },
  veTabSubtitle: { en: "Subtitle", zh: "字幕", id: "Subtitle" },
  veTabTransition: { en: "Transition", zh: "转场", id: "Transisi" },
  veTabSound: { en: "SFX", zh: "音效", id: "Efek" },
  veLocalUpload: { en: "Upload local", zh: "本地上传", id: "Unggah lokal" },
  vePlay: { en: "Play", zh: "播放", id: "Putar" },
  vePause: { en: "Pause", zh: "暂停", id: "Jeda" },
  veGoEnd: { en: "Go to end", zh: "转到结束", id: "Ke akhir" },
  veMute: { en: "Mute", zh: "静音", id: "Bisukan" },
  veUnmute: { en: "Unmute", zh: "取消静音", id: "Bunyikan" },
  veDeleteClip: { en: "Delete clip", zh: "删除片段", id: "Hapus klip" },
  veCropLeft: { en: "Trim left", zh: "向左裁剪", id: "Potong kiri" },
  veCropRight: { en: "Trim right", zh: "向右裁剪", id: "Potong kanan" },
  veLock: { en: "Lock", zh: "锁定", id: "Kunci" },
  veUnlock: { en: "Unlock", zh: "解锁", id: "Buka kunci" },
  veHide: { en: "Hide", zh: "隐藏", id: "Sembunyi" },
  veShow: { en: "Show", zh: "显示", id: "Tampil" },
  veParamHint: {
    en: "Select a track or clip below to edit",
    zh: "选择下方轨道或片段后，在此设置参数",
    id: "Pilih trek atau klip di bawah untuk mengatur",
  },
  veOrigOff: { en: "Original off", zh: "原声关", id: "Suara asli off" },
  veDetachAudio: { en: "Detach audio", zh: "剥离原声", id: "Pisah audio" },
  veDetachBusy: { en: "Detaching…", zh: "剥离中…", id: "Memisah…" },
  veDetachTrack: { en: "Detach audio track", zh: "剥离音轨", id: "Pisah trek audio" },
  vePosition: { en: "Pos", zh: "位置", id: "Pos" },
  veSize: { en: "Size", zh: "尺寸", id: "Ukuran" },
  veScale: { en: "Scale", zh: "缩放", id: "Skala" },
  veRotate: { en: "Rotate", zh: "旋转", id: "Putar" },
  veOpacity: { en: "Opacity", zh: "透明", id: "Opasitas" },
  veSelect: { en: "Select", zh: "选择", id: "Pilih" },
  veContent: { en: "Text", zh: "内容", id: "Teks" },
  veFont: { en: "Font", zh: "字体", id: "Font" },
  veColor: { en: "Color", zh: "颜色", id: "Warna" },
  veSpacing: { en: "Gap", zh: "间距", id: "Jarak" },
  veAlign: { en: "Align", zh: "对齐", id: "Rata" },
  veUnderline: { en: "Underline", zh: "下划线", id: "Garis bawah" },
  veStrike: { en: "Strikethrough", zh: "删除线", id: "Coret" },
  veAlignLeft: { en: "Left", zh: "左对齐", id: "Kiri" },
  veAlignCenter: { en: "Center", zh: "居中对齐", id: "Tengah" },
  veAlignRight: { en: "Right", zh: "右对齐", id: "Kanan" },
  veLetter: { en: "Letter", zh: "字", id: "Huruf" },
  veLine: { en: "Line", zh: "行", id: "Baris" },
  veSec: { en: "s", zh: "秒", id: "dtk" },
  vePrevCat: { en: "Previous category", zh: "上一个分类", id: "Kategori sebelumnya" },
  veNextCat: { en: "Next category", zh: "下一个分类", id: "Kategori berikutnya" },
  veSlideLeft: { en: "Slide left", zh: "向左滑动", id: "Geser kiri" },
  veSlideRight: { en: "Slide right", zh: "向右滑动", id: "Geser kanan" },
  veLinkedAudio: { en: "Linked to audio, moves together", zh: "与音轨绑定，拖动时联动", id: "Terikat audio, bergerak bersama" },
  veSwitchVariant: { en: "Switch clip version", zh: "切换该片段使用的视频", id: "Ganti versi klip" },
  veTrimStart: { en: "Adjust start / zoom", zh: "调整起点 / 缩放", id: "Atur awal / zoom" },
  veTrimEnd: { en: "Adjust end / zoom", zh: "调整终点 / 缩放", id: "Atur akhir / zoom" },
  veFontSans: { en: "Sans", zh: "思源黑体", id: "Sans" },
  veFontSerif: { en: "Serif", zh: "思源宋体", id: "Serif" },
  veFontRound: { en: "Rounded", zh: "阿里巴巴普惠体", id: "Rounded" },
  veFontKai: { en: "Kai", zh: "楷体", id: "Kai" },
  veAudioOf: { en: "{name} · audio", zh: "{name} · 音轨", id: "{name} · audio" },
  veSubtitleN: { en: "Subtitle {n}", zh: "字幕 {n}", id: "Subtitle {n}" },
  veImageN: { en: "Image {n}", zh: "图片 {n}", id: "Gambar {n}" },
  veVideoN: { en: "Video {n}", zh: "视频 {n}", id: "Video {n}" },
  veAudioN: { en: "Audio {n}", zh: "音频 {n}", id: "Audio {n}" },
  veVersionN: { en: "Version {n}", zh: "版本 {n}", id: "Versi {n}" },
  veVoiceSubtitle: { en: "Voiceover subtitle", zh: "口播字幕", id: "Subtitle lisan" },
  veSfxWhoosh: { en: "Whoosh", zh: "转场 Whoosh", id: "Whoosh" },
  veSfxDing: { en: "Ding", zh: "叮咚提示", id: "Ding" },
  veSfxTypewriter: { en: "Typewriter", zh: "打字机", id: "Mesin ketik" },
  veSfxApplause: { en: "Applause", zh: "掌声", id: "Tepuk tangan" },
  veSfxDrum: { en: "Hit drum", zh: "爆点鼓声", id: "Drum hit" },
  veSfxPop: { en: "Bubble pop", zh: "气泡弹出", id: "Gelembung" },
  veSfxPage: { en: "Page turn", zh: "翻页", id: "Balik halaman" },
  veSfxCheer: { en: "Cheer", zh: "欢呼", id: "Sorak" },
  veTransDissolve: { en: "Dissolve", zh: "叠化", id: "Dissolve" },
  veTransFlashBlack: { en: "Flash black", zh: "闪黑", id: "Flash hitam" },
  veTransSlideH: { en: "Slide H", zh: "左右滑动", id: "Geser horizontal" },
  veTransSlideV: { en: "Slide V", zh: "上下滑动", id: "Geser vertikal" },
  veTransZoom: { en: "Zoom", zh: "缩放", id: "Zoom" },
  veTransRotate: { en: "Rotate", zh: "旋转", id: "Putar" },
  veTransGlitch: { en: "Glitch", zh: "故障", id: "Glitch" },
  veTransPage: { en: "Page turn", zh: "翻页", id: "Balik halaman" },
  veSubTplDefault: { en: "Default", zh: "默认字幕", id: "Default" },
  veSubTplTitle: { en: "Title", zh: "标题字幕", id: "Judul" },
  veSubTplOutline: { en: "Outline", zh: "花字·描边", id: "Outline" },
  veSubTplGradient: { en: "Gradient", zh: "花字·渐变", id: "Gradien" },
  veSubTplVariety: { en: "Variety bounce", zh: "综艺弹跳", id: "Variety" },
  veSubTplVoice: { en: "Voiceover style", zh: "口播字幕", id: "Gaya lisan" },
  veSubSampleDefault: { en: "Sample subtitle", zh: "示例字幕内容", id: "Contoh subtitle" },
  veSubSampleTitle: { en: "Big title", zh: "大标题", id: "Judul besar" },
  veSubSampleOutline: { en: "Outline text", zh: "描边花字", id: "Teks outline" },
  veSubSampleGradient: { en: "Gradient text", zh: "渐变花字", id: "Teks gradien" },
  veSubSampleVariety: { en: "Variety bounce", zh: "综艺弹跳", id: "Variety" },
  veSubSampleVoice: { en: "Voiceover style", zh: "口播字幕样式", id: "Gaya lisan" },
  veSeg1: { en: "Clip 1 · Hook", zh: "片段 1 · 开场吸睛", id: "Klip 1 · Hook" },
  veSeg2: { en: "Clip 2 · Pain point", zh: "片段 2 · 痛点铺垫", id: "Klip 2 · Pain point" },
  veSeg3: { en: "Clip 3 · Product", zh: "片段 3 · 产品展示", id: "Klip 3 · Produk" },
  veSeg4: { en: "Clip 4 · Benefits", zh: "片段 4 · 卖点讲解", id: "Klip 4 · Manfaat" },
  veSeg5: { en: "Clip 5 · CTA", zh: "片段 5 · 行动号召", id: "Klip 5 · CTA" },
  veAiSub1: { en: "Hi, welcome to this video", zh: "大家好，欢迎来到本期视频", id: "Hai, selamat datang di video ini" },
  veAiSub2: { en: "Today we talk about this topic", zh: "今天我们聊聊这个话题", id: "Hari ini kita bahas topik ini" },
  veAiSub3: { en: "First, look at point one", zh: "先看第一个要点", id: "Pertama, lihat poin satu" },
  veAiSub4: { en: "Next is the second part", zh: "接下来是第二个部分", id: "Berikutnya bagian kedua" },
  veAiSub5: { en: "Pay special attention here", zh: "这里需要特别注意", id: "Perhatikan bagian ini" },
  veAiSub6: { en: "A quick summary", zh: "我们再做一个简单总结", id: "Ringkasan singkat" },
  veAiSub7: { en: "That is everything", zh: "以上就是全部内容", id: "Itu saja isinya" },
  veAiSub8: { en: "Thanks for watching", zh: "感谢观看，下期再见", id: "Terima kasih, sampai jumpa" },
  createBgm: { en: "BGM", zh: "BGM", id: "BGM" },
  rwBoundCount: { en: "({n} total)", zh: "(共{n}个)", id: "({n} total)" },
  rwDefaultShot: { en: "Medium · locked off", zh: "中景 · 固定镜头", id: "Medium · locked off" },
  rwElHost: { en: "Livestream host", zh: "带货主播", id: "Host live" },
  rwElYam: { en: "White yam", zh: "白色山药", id: "Ubi putih" },
  rwElKitchen: { en: "Home kitchen", zh: "居家厨房", id: "Dapur rumah" },
  rwElPersonLabel: { en: "Character", zh: "人物", id: "Karakter" },
  rwElProductLabel: { en: "Product", zh: "产品", id: "Produk" },
  rwElSceneLabel: { en: "Scene", zh: "场景", id: "Adegan" },
  rwElHostDesc: { en: "On-camera talent", zh: "出镜达人", id: "Talent on-cam" },
  rwElProductDesc: { en: "Hero product", zh: "主推产品", id: "Produk utama" },
  rwElSceneDesc: { en: "Shoot location", zh: "拍摄场景", id: "Lokasi syuting" },

  rwClipScript: { en: "Clip script", zh: "片段文字脚本", id: "Naskah klip" },
  rwClickGenBoard: { en: "Generate board", zh: "点击生成画板", id: "Buat papan" },
  rwView: { en: "View", zh: "查看", id: "Lihat" },
  rwRegen: { en: "Regenerate", zh: "重新生成", id: "Buat ulang" },
  rwGenClipVideo: { en: "Generate clip video", zh: "生成片段视频", id: "Buat video klip" },
  rwGeneratingClip: { en: "Generating clip {n}…", zh: "正在生成片段 {n} 视频…", id: "Membuat klip {n}…" },
  rwGeneratingHint: {
    en: "Generating clip video. You can switch to other clips and keep generating.",
    zh: "正在生成片段视频，可以切换至其他片段继续生成",
    id: "Sedang membuat video klip. Bisa pindah ke klip lain dan lanjut buat.",
  },
  rwBindDh: { en: "Bind character", zh: "绑定数字人", id: "Ikat karakter" },
  rwBindImage: { en: "Bind image", zh: "绑定图片", id: "Ikat gambar" },
  rwColClip: { en: "Clip", zh: "片段", id: "Klip" },
  rwColClipVideo: { en: "Clip video", zh: "片段视频", id: "Video klip" },
  rwColShotNo: { en: "Shot", zh: "镜号", id: "Shot" },
  rwColTimeline: { en: "Timeline", zh: "时间轴", id: "Timeline" },
  rwColVisual: { en: "Visual", zh: "画面描述", id: "Visual" },
  rwColVoice: { en: "Voiceover", zh: "口播", id: "Lisan" },
  rwColReplaced: { en: "Replaced", zh: "已替换元素", id: "Elemen diganti" },
  rwBoard: { en: "Board", zh: "画板", id: "Papan" },
  rwOrigClip: { en: "Scene {n} original", zh: "第 {n} 幕原片段", id: "Adegan {n} asli" },
  rwSceneClipTitle: { en: "Scene {n} {label}", zh: "第 {n} 幕{label}", id: "Adegan {n} {label}" },
  rwGenN: { en: "Take {n}", zh: "生成 {n}", id: "Take {n}" },
  rwHelpAria: { en: "Help", zh: "说明", id: "Bantuan" },
  rwTourMore: { en: "Try the other tools here", zh: "其他几个功能，按需点击看看吧", id: "Coba fitur lain di sini" },

  /** 以下为 Vue 壳层补充字段，原型 dict 未覆盖的占位/校验文案 */
  comingSoon: { en: "Coming soon", zh: "即将上线", id: "Segera hadir" },
  featuredComingSoon: { en: "Featured content is coming soon", zh: "精选内容即将上线", id: "Konten Pilihan segera hadir" },
  dramaComingSoon: { en: "Dramas are coming soon", zh: "短剧内容即将上线", id: "Drama segera hadir" },
  followingComingSoon: { en: "Following is coming soon", zh: "关注内容即将上线", id: "Mengikuti segera hadir" },
  meComingSoon: { en: "Your profile is coming soon", zh: "个人主页即将上线", id: "Profil segera hadir" },
  openInNewWindow: { en: "Open in new window", zh: "在新窗口打开", id: "Buka di jendela baru" },
  popupBlocked: {
    en: "The browser blocked the new window. Allow pop-ups and try again.",
    zh: "浏览器拦截了新窗口，请允许弹出窗口后重试",
    id: "Browser memblokir jendela baru. Izinkan pop-up lalu coba lagi.",
  },
  backToFeatured: { en: "Back to Featured", zh: "回到精选", id: "Kembali ke Pilihan" },
  videoUnavailable: {
    en: "This video is unavailable or has been removed",
    zh: "视频不存在或已下架",
    id: "Video tidak tersedia atau telah dihapus",
  },
  profileCenter: { en: "Profile", zh: "个人中心", id: "Pusat profil" },
  teamManage: { en: "Team", zh: "团队管理", id: "Manajemen tim" },
  queueManage: { en: "Queue", zh: "队列管理", id: "Manajemen antrean" },
  rechargeBeans: { en: "Get Dream Beans", zh: "梦想豆充值", id: "Isi ulang Kacang Impian" },
  /** 本页副标题：一句话说清充豆链路（原型 screen-beanpacks 页头文案） */
  beanRechargeIntro: {
    en: "Pick a tier → an order is generated → pay online on the secure checkout → Dream Beans are credited automatically.",
    zh: "选一档充值 → 生成订单 → 在线支付 → 支付成功即自动发豆。",
    id: "Pilih paket → pesanan dibuat → bayar online di halaman checkout → Kacang Impian masuk otomatis.",
  },
  /**
   * 本页副标题（线下转账模式 `pay.mode=offline` 的版本）。
   *
   * <p>与 {@link beanRechargeIntro} 的区别：**不出现「在线支付 / checkout」**，
   * 因为该模式下前端完全不拉收银台，说「在线支付」会让用户去找一个不存在的按钮。</p>
   */
  beanRechargeIntroOffline: {
    en: "Pick a tier → an order is generated → transfer the amount to our bank account → Dream Beans are credited once we confirm receipt.",
    zh: "选一档充值 → 生成订单 → 按页面收款信息转账 → 我们确认到账后自动发豆。",
    id: "Pilih paket → pesanan dibuat → transfer ke rekening kami → Kacang Impian masuk setelah dana kami terima.",
  },
  /** 「会员是充豆前置」门槛卡（原型 screen-beanpacks 的 only-guest 区） */
  beanRechargeGateTitle: { en: "You are not a member yet", zh: "你还不是会员", id: "Anda belum menjadi anggota" },
  beanRechargeGateDesc: {
    en: "Dream Beans top-up requires an active membership — membership comes first.",
    zh: "梦想豆充值需要先开通会员 —— 会员是充豆的前置。",
    id: "Isi ulang Kacang Impian memerlukan keanggotaan aktif — keanggotaan lebih dulu.",
  },
  beanRechargeGateCta: { en: "Choose a membership tier", zh: "去选择会员档位", id: "Pilih paket keanggotaan" },
  beanRechargeNeedMember: {
    en: "Please activate a membership before topping up Dream Beans",
    zh: "请先开通会员后再充值梦想豆",
    id: "Aktifkan keanggotaan sebelum mengisi Kacang Impian",
  },
  beanRechargeFailed: {
    en: "Could not create the top-up order. Please try again.",
    zh: "生成充值订单失败，请重试",
    id: "Gagal membuat pesanan isi ulang. Coba lagi.",
  },
  creator: { en: "Creator", zh: "创作者", id: "Kreator" },
  beansCount: { en: "{n} Dream Beans", zh: "{n} 梦想豆", id: "{n} Kacang Impian" },
  sending: { en: "Sending...", zh: "发送中", id: "Mengirim..." },
  agreeTermsFirst: {
    en: "Please read the Terms and Privacy Policy first",
    zh: "请先阅读服务条款和隐私政策",
    id: "Harap baca Ketentuan dan Kebijakan Privasi terlebih dahulu",
  },
  invalidPhone: { en: "Enter a valid phone number", zh: "请输入正确的手机号", id: "Masukkan nomor telepon yang valid" },
  codeSent: { en: "Code sent", zh: "验证码已发送", id: "Kode terkirim" },
  enterPhone: { en: "Enter phone number", zh: "请输入手机号", id: "Masukkan nomor telepon" },
  enterCode: { en: "Enter verification code", zh: "请输入验证码", id: "Masukkan kode verifikasi" },
  enterAccountOrPhone: {
    en: "Enter username or phone number",
    zh: "请输入手机号或用户名",
    id: "Masukkan nama pengguna atau nomor telepon",
  },
  enterPassword: { en: "Enter password", zh: "请输入密码", id: "Masukkan kata sandi" },
  sessionExpired: { en: "Session expired. Please log in again.", zh: "登录已失效，请重新登录", id: "Sesi berakhir. Silakan masuk lagi." },
  loginSuccess: { en: "Login successful", zh: "登录成功", id: "Login berhasil" },
  logoutConfirm: {
    en: "Log out of this account?",
    zh: "确定要退出当前账号吗？",
    id: "Keluar dari akun ini?",
  },
  closeLogin: { en: "Close login", zh: "关闭登录弹窗", id: "Tutup masuk" },

  loading: { en: "Loading...", zh: "加载中...", id: "Memuat..." },
  deleting: { en: "Deleting...", zh: "删除中...", id: "Menghapus..." },
  generating: { en: "Generating...", zh: "生成中...", id: "Membuat..." },
  saving: { en: "Saving...", zh: "保存中...", id: "Menyimpan..." },
  confirming: { en: "Confirming...", zh: "确认中...", id: "Mengonfirmasi..." },
  save: { en: "Save", zh: "保存", id: "Simpan" },
  copy: { en: "Copy", zh: "复制", id: "Salin" },
  modify: { en: "Edit", zh: "修改", id: "Ubah" },
  moreActions: { en: "More actions", zh: "更多操作", id: "Aksi lainnya" },
  loadMore: { en: "Load more ({loaded}/{total})", zh: "加载更多（已加载 {loaded}/{total}）", id: "Muat lagi ({loaded}/{total})" },
  untitledCreation: { en: "Untitled creation", zh: "未命名创作", id: "Kreasi tanpa nama" },
  noPreview: { en: "No preview", zh: "暂无预览画面", id: "Tidak ada pratinjau" },
  noWorksYet: { en: "No works yet", zh: "暂无作品", id: "Belum ada karya" },
  composedWorksCount: { en: "Composed: {n}", zh: "已合成作品：{n}", id: "Tersintesis: {n}" },
  allRatios: { en: "All ratios", zh: "所有比例", id: "Semua rasio" },
  ratioPortrait: { en: "{ratio} (portrait)", zh: "{ratio}(竖屏)", id: "{ratio} (vertikal)" },
  ratioLandscape: { en: "{ratio} (landscape)", zh: "{ratio}(横屏)", id: "{ratio} (horizontal)" },
  videoParams: { en: "Video settings", zh: "视频参数", id: "Pengaturan video" },
  historyWorks: { en: "My works", zh: "历史作品", id: "Karya saya" },
  composeOutput: { en: "Compose video", zh: "合成成片", id: "Sintesis video" },
  loadingClips: { en: "Loading clips...", zh: "正在加载片段数据...", id: "Memuat klip..." },
  wizardStepScript: { en: "Script", zh: "选择脚本", id: "Pilih naskah" },
  wizardStepEdit: { en: "Edit", zh: "脚本编辑", id: "Edit naskah" },
  wizardStepClips: { en: "Storyboard", zh: "片段生成", id: "Buat klip" },
  wizardStepVideo: { en: "Compose", zh: "视频生成", id: "Buat video" },
  createVideoModel: { en: "Video model", zh: "视频模型", id: "Model video" },
  createExistingScript: { en: "Upload script", zh: "已有脚本", id: "Unggah naskah" },
  createModelColon: { en: "Model:", zh: "模型：", id: "Model:" },
  createResolutionColon: { en: "Resolution:", zh: "分辨率：", id: "Resolusi:" },
  createDurationSummary: {
    en: "Totaled from all clip durations. Adjust individual clips in Edit script or Generate clips.",
    zh: "由各片段时长汇总；调整单段请至脚本编辑或片段页",
    id: "Dijumlahkan dari durasi semua klip. Atur klip individual di Edit naskah atau Buat klip.",
  },
  createNoResolutionForModel: {
    en: "No resolutions are configured for the current model",
    zh: "当前模型未配置可用分辨率",
    id: "Tidak ada resolusi yang dikonfigurasi untuk model saat ini",
  },
  createCanvasUnaffectedByVideoParams: {
    en: "Storyboards are not affected by video settings",
    zh: "画板不受视频参数影响",
    id: "Storyboard tidak dipengaruhi pengaturan video",
  },
  createGeneratingWait: {
    en: "Writing your script from the brief. About {time}. Please wait.",
    zh: "根据您描述智能生成专属视频脚本，预计 {time}，请稍候~",
    id: "Membuat naskah dari deskripsimu, sekitar {time}. Mohon tunggu.",
  },
  createBannedHintShort: {
    en: "Restricted wording detected. Please edit.",
    zh: "检测到违禁词，请注意合规",
    id: "Terdeteksi kata terlarang. Silakan edit.",
  },
  createParseBusy: {
    en: "A script is still parsing. Generate script is unavailable until it finishes.",
    zh: "已有脚本正在解析中，完成前【智能生成脚本】功能不可用",
    id: "Naskah masih diurai. Buat naskah tidak tersedia sampai selesai.",
  },
  createNeedScriptEdit: {
    en: "Finish script editing and clip setup first",
    zh: "请先完成脚本编辑和片段整理",
    id: "Selesaikan edit naskah dan klip dulu",
  },
  createClipDurationInvalid: {
    en: "Clip {n} duration must be an integer from 4 to 15 seconds",
    zh: "片段{n}时长必须为 4-15 秒的整数",
    id: "Durasi klip {n} harus bilangan bulat 4-15 detik",
  },
  createFreezeFailed: {
    en: "Failed to freeze the script version. Try again later.",
    zh: "冻结脚本版本失败，请稍后重试",
    id: "Gagal mengunci versi naskah. Coba lagi nanti.",
  },
  createDeleteTitle: { en: "Delete this creation?", zh: "确认删除", id: "Hapus kreasi ini?" },
  createDeleteDesc: {
    en: 'Delete "{name}"? The creation process will be cleared. Composed videos stay in My Works. This cannot be undone.',
    zh: "确定要删除「{name}」吗？创作过程将被清除，已经合成的作品仍保留在“我的作品”中。此操作无法撤销。",
    id: 'Hapus "{name}"? Proses kreasi akan dihapus. Video yang sudah jadi tetap di Karya Saya. Tidak bisa dibatalkan.',
  },
  /** 创作入口页（/create）主磁贴：自主创作标题 */
  createEntryAiTitle: { en: "AI Creation", zh: "自主创作", id: "Kreasi Mandiri" },
  /** 创作入口页（/create）主磁贴：自主创作说明 */
  createEntryAiDesc: {
    en: "From idea to final video — AI co-pilots script, assets and editing",
    zh: "从创意到成片，AI 协同完成脚本、素材与剪辑",
    id: "Dari ide ke video — AI membantu naskah, aset, dan penyuntingan",
  },
  createGuestEyebrow: { en: "Create", zh: "自主创作", id: "Kreasi Mandiri" },
  createGuestTitle: {
    en: "Describe your product. AI writes the script, boards, and voiceover.",
    zh: "输入你的商品,让 AI 把脚本、分镜、口播/旁白一次给你",
    id: "Masukkan produkmu, AI akan buat naskah, papan, dan lisan.",
  },
  createGuestDesc: {
    en: "Add product details and images. AI plans the script, generates boards, and matches a digital-human voiceover.",
    zh: "只需输入商品详情与图片,AI 自动规划脚本结构、生成分镜画面、匹配数字人口播/旁白。从灵感到成片,快速出片",
    id: "Tambah detail dan gambar produk. AI merencanakan naskah, membuat papan, dan mencocokkan suara karakter.",
  },
  createGuestH1: {
    en: "One-click AI scripts in multiple styles",
    zh: "AI 脚本一键生成,支持多风格切换",
    id: "Naskah AI sekali klik, banyak gaya",
  },
  createGuestH2: {
    en: "Auto boards, regenerate per clip",
    zh: "分镜自动配图,可逐段重新生成",
    id: "Papan otomatis, bisa dibuat ulang per klip",
  },
  createGuestH3: { en: "Precise prompts, fewer rerolls", zh: "提示词精准控镜,降低抽卡成本", id: "Prompt presisi, lebih sedikit undian" },
  createGuestP1: { en: "AI script", zh: "AI 脚本生成", id: "Naskah AI" },
  createGuestP2: { en: "Auto boards", zh: "分镜自动配图", id: "Papan otomatis" },
  createGuestP3: { en: "Digital-human VO", zh: "数字人口播/旁白", id: "Lisan karakter" },
  createGuestP4: { en: "4K export", zh: "4K 成片导出", id: "Ekspor 4K" },
  noDownloadVideo: { en: "No composed video to download", zh: "暂无可下载的合成视频", id: "Tidak ada video untuk diunduh" },
  downloadFailed: { en: "Download failed", zh: "下载失败", id: "Unduhan gagal" },
  missingSession: { en: "Session ID is missing", zh: "会话ID不存在", id: "ID sesi tidak ada" },
  taskName: { en: "Task name", zh: "任务名称", id: "Nama tugas" },
  enterTaskName: { en: "Enter a task name", zh: "请输入任务名称", id: "Masukkan nama tugas" },
  taskNameRequired: { en: "Task name is required", zh: "任务名称不能为空", id: "Nama tugas wajib diisi" },
  taskNameTooLong: { en: "Task name cannot exceed 50 characters", zh: "任务名称不能超过 50 个字符", id: "Nama tugas maksimal 50 karakter" },
  renameOk: { en: "Renamed", zh: "重命名成功", id: "Nama diganti" },
  renameFailed: { en: "Rename failed. Try again later.", zh: "重命名失败，请稍后重试", id: "Gagal ganti nama. Coba lagi nanti." },

  profileInfo: { en: "Profile", zh: "个人信息", id: "Info profil" },
  profileInfoDesc: { en: "Avatar, account, and membership", zh: "头像、账号与会员信息", id: "Avatar, akun, dan keanggotaan" },
  profileOrdersDesc: { en: "Purchases, renewals, and top-ups", zh: "购买、续费与充值订单", id: "Pembelian, perpanjang, dan isi ulang" },
  profileBeans: { en: "Dream Beans records", zh: "梦想豆记录", id: "Catatan Kacang Impian" },
  profileBeansDesc: { en: "Balance and transaction history", zh: "梦想豆余额与收支明细", id: "Saldo dan riwayat Kacang Impian" },
  profileFeedback: { en: "Feedback", zh: "用户反馈", id: "Masukan" },
  profileFeedbackDesc: { en: "Report issues and suggestions", zh: "提交问题与建议", id: "Kirim masalah dan saran" },
  changeAvatar: { en: "Change photo", zh: "更换头像", id: "Ganti foto" },
  editNickname: { en: "Edit nickname", zh: "编辑昵称", id: "Edit nama" },
  userFallback: { en: "User", zh: "用户", id: "Pengguna" },
  remainingBeans: { en: "Dream Beans left", zh: "剩余梦想豆", id: "Sisa Kacang Impian" },
  memberExpire: { en: "Membership expires", zh: "会员到期时间", id: "Keanggotaan berakhir" },
  renew: { en: "Renew", zh: "续费", id: "Perpanjang" },
  userIdLine: { en: "ID: {id}", zh: "ID：{id}", id: "ID: {id}" },
  /** 会员等级名（后端 level_code / 中文 level_name） */
  levelGuest: { en: "Guest", zh: "游客", id: "Tamu" },
  levelTrial: { en: "Free trial", zh: "免费体验版", id: "Uji coba gratis" },
  levelPersonal: { en: "Personal", zh: "个人版", id: "Pribadi" },
  levelCreator: { en: "Creator", zh: "创作者版", id: "Kreator" },
  levelTeam: { en: "Team", zh: "团队版", id: "Tim" },
  levelFlagship: { en: "Flagship", zh: "旗舰版", id: "Unggulan" },
  /**
   * 会员体系档位（2026-10-13 新增 6/7/8）。
   *
   * <p>2026-10-16 原型改名为「入门版 / 专业版 / 专业直播版」——
   * 不再用「5 / 50 / 100 条」这类**按条数命名**的口径（条数只是权益里的一项，
   * 且三档都含短视频额度差异很大，容易被理解成「只能发 5 条」）。
   * 这几个 key 是全站档位显示名的唯一来源：Me 会员卡、管理端用户列表、
   * 选择会员页都走 `localizeMemberLevel` ⇒ 改一处即全站一致。</p>
   */
  levelMember5: { en: "Starter", zh: "入门版", id: "Pemula" },
  levelMember50: { en: "Pro", zh: "专业版", id: "Pro" },
  levelMember100: { en: "Pro Live", zh: "专业直播版", id: "Pro Live" },
  usernameLabel: { en: "Username", zh: "账号名", id: "Nama akun" },
  boundPhone: { en: "Phone", zh: "绑定手机", id: "Telepon" },
  boundEmail: { en: "Email", zh: "注册邮箱", id: "Email" },
  passwordLabel: { en: "Password", zh: "密码", id: "Kata sandi" },
  /** 子账户密码行：后端禁止子账户自助改密（must be reset by enterprise admin），入口改为引导联系管理员 */
  profileSubPwdContactAdmin: {
    en: "Ask your enterprise admin to reset the password",
    zh: "请联系企业管理员重置密码",
    id: "Minta admin perusahaan mengatur ulang kata sandi",
  },
  companyName: { en: "Company", zh: "公司全称", id: "Perusahaan" },
  unbound: { en: "Not bound", zh: "未绑定", id: "Belum terikat" },
  notSet: { en: "Not set", zh: "未设置", id: "Belum diatur" },
  alreadySet: { en: "Set", zh: "已设置", id: "Sudah diatur" },
  notFilled: { en: "Not filled", zh: "未填写", id: "Belum diisi" },
  setPassword: { en: "Set password", zh: "设置密码", id: "Atur kata sandi" },
  changePassword: { en: "Change password", zh: "修改密码", id: "Ubah kata sandi" },
  bindPhoneFirstHint: {
    en: "Bind a phone number first, then change the password.",
    zh: "请先在「绑定手机」中绑定手机号后再修改密码",
    id: "Ikat nomor telepon dulu, lalu ubah kata sandi.",
  },
  currentPassword: { en: "Current password", zh: "当前密码", id: "Kata sandi saat ini" },
  newPassword: { en: "New password", zh: "新密码", id: "Kata sandi baru" },
  confirmNewPassword: { en: "Confirm new password", zh: "确认新密码", id: "Konfirmasi kata sandi baru" },
  enterCurrentPassword: { en: "Enter current password", zh: "请输入当前密码", id: "Masukkan kata sandi saat ini" },
  enterNewPasswordMin: {
    en: "Enter a new password (at least 6 characters)",
    zh: "请输入新密码（至少6位）",
    id: "Masukkan kata sandi baru (min. 6)",
  },
  reenterNewPassword: { en: "Enter the new password again", zh: "请再次输入新密码", id: "Masukkan lagi kata sandi baru" },
  confirmChange: { en: "Confirm change", zh: "确认修改", id: "Konfirmasi ubah" },
  confirmSet: { en: "Confirm", zh: "确认设置", id: "Konfirmasi" },
  changeBoundPhone: { en: "Change phone", zh: "修改绑定手机", id: "Ubah telepon" },
  bindPhone: { en: "Bind phone", zh: "绑定手机", id: "Ikat telepon" },
  oldPhoneVerified: { en: "Current phone verified", zh: "✓ 当前手机已验证", id: "Telepon saat ini terverifikasi" },
  verifyCurrentPhone: { en: "Step 1: verify current phone", zh: "第一步：验证当前手机", id: "Langkah 1: verifikasi telepon saat ini" },
  currentPhone: { en: "Current phone", zh: "当前手机号", id: "Telepon saat ini" },
  enterOldPhoneCode: { en: "Enter the current phone code", zh: "请输入旧手机验证码", id: "Masukkan kode telepon lama" },
  verify: { en: "Verify", zh: "验证", id: "Verifikasi" },
  bindNewPhone: { en: "Step 2: bind a new phone", zh: "第二步：绑定新手机", id: "Langkah 2: ikat telepon baru" },
  verifyNewPhone: { en: "Verify the new phone", zh: "验证新手机号", id: "Verifikasi telepon baru" },
  newPhone: { en: "New phone", zh: "新手机号", id: "Telepon baru" },
  enterNewPhoneCode: { en: "Enter the new phone code", zh: "请输入新手机验证码", id: "Masukkan kode telepon baru" },
  phoneSameAsOld: {
    en: "The new number cannot be the same as the old one",
    zh: "新手机号不能与旧手机号相同",
    id: "Nomor baru tidak boleh sama dengan yang lama",
  },
  confirmRebind: { en: "Confirm change", zh: "确认换绑", id: "Konfirmasi ganti" },
  confirmBind: { en: "Confirm bind", zh: "确认绑定", id: "Konfirmasi ikat" },
  signOut: { en: "Log out", zh: "退出登录", id: "Keluar" },
  orderNo: { en: "Order ID", zh: "订单编号", id: "ID pesanan" },
  content: { en: "Details", zh: "内容", id: "Konten" },
  amount: { en: "Amount", zh: "金额", id: "Jumlah" },
  time: { en: "Time", zh: "时间", id: "Waktu" },
  currentBeanBalance: { en: "Current Dream Beans Balance", zh: "当前梦想豆余额", id: "Saldo Kacang Impian saat ini" },
  beansOfSource: { en: "{name} Dream Beans", zh: "{name}梦想豆", id: "Kacang Impian {name}" },
  noBeanBalance: { en: "No Dream Beans yet", zh: "暂无梦想豆余额", id: "Belum ada Kacang Impian" },
  beanLedger: { en: "Dream Beans Transactions", zh: "梦想豆收支明细", id: "Riwayat Kacang Impian" },
  pickMonth: { en: "Select month", zh: "选择年月", id: "Pilih bulan" },
  monthGain: { en: "Gained this month", zh: "本月获取", id: "Diperoleh bulan ini" },
  monthCost: { en: "Spent this month", zh: "本月消耗", id: "Dipakai bulan ini" },
  monthExpired: { en: "Expired this month", zh: "本月过期", id: "Kedaluwarsa bulan ini" },
  monthGainOf: { en: "Gained in {month}", zh: "{month}获取", id: "Diperoleh {month}" },
  monthCostOf: { en: "Spent in {month}", zh: "{month}消耗", id: "Dipakai {month}" },
  monthExpiredOf: { en: "Expired in {month}", zh: "{month}过期", id: "Kedaluwarsa {month}" },
  netChange: { en: "Net change", zh: "净变化", id: "Perubahan bersih" },
  totalItems: { en: "{n} items", zh: "共 {n} 条", id: "{n} item" },
  perPage: { en: "Per page", zh: "每页", id: "Per halaman" },
  itemsUnit: { en: "{n} / page", zh: "{n} 条", id: "{n} / hlm" },
  jumpTo: { en: "Go to", zh: "跳至", id: "Ke" },
  pageUnit: { en: "page", zh: "页", id: "hlm" },
  firstPage: { en: "First page", zh: "首页", id: "Halaman pertama" },
  prevPage: { en: "Previous page", zh: "上一页", id: "Halaman sebelumnya" },
  nextPage: { en: "Next page", zh: "下一页", id: "Halaman berikutnya" },
  lastPage: { en: "Last page", zh: "末页", id: "Halaman terakhir" },
  submitFeedback: { en: "Submit feedback", zh: "提交反馈", id: "Kirim masukan" },
  myFeedback: { en: "My feedback", zh: "我的反馈", id: "Masukan saya" },
  myFeedbackRecords: { en: "My tickets", zh: "我的反馈记录", id: "Tiket saya" },
  myFeedbackHint: { en: "All tickets you have submitted", zh: "查看您提交的所有反馈工单", id: "Semua tiket yang Anda kirim" },
  emptyFeedback: { en: "No feedback yet", zh: "暂无反馈记录", id: "Belum ada masukan" },
  officialReply: { en: "Official reply:", zh: "官方回复：", id: "Balasan resmi:" },
  newFeedbackTicket: { en: "New ticket", zh: "新建反馈工单", id: "Tiket baru" },
  newFeedbackHint: {
    en: "Please describe the issue in as much detail as you can.",
    zh: "请尽可能详细地描述，以便我们更好地为您服务。",
    id: "Jelaskan sedetail mungkin agar kami bisa membantu.",
  },
  feedbackType: { en: "Type", zh: "反馈类型", id: "Jenis" },
  feedbackDetail: { en: "Details", zh: "详细描述", id: "Detail" },
  contactOptional: { en: "Contact", zh: "联系方式", id: "Kontak" },
  optional: { en: "(optional)", zh: "(选填)", id: "(opsional)" },
  contactPlaceholder: { en: "Phone / WeChat / email", zh: "手机号 / 微信 / 邮箱 (三选一即可)", id: "Telepon / WeChat / email" },
  submitTicket: { en: "Submit ticket", zh: "提交工单", id: "Kirim tiket" },
  feedbackBug: { en: "Bug", zh: "Bug反馈", id: "Bug" },
  feedbackBugDesc: { en: "Errors, crashes, or lag", zh: "功能异常、报错、卡顿", id: "Error, crash, atau lag" },
  feedbackIdea: { en: "Suggestion", zh: "功能建议", id: "Saran" },
  feedbackIdeaDesc: {
    en: "Features you want added or improved",
    zh: "希望新增或改进的功能",
    id: "Fitur yang ingin ditambah atau diperbaiki",
  },
  feedbackQuestion: { en: "Question", zh: "使用疑问", id: "Pertanyaan" },
  feedbackQuestionDesc: { en: "Not sure how to use something", zh: "不知道怎么操作", id: "Tidak tahu cara pakai" },
  feedbackPhBug: {
    en: "Please include:\n1. What went wrong\n2. What you were doing\n3. What you expected",
    zh: "请按以下格式描述：\n1. 出现了什么问题\n2. 在什么操作下触发\n3. 期望的正常结果是什么",
    id: "Sertakan:\n1. Apa yang salah\n2. Apa yang sedang dilakukan\n3. Hasil yang diharapkan",
  },
  feedbackPhIdea: {
    en: "Please include:\n1. How it works now\n2. What you want instead\n3. Why it matters",
    zh: "请按以下格式描述：\n1. 现在是什么情况\n2. 你希望变成什么样\n3. 为什么需要这个功能/改进",
    id: "Sertakan:\n1. Kondisi sekarang\n2. Yang Anda inginkan\n3. Mengapa ini penting",
  },
  feedbackPhQuestion: {
    en: "Please include:\n1. What you want to do\n2. Where you got stuck",
    zh: "请按以下格式描述：\n1. 你想实现什么效果\n2. 卡在哪一步了",
    id: "Sertakan:\n1. Apa yang ingin dilakukan\n2. Di mana Anda tertahan",
  },
  fbStatusPending: { en: "Pending", zh: "待处理", id: "Menunggu" },
  fbStatusProcessing: { en: "Processing", zh: "处理中", id: "Diproses" },
  fbStatusResolved: { en: "Resolved", zh: "已解决", id: "Selesai" },
  fbStatusClosed: { en: "Closed", zh: "已关闭", id: "Ditutup" },
  statusUnknown: { en: "Unknown", zh: "未知", id: "Tidak diketahui" },
  statusFailed: { en: "Failed", zh: "失败", id: "Gagal" },
  statusRefunded: { en: "Refunded", zh: "已退款", id: "Dikembalikan" },
  statusRefundFailed: { en: "Refund failed", zh: "退款失败", id: "Pengembalian gagal" },
  beanValid: { en: "Active", zh: "有效", id: "Aktif" },
  beanUsedUp: { en: "Used up", zh: "已用完", id: "Habis" },
  beanExpired: { en: "Expired", zh: "已过期", id: "Kedaluwarsa" },
  recordSpend: { en: "Spend", zh: "消费", id: "Pakai" },
  recordGift: { en: "Gift", zh: "赠送", id: "Hadiah" },
  recordRefund: { en: "Refund", zh: "退回", id: "Kembali" },
  recordExpire: { en: "Expired", zh: "过期", id: "Kedaluwarsa" },
  emptyRequired: { en: "This field cannot be empty", zh: "内容不能为空", id: "Tidak boleh kosong" },
  updateOk: { en: "Updated", zh: "修改成功", id: "Berhasil diubah" },
  checkPhone: { en: "Please check the phone number", zh: "请检查手机号是否正确", id: "Periksa nomor telepon" },
  bindPhoneFirst: { en: "Bind a phone number first", zh: "请先绑定手机号", id: "Ikat nomor telepon dulu" },
  passwordMin6: { en: "New password must be at least 6 characters", zh: "新密码至少6位", id: "Kata sandi baru min. 6 karakter" },
  passwordMismatch: { en: "Passwords do not match", zh: "两次密码不一致", id: "Kata sandi tidak sama" },
  passwordSetRelogin: {
    en: "Password set. Please log in again.",
    zh: "密码设置成功，请重新登录",
    id: "Kata sandi diatur. Silakan masuk lagi.",
  },
  oldPhoneVerifiedOk: { en: "Current phone verified", zh: "旧手机验证通过", id: "Telepon lama terverifikasi" },
  invalidPhoneFormat: { en: "Invalid phone number", zh: "手机号格式不正确", id: "Nomor telepon tidak valid" },
  enterNewPhone: { en: "Enter a new phone number", zh: "请输入新手机号", id: "Masukkan nomor telepon baru" },
  phoneReboundOk: { en: "Phone updated", zh: "手机换绑成功", id: "Telepon diperbarui" },
  enterFeedbackDetail: { en: "Please describe the issue", zh: "请填写详细描述", id: "Isi deskripsi dulu" },
  feedbackSubmitted: { en: "Ticket submitted. Thank you!", zh: "工单已提交，感谢您的反馈！", id: "Tiket terkirim. Terima kasih!" },
  avatarUpdated: { en: "Avatar updated", zh: "头像已更新", id: "Avatar diperbarui" },
  enterUsername: { en: "Enter a username", zh: "请输入账号名", id: "Masukkan nama akun" },
  enterNickname: { en: "Enter a nickname", zh: "请输入昵称", id: "Masukkan nama panggilan" },
  enterCompany: { en: "Enter the company name", zh: "请输入公司全称", id: "Masukkan nama perusahaan" },
  editField: { en: "Edit {name}", zh: "修改{name}", id: "Ubah {name}" },
  editNicknameTitle: { en: "Edit nickname", zh: "修改昵称", id: "Ubah nama panggilan" },
  beanContains: { en: "Includes {items}", zh: "含 {items}", id: "Termasuk {items}" },
  /** 分池卡静态注释（对齐原型：会员赠送 / 充值 / 返佣 三池各一行小字）
   *  ⚠️ 英文务必短：卡片是左右三栏，「Follows membership · expires with it」那种长句
   *  会把右侧挤到数字换行（2026-10-23 亲测），故用「Expires with membership」等短句。 */
  beanHintMember: { en: "Expires with membership", zh: "随会员期 · 到期即清", id: "Hangus saat member berakhir" },
  beanHintRecharge: { en: "Valid for 365 days", zh: "365 天有效", id: "Berlaku 365 hari" },
  beanHintCommission: { en: "Never expires", zh: "长期有效", id: "Tidak kedaluwarsa" },
  beanExpireToday: {
    en: "<1 day: {n} Dream Beans will expire{time}",
    zh: "<1天有{n}梦想豆过期{time}",
    id: "<1 hari: {n} Kacang Impian kedaluwarsa{time}",
  },
  beanExpireInDays: {
    en: "{days} days: {n} Dream Beans will expire{time}",
    zh: "{days}天后有{n}梦想豆过期{time}",
    id: "{days} hari: {n} Kacang Impian kedaluwarsa{time}",
  },
  /** 过期时刻，拼在过期提示后面 */
  beanExpireClock: { en: " ({time})", zh: "（{time}）", id: " ({time})" },
  sendCodeCountdown: { en: "{n}s", zh: "{n}s", id: "{n}d" },
  downloadVideo: { en: "Download video", zh: "下载视频", id: "Unduh video" },
  createStartNew: { en: "New creation", zh: "开始新创作", id: "Kreasi baru" },
  statusComposed: { en: "Composed", zh: "已合成作品", id: "Sudah tersintesis" },
  deleteOk: { en: "Deleted", zh: "删除成功", id: "Berhasil dihapus" },
  deleteFailed: { en: "Delete failed. Try again later.", zh: "删除失败，请稍后重试", id: "Gagal menghapus. Coba lagi nanti." },
  saveOk: { en: "Saved", zh: "保存成功", id: "Tersimpan" },
  gotIt: { en: "Got it", zh: "知道了", id: "Mengerti" },
  serviceUnavailable: { en: "Service temporarily unavailable", zh: "服务暂时不可用", id: "Layanan sementara tidak tersedia" },
  loadingModels: { en: "Loading models…", zh: "加载模型中…", id: "Memuat model…" },
  noAvailableModel: { en: "No models available", zh: "暂无可用模型", id: "Tidak ada model" },
  pleaseSelectModel: { en: "Select a model first", zh: "请先选择模型", id: "Pilih model dulu" },
  resolutionUnsupported: {
    en: "This model does not support the selected resolution",
    zh: "当前模型不支持所选分辨率",
    id: "Model ini tidak mendukung resolusi yang dipilih",
  },
  modelUnavailable: {
    en: '"{name}" is no longer available. Please choose another model.',
    zh: "当前模型「{name}」已不可用，请重新选择",
    id: '"{name}" tidak tersedia. Pilih model lain.',
  },
  unnamedDh: { en: "Unnamed character", zh: "未命名数字人", id: "Karakter tanpa nama" },
  enterProductName: { en: "Enter a product name", zh: "请先输入产品名称", id: "Masukkan nama produk" },
  enterAudience: { en: "Enter a target audience", zh: "请先输入目标人群", id: "Masukkan target audiens" },
  enterProductBrief: { en: "Describe your product idea", zh: "请先输入产品想法描述", id: "Jelaskan ide produk" },
  generateScriptFailed: {
    en: "Script generation failed. Please try again.",
    zh: "生成脚本失败，请重试",
    id: "Pembuatan naskah gagal. Coba lagi.",
  },
  restoreScriptFailed: {
    en: "Could not restore the generated script. Please try again.",
    zh: "恢复脚本生成结果失败，请重试",
    id: "Gagal memulihkan naskah. Coba lagi.",
  },
  scriptMaxLength: { en: "Script can be at most {n} characters", zh: "生成脚本最多 {n} 字", id: "Naskah maks. {n} karakter" },
  productNameExample: { en: "e.g. portable neck massager", zh: "例如：便携式颈椎按摩仪", id: "Mis. pijat leher portabel" },
  audienceExample: { en: "e.g. office workers at a desk", zh: "例如：长期伏案的上班族", id: "Mis. pekerja kantor" },
  minutesRange: { en: "{n} min", zh: "{n} 分钟", id: "{n} menit" },
  ledgerDetail: { en: "Details", zh: "明细", id: "Rincian" },
  txnType: { en: "Type", zh: "交易类型", id: "Jenis" },
  colBizType: { en: "Business", zh: "业务类型", id: "Jenis bisnis" },
  colBizNo: { en: "Reference", zh: "业务编号", id: "Referensi" },
  colOperator: { en: "Member", zh: "操作成员", id: "Anggota" },
  colSpent: { en: "Spent", zh: "消耗", id: "Terpakai" },
  colBalanceAfter: { en: "Balance after", zh: "变动后余额", id: "Saldo setelah" },
  teamLedgerTitle: { en: "Team spend and top-ups", zh: "团队消费 / 充值记录", id: "Pemakaian dan isi ulang tim" },
  teamLedgerHint: {
    en: "Dream Beans activity for the main account and every member",
    zh: "主账号与全部子账号的梦想豆流水",
    id: "Aktivitas Kacang Impian akun utama dan semua anggota",
  },
  allMembers: { en: "All members", zh: "全部成员", id: "Semua anggota" },
  consumeRecords: { en: "Spend records", zh: "消费记录", id: "Catatan pemakaian" },
  rechargeRecords: { en: "Top-up records", zh: "充值记录", id: "Catatan isi ulang" },
  emptyConsume: { en: "No spend records", zh: "暂无消费记录", id: "Belum ada catatan pemakaian" },
  emptyRechargeOrders: { en: "No top-up orders", zh: "暂无充值订单", id: "Belum ada pesanan isi ulang" },
  colType: { en: "Type", zh: "类型", id: "Jenis" },
  rechargeMember: { en: "Member", zh: "充值成员", id: "Anggota" },
  gained: { en: "Received", zh: "获得", id: "Diterima" },
  rechargeSuccess: { en: "Top-up successful", zh: "充值成功", id: "Isi ulang berhasil" },
  roleAdmin: { en: "Admin", zh: "管理员", id: "Admin" },
  roleMember: { en: "Member", zh: "成员", id: "Anggota" },
  memberOption: { en: "{name} ({role})", zh: "{name}（{role}）", id: "{name} ({role})" },
  loadMembers: { en: "Loading members...", zh: "加载成员列表...", id: "Memuat anggota..." },
  loadMembersFailed: { en: "Could not load members", zh: "加载成员列表失败", id: "Gagal memuat anggota" },
  loadConsumeFailed: { en: "Could not load spend records", zh: "加载消费记录失败", id: "Gagal memuat catatan pemakaian" },
  loadRechargeFailed: { en: "Could not load top-up records", zh: "加载充值记录失败", id: "Gagal memuat catatan isi ulang" },
  paySuccess: { en: "Paid", zh: "支付成功", id: "Berhasil dibayar" },
  payPending: { en: "Awaiting payment", zh: "待支付", id: "Menunggu pembayaran" },
  payFailed: { en: "Payment failed", zh: "支付失败", id: "Pembayaran gagal" },
  beanSourceCol: { en: "Dream Beans source", zh: "梦想豆来源", id: "Sumber Kacang Impian" },
  changeAmount: { en: "Change", zh: "变化", id: "Perubahan" },
  viewMoreItems: { en: "View {n} more", zh: "查看更多 {n} 条", id: "Lihat {n} lagi" },
  historicalBeans: { en: "Legacy Dream Beans", zh: "历史梦想豆", id: "Kacang Impian lama" },
  recordMemberGift: { en: "Membership gift", zh: "会员赠送", id: "Hadiah member" },
  recordRefundFee: { en: "Refund", zh: "退费", id: "Pengembalian" },
  beanSourceMember: { en: "Membership Dream Beans", zh: "会员梦想豆", id: "Kacang Impian member" },
  // 分池来源名：与余额卡分池（poolRecharge = "Recharge"）合成 "Recharge Dream Beans" 同一说法，
  // 原先 en 写 "Top-up Dream Beans"，与充值按钮/Type 列统一后的 "Recharge now" 自相矛盾（2026-10-20）
  beanSourceRecharge: { en: "Recharge Dream Beans", zh: "充值梦想豆", id: "Kacang Impian isi ulang" },
  consumeOk: { en: "Success", zh: "成功", id: "Berhasil" },
  consumeProcessing: { en: "Processing", zh: "处理中", id: "Diproses" },
  featurePreview: { en: "Preview", zh: "功能预览", id: "Pratinjau" },
  loginToUnlock: { en: "Log in to unlock {name}", zh: "登录后开启 {name}", id: "Masuk untuk membuka {name}" },
  freeBeansHint: {
    en: "Sign up free and get 500 Dream Beans",
    zh: "免费注册即送 500 梦想豆,即刻体验",
    id: "Daftar gratis, dapat 500 Kacang Impian",
  },
  newUserBeans: { en: "New users get 500 Dream Beans", zh: "新用户注册即享 500 梦想豆", id: "Pengguna baru dapat 500 Kacang Impian" },
  createParsing: { en: "Parsing...", zh: "解析中...", id: "Mengurai..." },
  createFileReadyParse: { en: "Selected. Tap Parse to continue.", zh: "已选择，点击开始解析", id: "Dipilih. Ketuk Urai untuk lanjut." },
  createNeedReselectFile: {
    en: 'The previous file "{name}" must be selected again before Parse.',
    zh: '之前上传的文件 "{name}" 需要重新选择后再点击「开始解析」',
    id: 'File sebelumnya "{name}" harus dipilih lagi sebelum Urai.',
  },
  createReupload: { en: "Re-upload", zh: "重新上传", id: "Unggah ulang" },
  createClearRecord: { en: "Clear record", zh: "清除记录", id: "Hapus catatan" },
  createSelectFile: { en: "Choose file", zh: "选择文件", id: "Pilih file" },
  createGenerateBusyParse: {
    en: "Script generation is still running. Parse is unavailable until it finishes.",
    zh: "「智能分析」正在进行中，完成前「开始解析」不可用",
    id: "Pembuatan naskah masih berjalan. Urai tidak tersedia sampai selesai.",
  },
  pasteScriptMaxLength: {
    en: "Pasted script can be at most {n} characters",
    zh: "粘贴脚本最多 {n} 字",
    id: "Naskah tempel maks. {n} karakter",
  },
  scriptFileMaxMb: { en: "Script file cannot exceed {n}MB", zh: "脚本文档大小不能超过 {n}MB", id: "File naskah maks. {n}MB" },
  readFileFailed: {
    en: "Could not read the file. Please choose it again.",
    zh: "读取文件失败，请重新选择",
    id: "Gagal baca file. Pilih lagi.",
  },
  scriptFormatReq: { en: "Format:", zh: "脚本格式要求：", id: "Format:" },
  scriptFormatHint: {
    en: "Each clip 4–15s · board numbers stay sequential · clips and boards on separate lines",
    zh: "单个片段时长 4-15 秒 · 分镜序号全局连续 · 片段、分镜独立分行书写",
    id: "Tiap klip 4–15 dtk · nomor papan berurutan · klip dan papan di baris terpisah",
  },
  scriptFormatWarn: {
    en: "Wrong format can split clips incorrectly and drop scenes.",
    zh: "格式错误会导致片段拆分失败，导致片段丢失",
    id: "Format salah bisa memecah klip gagal dan menghilangkan adegan.",
  },
  scriptFormatExample: { en: "Format example", zh: "脚本格式示例", id: "Contoh format" },
  copyFormatPrompt: { en: "Copy format instructions", zh: "一键复制脚本格式要求", id: "Salin panduan format" },
  scriptFormatCopyHint: {
    en: "Copy, generate a matching script with an LLM, then paste it below.",
    zh: "复制后到大模型生成符合格式的脚本，再粘贴回下方",
    id: "Salin, buat naskah sesuai format dengan LLM, lalu tempel di bawah.",
  },
  scriptTextLabel: { en: "Script text", zh: "脚本文本", id: "Teks naskah" },
  createPasteHere: {
    en: "Paste the generated script here...",
    zh: "将生成好的脚本粘贴到这里...",
    id: "Tempel naskah yang sudah dibuat di sini...",
  },
  scriptExampleClip: { en: "Clip 1: Clip title (0s - 6s)", zh: "片段 1：片段标题（0s - 6s）", id: "Klip 1: Judul klip (0d - 6d)" },
  scriptExampleOverview: {
    en: "Overview: {scene, characters, and content summary}.",
    zh: "整体描述：{该片段的场景、人物和内容概述}。",
    id: "Ringkasan: {ringkasan adegan, karakter, dan konten}.",
  },
  scriptExampleShot: { en: "Shot 1 (0s - 2s)", zh: "分镜 1（0s - 2s）", id: "Shot 1 (0d - 2d)" },
  scriptExampleVisual: {
    en: "Visual: {specific shot description}. {Character} says {voiceover}.",
    zh: "画面描述：{该分镜画面的具体描述}。{人物}说 {口播内容}。",
    id: "Visual: {deskripsi shot}. {Karakter} berkata {voiceover}.",
  },
  scriptPromptIntro: {
    en: "Create a short-video script following these format rules:",
    zh: "请根据以下格式要求生成短视频脚本：",
    id: "Buat naskah video pendek mengikuti aturan format berikut:",
  },
  scriptPromptRules: { en: "Format rules", zh: "格式要求", id: "Aturan format" },
  scriptPromptDuration: { en: "Each clip is 4–15 seconds", zh: "单个片段时长：4–15 秒", id: "Setiap klip berdurasi 4–15 detik" },
  scriptPromptSequential: { en: "Shot numbers are globally sequential", zh: "分镜序号全局连续", id: "Nomor shot berurutan secara global" },
  scriptPromptSeparateLines: {
    en: "Write each clip and shot on its own line",
    zh: "片段、分镜独立分行书写",
    id: "Tulis setiap klip dan shot pada baris terpisah",
  },
  scriptPromptFormatWarning: {
    en: "Incorrect formatting may split clips incorrectly and cause scenes to be lost.",
    zh: "格式错误会导致片段拆分失败，导致片段丢失",
    id: "Format yang salah dapat memecah klip dan menyebabkan adegan hilang.",
  },
  scriptPromptExampleTitle: { en: "Format example", zh: "脚本格式示例", id: "Contoh format" },
  scriptPromptStrict: {
    en: "Follow the format above exactly.",
    zh: "请严格按上述格式生成脚本。",
    id: "Ikuti format di atas secara tepat.",
  },
  scriptSkillLabel: { en: "Script Skill", zh: "脚本 Skill", id: "Skill naskah" },
  canvasSkillLabel: { en: "Storyboard Skill", zh: "画板 Skill", id: "Skill storyboard" },
  videoSkillLabel: { en: "Clip video Skill", zh: "片段视频 Skill", id: "Skill video klip" },
  skillLoading: { en: "Loading", zh: "加载中", id: "Memuat" },
  skillUnavailable: { en: "Skill unavailable", zh: "Skill 不可用", id: "Skill tidak tersedia" },
  skillUsingSystemPrompt: {
    en: "Using the system prompt for this task",
    zh: "本任务使用系统提示词",
    id: "Tugas ini menggunakan prompt sistem",
  },
  skillReplace: { en: "Replace", zh: "替换", id: "Ganti" },
  skillUploadMd: { en: "Upload MD", zh: "上传 MD", id: "Unggah MD" },
  skillDisableAria: { en: "Do not use {label} for this task", zh: "本任务不使用{label}", id: "Jangan gunakan {label} untuk tugas ini" },
  skillDisableTitle: {
    en: "Do not use this Skill for this task",
    zh: "本任务不使用该 Skill",
    id: "Jangan gunakan Skill ini untuk tugas ini",
  },
  skillFeatureUnavailable: { en: "This feature is not available yet", zh: "该功能暂未开放", id: "Fitur ini belum tersedia" },
  skillTaskNotInitialized: {
    en: "The creation task is not initialized",
    zh: "自主创作任务尚未初始化",
    id: "Tugas kreasi belum diinisialisasi",
  },
  skillMarkdownOnly: { en: "Only Markdown files are supported", zh: "仅支持 Markdown 文件", id: "Hanya file Markdown yang didukung" },
  skillMaxSize: { en: "Skill file cannot exceed {n}KB", zh: "Skill 文件不能超过 {n}KB", id: "File Skill tidak boleh lebih dari {n}KB" },
  skillSelected: { en: "{label} selected for this task", zh: "已为当前任务选择{label}", id: "{label} dipilih untuk tugas ini" },
  skillUploadFailed: { en: "Could not upload {label}", zh: "{label}上传失败", id: "Gagal mengunggah {label}" },
  skillSystemPromptRestored: {
    en: "This task now uses the system prompt",
    zh: "当前任务已恢复系统提示词",
    id: "Tugas ini kembali menggunakan prompt sistem",
  },
  skillClearFailed: { en: "Could not clear {label}", zh: "{label}清除失败", id: "Gagal menghapus {label}" },
  copyFailedManual: { en: "Copy failed. Please copy it manually.", zh: "复制失败，请手动复制", id: "Gagal menyalin. Salin manual." },
  okAction: { en: "OK", zh: "确定", id: "OK" },
  wordTemplate: { en: "Word template", zh: "Word 模板", id: "Template Word" },
  excelTemplate: { en: "Excel template", zh: "Excel 模板", id: "Template Excel" },
  createSearchVoice: { en: "Search voice name, tags…", zh: "搜索音色名称、标签…", id: "Cari nama suara, tag…" },
  voiceFemale: { en: "Female", zh: "女声", id: "Wanita" },
  voiceMale: { en: "Male", zh: "男声", id: "Pria" },
  voiceAllAge: { en: "All ages", zh: "全部年龄", id: "Semua usia" },
  voiceAllScene: { en: "All scenes", zh: "全部场景", id: "Semua scene" },
  voiceAllRole: { en: "All roles", zh: "全部角色", id: "Semua peran" },
  voiceAllDialect: { en: "All dialects", zh: "全部方言", id: "Semua dialek" },
  publicLibrary: { en: "Public", zh: "公共库", id: "Publik" },
  noVoiceTags: { en: "No tags", zh: "未设置标签", id: "Tidak ada tag" },
  noMatchingVoice: {
    en: "No matching voices. Adjust the filters.",
    zh: "无匹配音色，请调整筛选条件",
    id: "Tidak ada suara cocok. Ubah filter.",
  },
  requestTimeoutRetry: {
    en: "Request timed out. Please try again later.",
    zh: "请求超时，请稍后重试",
    id: "Waktu habis. Coba lagi nanti.",
  },
  networkRetry: {
    en: "Network error. Check your connection and retry.",
    zh: "网络连接失败，请检查网络后重试",
    id: "Jaringan gagal. Periksa koneksi lalu coba lagi.",
  },
  parseScriptFailed: {
    en: "Could not parse the script. Check the file format or content.",
    zh: "脚本解析失败，请检查文件格式或内容",
    id: "Gagal mengurai naskah. Periksa format atau isi file.",
  },
  parseNoContent: { en: "No script content was parsed", zh: "未解析到脚本内容", id: "Tidak ada isi naskah yang diurai" },
  parseSaveFailed: {
    en: "Could not save the parse result. Please retry.",
    zh: "脚本解析结果保存失败，请重试",
    id: "Gagal simpan hasil urai. Coba lagi.",
  },
  parseTimeoutRetry: {
    en: "Script parsing timed out. Please try again.",
    zh: "脚本解析超时，请重新尝试",
    id: "Penguraian naskah habis waktu. Coba lagi.",
  },

  // ===== /me 页面文案（2026-09-22 my-menu 迁移） =====
  meStatFollow: { en: "Following", zh: "关注", id: "Mengikuti" },
  meStatFans: { en: "Followers", zh: "粉丝", id: "Pengikut" },
  meStatLikes: { en: "Likes", zh: "获赞", id: "Suka" },
  meEditNickname: { en: "Edit nickname", zh: "编辑昵称", id: "Edit nama panggilan" },
  meCopyId: { en: "Copy ID", zh: "复制 ID", id: "Salin ID" },
  meCopied: { en: "Copied", zh: "已复制", id: "Tersalin" },
  meBeanLabel: { en: "Dream Beans Balance", zh: "梦想豆余额", id: "Saldo Kacang Impian" },
  meChargeNow: { en: "Recharge now", zh: "立即充值", id: "Isi ulang sekarang" },
  meEmptyWorks: { en: "No works yet", zh: "暂无作品", id: "Belum ada karya" },
  meDraftBadge: { en: "Draft", zh: "草稿", id: "Draf" },
  meEmptyFav: { en: "No favorites yet", zh: "暂无收藏", id: "Belum ada favorit" },
  meEmptyDramaFav: { en: "No saved dramas yet", zh: "暂无短剧收藏", id: "Belum ada drama tersimpan" },
  mePublishedBadge: { en: "Published", zh: "已发布", id: "Dipublikasikan" },

  // ===== 定价充值页（2026-09-28） =====
  buyNow: { en: "Buy now", zh: "立即购买", id: "Beli sekarang" },
  confirmOrder: { en: "Confirm order", zh: "确认订单", id: "Konfirmasi pesanan" },
  orderDetails: { en: "Order details", zh: "订单详情", id: "Detail pesanan" },
  beansCredited: { en: "Dream Beans credited", zh: "到账梦想豆", id: "Kacang Impian diterima" },
  originalPrice: { en: "List price", zh: "原价", id: "Harga asli" },
  discountLabel: { en: "Discount", zh: "优惠", id: "Diskon" },
  discountValue: {
    en: "-{percent}% (−{amount})",
    zh: "-{percent}%（-{amount}）",
    id: "-{percent}% (−{amount})",
  },
  memberLevelLabel: { en: "Membership", zh: "会员等级", id: "Level keanggotaan" },
  purchasePlan: { en: "Plan", zh: "购买方案", id: "Paket" },
  orderTypeBuy: { en: "Purchase", zh: "购买", id: "Pembelian" },
  orderTypeUpgrade: { en: "Upgrade", zh: "升级", id: "Upgrade" },
  payByTransfer: {
    en: "Message support in-app and pay by transfer.",
    zh: "私信联系客服，通过线下转账完成支付",
    id: "Hubungi dukungan lewat pesan, lalu bayar dengan transfer.",
  },
  payByEmail: {
    en: "Email support and pay by transfer.",
    zh: "邮件联系客服，通过线下转账完成支付",
    id: "Hubungi dukungan lewat email, lalu bayar dengan transfer.",
  },
  contactSupport: { en: "Contact support", zh: "联系客服", id: "Hubungi dukungan" },
  contactSupportHint: {
    en: "Have a question about this order? Message support.",
    zh: "对订单有疑问？可直接私信客服",
    id: "Ada pertanyaan tentang pesanan ini? Hubungi dukungan.",
  },
  contactSupportFailed: {
    en: "Support is unavailable right now, please try again later.",
    zh: "客服暂时无法接通，请稍后再试",
    id: "Dukungan sedang tidak tersedia, coba lagi nanti.",
  },
  supportEmail: { en: "Support email", zh: "客服邮箱", id: "Email dukungan" },
  supportEmailHint: {
    en: "Questions about this order? Email support.",
    zh: "对订单有疑问？请发邮件联系客服",
    id: "Ada pertanyaan tentang pesanan ini? Kirim email ke dukungan.",
  },
  supportEmailMissing: {
    en: "Support email is not configured.",
    zh: "暂未配置客服邮箱",
    id: "Email dukungan belum dikonfigurasi.",
  },
  transferRemark: {
    en: "Transfer note: put the order ID in the remark.",
    zh: "转账说明：转账备注请填写订单编号",
    id: "Catatan transfer: cantumkan ID pesanan di keterangan.",
  },
  supportHoursLine: { en: "Support hours: {hours}", zh: "客服在线时间：{hours}", id: "Jam dukungan: {hours}" },
  supportHoursFallback: { en: "Weekdays 9:00–18:00", zh: "工作日 9:00-18:00", id: "Hari kerja 09.00–18.00" },
  supportConfirmLine: {
    en: "Support will confirm within {hours}.",
    zh: "客服将在 {hours} 确认到账",
    id: "Dukungan mengonfirmasi dalam {hours}.",
  },
  supportConfirmFallback: { en: "1 business day", zh: "1 个工作日内", id: "1 hari kerja" },
  transferredWaiting: {
    en: "I've transferred, waiting for confirmation",
    zh: "我已转账，等待确认",
    id: "Saya sudah transfer, menunggu konfirmasi",
  },
  orderWaitingConfirm: {
    en: "Order submitted. Waiting for support to confirm the payment.",
    zh: "已提交订单，等待客服确认到账",
    id: "Pesanan terkirim. Menunggu dukungan mengonfirmasi pembayaran.",
  },

  // ===== 梦想豆收支明细 · 明细列（后端中文模板，前端按语言改写） =====
  /** 两段展示文案用间隔号拼接 */
  ledgerDot: { en: "{a} · {b}", zh: "{a} · {b}", id: "{a} · {b}" },
  modElement: { en: "Element Studio", zh: "元素工坊", id: "Studio Elemen" },
  modReverse: { en: "Reverse Studio", zh: "逆向工坊", id: "Studio Balik" },
  modSlice: { en: "Clip Remix", zh: "切片裂变", id: "Remix Klip" },
  modBatch: { en: "Batch Remix", zh: "批量复刻", id: "Remix Massal" },
  modViralRemix: { en: "Viral Remix", zh: "爆款复刻", id: "Remix Viral" },
  modAssetLib: { en: "Asset library", zh: "素材库", id: "Pustaka aset" },
  actGenVideo: { en: "Generate video", zh: "生成视频", id: "Buat video" },
  actEditVideo: { en: "Edit video", zh: "视频编辑", id: "Edit video" },
  actBatchGen: { en: "Batch generate", zh: "批量生成", id: "Buat massal" },
  actGenScript: { en: "Generate script", zh: "生成脚本", id: "Buat naskah" },
  actGenBoard: { en: "Generate board", zh: "生成画板", id: "Buat papan" },
  actParseSource: { en: "Parse source", zh: "拓影解析", id: "Urai sumber" },
  actHd: { en: "HD upscale", zh: "一键高清", id: "HD upscale" },
  actWan: { en: "Wan 3.0", zh: "万相3.0", id: "Wan 3.0" },
  actAiImage: { en: "AI image", zh: "AI生成图片", id: "Gambar AI" },
  /** 「Top up」已于 2026-10-20 全站下线，充值统一说 Recharge（按钮用 meChargeNow） */
  ledgerRecharge: { en: "Recharge", zh: "充值", id: "Isi ulang" },
  ledgerJoinBonus: { en: "Team join bonus", zh: "入企赠送", id: "Bonus gabung tim" },
  ledgerDhAvatar: { en: "Generate character", zh: "生成数字人形象", id: "Buat karakter" },
  ledgerViralBreakdown: { en: "Viral breakdown", zh: "爆款拆解", id: "Urai viral" },
  ledgerBeanExpire: { en: "Dream Beans expired", zh: "梦想豆过期", id: "Kacang Impian kedaluwarsa" },
  /** 明细列：返佣豆入账（后端 title=「返佣豆」） */
  ledgerCommissionGrant: { en: "Commission beans credited", zh: "返佣豆", id: "Kacang komisi masuk" },
  /** 明细列：会员赠送豆发放（后端 title=「会员赠送豆」） */
  ledgerMemberGrant: { en: "Membership gift beans", zh: "会员赠送豆", id: "Kacang hadiah member" },
  /** 充值档位标签（与 nw_bean_recharge_tier.tier_tag 对应），用于「充值 · 标准」拼接 */
  beanTierStandard: { en: "Standard", zh: "标准", id: "Standar" },
  beanTierPopular: { en: "Popular", zh: "热门", id: "Populer" },
  beanTierValue: { en: "Value pack", zh: "超值", id: "Paket hemat" },
  beanTierEnterprise: { en: "Enterprise pick", zh: "企业推荐", id: "Pilihan perusahaan" },
  beanTierBest: { en: "Best deal", zh: "最优惠", id: "Paling hemat" },
  /** 历史遗留流水标题（旧模块 / 退款 / 赠送），用于明细列英文环境兜底 */
  ledgerRegenFrame: { en: "Regenerate frame", zh: "单帧重生成", id: "Buat ulang frame" },
  ledgerRefundRevoke: { en: "Order refund revoke", zh: "订单退款回收", id: "Penarikan dana pesanan" },
  ledgerPointsRefund: { en: "Points refunded", zh: "积分退回", id: "Poin dikembalikan" },
  ledgerSmartAnalyze: { en: "Smart shot analysis", zh: "智能拉片", id: "Analisis bidikan cerdas" },
  ledgerGenStoryboardVideo: { en: "Generate storyboard video", zh: "生成分镜视频", id: "Buat video papan cerita" },
  ledgerGenStoryboardFrames: { en: "Generate storyboard frames", zh: "生成分镜画面", id: "Buat bingkai papan cerita" },
  ledgerDhPreview: { en: "Generate avatar preview", zh: "生成数字人预览", id: "Pratinjau avatar" },
  ledgerDh: { en: "Generate avatar", zh: "生成数字人", id: "Buat avatar" },
  ledgerSelectScript: { en: "Select video script", zh: "选择视频脚本", id: "Pilih naskah video" },
  ledgerAiDirector: { en: "AI director", zh: "智能编导", id: "Sutradara AI" },
  ledgerNewMemberBonus: { en: "New member bonus", zh: "新增成员赠送", id: "Bonus anggota baru" },
  actGenClip: { en: "Generate clips", zh: "生成片段", id: "Buat klip" },
  actAiAnalysis: { en: "AI analysis", zh: "AI智能分析", id: "Analisis AI" },
  ledgerPackageTopup: { en: "Plan top-up", zh: "套餐充值", id: "Isi ulang paket" },
  ledgerTrialGift: { en: "System gift · Free trial", zh: "系统赠送-免费体验版", id: "Hadiah sistem · Uji coba gratis" },
  ledgerUnlockEpisode: { en: "Unlock this episode", zh: "解锁本集", id: "Buka episode ini" },
  ledgerUnlockAll: { en: "Unlock all episodes", zh: "解锁全部剧集", id: "Buka semua episode" },
  ledgerDramaUnlockEpisode: { en: "Unlock this episode", zh: "短剧解锁本集", id: "Buka episode ini" },
  ledgerDramaUnlockAll: { en: "Unlock all episodes", zh: "短剧解锁全部剧集", id: "Buka semua episode" },
  ledgerOther: { en: "Other", zh: "其他", id: "Lainnya" },
  ledgerCombo: { en: "Combo", zh: "组合", id: "Kombinasi" },
  ledgerUnknownModel: { en: "Unknown model", zh: "未知模型", id: "Model tidak dikenal" },
  ledgerCancelledRefunded: { en: "Cancelled · Refunded", zh: "已取消 · 已退费", id: "Dibatalkan · Dikembalikan" },
  ledgerFailedRefunded: { en: "Failed · Refunded", zh: "生成失败 · 已退费", id: "Gagal · Dikembalikan" },
  ledgerRefunded: { en: "Refunded", zh: "已退费", id: "Dikembalikan" },
  ledgerClipFeeFull: {
    en: "{duration}s · {resolution} · Clip fee {n} Dream Beans",
    zh: "{duration}s · {resolution} · 片段费 {n} 梦想豆",
    id: "{duration}s · {resolution} · Biaya klip {n} Kacang Impian",
  },
  ledgerClipFee: {
    en: "{resolution} · Clip fee {n} Dream Beans",
    zh: "{resolution} · 片段费 {n} 梦想豆",
    id: "{resolution} · Biaya klip {n} Kacang Impian",
  },
  ledgerClipCount: { en: "{n} clips generated", zh: "生成片段共 {n} 条", id: "{n} klip dibuat" },
  ledgerBatchBadge: {
    en: "{records} records · {combos} combos · {failed} failed",
    zh: "流水共 {records} 条 · 组合 {combos} · 失败 {failed}",
    id: "{records} catatan · {combos} kombinasi · {failed} gagal",
  },
  ledgerScriptScene: { en: "Script · Scene {n}", zh: "脚本生成 · 场景 {n}", id: "Naskah · Adegan {n}" },
  ledgerSliceId: { en: "Clip Remix #{id}", zh: "切片裂变 #{id}", id: "Remix Klip #{id}" },
  ledgerBatchId: { en: "Batch Remix #{id}", zh: "批量复刻 #{id}", id: "Remix Massal #{id}" },
  ledgerComboId: { en: "Combo #{id}", zh: "组合 #{id}", id: "Kombinasi #{id}" },
  ledgerHdTier: { en: "HD upscale · {tier}", zh: "一键高清-{tier}", id: "HD upscale · {tier}" },
  /** 退费流水兜底标题（后端原文「影豆退回」） */
  ledgerBeanRefund: { en: "Dream Beans refunded", zh: "梦想豆退回", id: "Kacang Impian dikembalikan" },
  /** 切片裂变拓影解析预扣标题 */
  ledgerSliceParse: { en: "Clip Remix · Parse source", zh: "切片裂变拓影解析", id: "Remix Klip · Urai sumber" },
  /** 切片裂变画板预扣标题 */
  ledgerSliceBoard: { en: "Clip Remix · Generate board", zh: "切片裂变-生成画板", id: "Remix Klip · Buat papan" },

  // ===== 后端 PowerConsumeTypeEnum / 写死 title 原文（明细列按整句对照） =====
  ledgerElVideo: { en: "Element Studio · Generate video", zh: "元素工坊-生成视频", id: "Studio Elemen · Buat video" },
  ledgerElVideo480: { en: "Element Studio · Generate video · 480p", zh: "元素工坊-生成视频-480p", id: "Studio Elemen · Buat video · 480p" },
  ledgerElVideo720: { en: "Element Studio · Generate video · 720p", zh: "元素工坊-生成视频-720p", id: "Studio Elemen · Buat video · 720p" },
  ledgerElVideo1080: {
    en: "Element Studio · Generate video · 1080p",
    zh: "元素工坊-生成视频-1080p",
    id: "Studio Elemen · Buat video · 1080p",
  },
  ledgerReplicaBatch: { en: "Viral Remix · Batch generate", zh: "爆款复刻-批量生成", id: "Remix Viral · Buat massal" },
  ledgerReplicaBatch480: {
    en: "Viral Remix · Batch generate · 480p",
    zh: "爆款复刻-批量生成-480p",
    id: "Remix Viral · Buat massal · 480p",
  },
  ledgerReplicaBatch720: {
    en: "Viral Remix · Batch generate · 720p",
    zh: "爆款复刻-批量生成-720p",
    id: "Remix Viral · Buat massal · 720p",
  },
  ledgerReplicaBatch1080: {
    en: "Viral Remix · Batch generate · 1080p",
    zh: "爆款复刻-批量生成-1080p",
    id: "Remix Viral · Buat massal · 1080p",
  },
  ledgerReverseVideo: { en: "Reverse Studio · Generate video", zh: "逆向工坊-生成视频", id: "Studio Balik · Buat video" },
  ledgerReverseVideo480: {
    en: "Reverse Studio · Generate video · 480p",
    zh: "逆向工坊-生成视频-480p",
    id: "Studio Balik · Buat video · 480p",
  },
  ledgerReverseVideo720: {
    en: "Reverse Studio · Generate video · 720p",
    zh: "逆向工坊-生成视频-720p",
    id: "Studio Balik · Buat video · 720p",
  },
  ledgerReverseVideo1080: {
    en: "Reverse Studio · Generate video · 1080p",
    zh: "逆向工坊-生成视频-1080p",
    id: "Studio Balik · Buat video · 1080p",
  },
  ledgerCreateScript: { en: "Creation · Generate script", zh: "自主创作-生成脚本", id: "Kreasi Mandiri · Buat naskah" },
  ledgerCreateBoard: { en: "Creation · Generate board", zh: "自主创作-生成画板", id: "Kreasi Mandiri · Buat papan" },
  ledgerCreateVideo: { en: "Creation · Generate video", zh: "自主创作-生成视频", id: "Kreasi Mandiri · Buat video" },
  ledgerCreateVideo480: { en: "Creation · Generate video · 480p", zh: "自主创作-生成视频-480p", id: "Kreasi Mandiri · Buat video · 480p" },
  ledgerCreateVideo720: { en: "Creation · Generate video · 720p", zh: "自主创作-生成视频-720p", id: "Kreasi Mandiri · Buat video · 720p" },
  ledgerCreateVideo1080: {
    en: "Creation · Generate video · 1080p",
    zh: "自主创作-生成视频-1080p",
    id: "Kreasi Mandiri · Buat video · 1080p",
  },
  ledgerAssetAiImage: { en: "Asset library · AI image", zh: "素材库-AI生成图片", id: "Pustaka aset · Gambar AI" },
  ledgerSliceScript: { en: "Clip Remix · Generate script", zh: "切片裂变-生成脚本", id: "Remix Klip · Buat naskah" },
  ledgerSliceVideo: { en: "Clip Remix · Generate video", zh: "切片裂变-生成视频", id: "Remix Klip · Buat video" },
  ledgerSliceVideo480: { en: "Clip Remix · Generate video · 480p", zh: "切片裂变-生成视频-480p", id: "Remix Klip · Buat video · 480p" },
  ledgerSliceVideo720: { en: "Clip Remix · Generate video · 720p", zh: "切片裂变-生成视频-720p", id: "Remix Klip · Buat video · 720p" },
  ledgerSliceVideo1080: { en: "Clip Remix · Generate video · 1080p", zh: "切片裂变-生成视频-1080p", id: "Remix Klip · Buat video · 1080p" },
  ledgerElVoice: { en: "Element Studio · Voiceover", zh: "元素工坊-口播", id: "Studio Elemen · Lisan" },
  ledgerEditSeedance20_480: {
    en: "Edit video · Seedance2.0 · 480p",
    zh: "视频编辑-Seedance2.0-480p",
    id: "Edit video · Seedance2.0 · 480p",
  },
  ledgerEditSeedance20_720: {
    en: "Edit video · Seedance2.0 · 720p",
    zh: "视频编辑-Seedance2.0-720p",
    id: "Edit video · Seedance2.0 · 720p",
  },
  ledgerEditSeedance20_1080: {
    en: "Edit video · Seedance2.0 · 1080p",
    zh: "视频编辑-Seedance2.0-1080p",
    id: "Edit video · Seedance2.0 · 1080p",
  },
  ledgerEditSeedance25_480: {
    en: "Edit video · Seedance2.5 · 480p",
    zh: "视频编辑-Seedance2.5-480p",
    id: "Edit video · Seedance2.5 · 480p",
  },
  ledgerEditSeedance25_720: {
    en: "Edit video · Seedance2.5 · 720p",
    zh: "视频编辑-Seedance2.5-720p",
    id: "Edit video · Seedance2.5 · 720p",
  },
  ledgerEditSeedance25_1080: {
    en: "Edit video · Seedance2.5 · 1080p",
    zh: "视频编辑-Seedance2.5-1080p",
    id: "Edit video · Seedance2.5 · 1080p",
  },
  ledgerEditSeedanceFast480: {
    en: "Edit video · SeedanceFast · 480p",
    zh: "视频编辑-SeedanceFast-480p",
    id: "Edit video · SeedanceFast · 480p",
  },
  ledgerEditSeedanceFast720: {
    en: "Edit video · SeedanceFast · 720p",
    zh: "视频编辑-SeedanceFast-720p",
    id: "Edit video · SeedanceFast · 720p",
  },
  ledgerEditMiniMax768: { en: "Edit video · MiniMaxH3 · 768P", zh: "视频编辑-MiniMaxH3-768P", id: "Edit video · MiniMaxH3 · 768P" },
  ledgerEditMiniMax2K: { en: "Edit video · MiniMaxH3 · 2K", zh: "视频编辑-MiniMaxH3-2K", id: "Edit video · MiniMaxH3 · 2K" },
  ledgerGenSeedance20_480: {
    en: "Generate video · Seedance2.0 · 480p",
    zh: "视频生成-Seedance2.0-480p",
    id: "Buat video · Seedance2.0 · 480p",
  },
  ledgerGenSeedance20_720: {
    en: "Generate video · Seedance2.0 · 720p",
    zh: "视频生成-Seedance2.0-720p",
    id: "Buat video · Seedance2.0 · 720p",
  },
  ledgerGenSeedance20_1080: {
    en: "Generate video · Seedance2.0 · 1080p",
    zh: "视频生成-Seedance2.0-1080p",
    id: "Buat video · Seedance2.0 · 1080p",
  },
  ledgerGenSeedance25_480: {
    en: "Generate video · Seedance2.5 · 480p",
    zh: "视频生成-Seedance2.5-480p",
    id: "Buat video · Seedance2.5 · 480p",
  },
  ledgerGenSeedance25_720: {
    en: "Generate video · Seedance2.5 · 720p",
    zh: "视频生成-Seedance2.5-720p",
    id: "Buat video · Seedance2.5 · 720p",
  },
  ledgerGenSeedance25_1080: {
    en: "Generate video · Seedance2.5 · 1080p",
    zh: "视频生成-Seedance2.5-1080p",
    id: "Buat video · Seedance2.5 · 1080p",
  },
  ledgerGenSeedanceMini480: {
    en: "Generate video · Seedance2.0Mini · 480p",
    zh: "视频生成-Seedance2.0Mini-480p",
    id: "Buat video · Seedance2.0Mini · 480p",
  },
  ledgerGenSeedanceMini720: {
    en: "Generate video · Seedance2.0Mini · 720p",
    zh: "视频生成-Seedance2.0Mini-720p",
    id: "Buat video · Seedance2.0Mini · 720p",
  },
  ledgerGenMiniMax768: { en: "Generate video · MiniMaxH3 · 768P", zh: "视频生成-MiniMaxH3-768P", id: "Buat video · MiniMaxH3 · 768P" },
  ledgerGenMiniMax2K: { en: "Generate video · MiniMaxH3 · 2K", zh: "视频生成-MiniMaxH3-2K", id: "Buat video · MiniMaxH3 · 2K" },
  ledgerEditWan480: { en: "Edit video · Wan 3.0 · 480p", zh: "视频编辑-万相3.0-480p", id: "Edit video · Wan 3.0 · 480p" },
  ledgerEditWan720: { en: "Edit video · Wan 3.0 · 720p", zh: "视频编辑-万相3.0-720p", id: "Edit video · Wan 3.0 · 720p" },
  ledgerEditWan1080: { en: "Edit video · Wan 3.0 · 1080p", zh: "视频编辑-万相3.0-1080p", id: "Edit video · Wan 3.0 · 1080p" },
  ledgerHd720: { en: "HD upscale · ≤720P", zh: "一键高清-≤720P", id: "HD upscale · ≤720P" },
  ledgerHd1440: { en: "HD upscale · ≤1440P", zh: "一键高清-≤1440P", id: "HD upscale · ≤1440P" },
  ledgerHd2160: { en: "HD upscale · ≤2160P", zh: "一键高清-≤2160P", id: "HD upscale · ≤2160P" },
  ledgerGenWan480: { en: "Generate video · Wan 3.0 · 480p", zh: "视频生成-万相3.0-480p", id: "Buat video · Wan 3.0 · 480p" },
  ledgerGenWan720: { en: "Generate video · Wan 3.0 · 720p", zh: "视频生成-万相3.0-720p", id: "Buat video · Wan 3.0 · 720p" },
  ledgerGenWan1080: { en: "Generate video · Wan 3.0 · 1080p", zh: "视频生成-万相3.0-1080p", id: "Buat video · Wan 3.0 · 1080p" },
  ledgerReverseWan480: { en: "Reverse Studio · Wan 3.0 · 480p", zh: "逆向工坊-万相3.0-480p", id: "Studio Balik · Wan 3.0 · 480p" },
  ledgerReverseWan720: { en: "Reverse Studio · Wan 3.0 · 720p", zh: "逆向工坊-万相3.0-720p", id: "Studio Balik · Wan 3.0 · 720p" },
  ledgerReverseWan1080: { en: "Reverse Studio · Wan 3.0 · 1080p", zh: "逆向工坊-万相3.0-1080p", id: "Studio Balik · Wan 3.0 · 1080p" },
  ledgerCreateWan480: { en: "Creation · Wan 3.0 · 480p", zh: "自主创作-万相3.0-480p", id: "Kreasi Mandiri · Wan 3.0 · 480p" },
  ledgerCreateWan720: { en: "Creation · Wan 3.0 · 720p", zh: "自主创作-万相3.0-720p", id: "Kreasi Mandiri · Wan 3.0 · 720p" },
  ledgerCreateWan1080: { en: "Creation · Wan 3.0 · 1080p", zh: "自主创作-万相3.0-1080p", id: "Kreasi Mandiri · Wan 3.0 · 1080p" },

  // ===== 完善资料弹窗 =====
  profileCompleteTitle: { en: "Complete your profile", zh: "完善资料", id: "Lengkapi profil" },
  profileCompleteHint1: {
    en: "After approval, you can upgrade to the free trial",
    zh: "提交资料通过审核后，即可免费升级体验版",
    id: "Setelah disetujui, Anda bisa naik ke uji coba gratis",
  },
  profileCompleteHint2: {
    en: "and receive 200 trial Dream Beans (valid for 7 days)",
    zh: "并获赠 200 试用梦想豆（有效期 7 天）",
    id: "dan mendapat 200 Kacang Impian uji coba (berlaku 7 hari)",
  },
  profileCompleteHint3: {
    en: "Trial Dream Beans can be used to try AI generation",
    zh: "试用梦想豆可用于体验 AI 生成功能",
    id: "Kacang Impian uji coba bisa dipakai untuk mencoba fitur AI",
  },
  profileRejectHint: {
    en: "Last review was rejected: {reason}. Please revise and submit again.",
    zh: "上次审核被拒绝：{reason}，请修改后重新提交",
    id: "Peninjauan terakhir ditolak: {reason}. Perbaiki lalu kirim lagi.",
  },
  profileIdentity: { en: "Identity", zh: "用户身份", id: "Identitas" },
  profileSelectIdentity: { en: "Select your identity", zh: "请选择用户身份", id: "Pilih identitas" },
  profileContact: { en: "Contact name", zh: "联系人姓名", id: "Nama kontak" },
  profileEnterContact: { en: "Enter the contact name", zh: "请输入联系人姓名", id: "Masukkan nama kontak" },
  profilePhone: { en: "Phone", zh: "手机号", id: "Telepon" },
  profileEnterPhone: { en: "Phone number", zh: "请输入手机号", id: "Nomor telepon" },
  profilePhoneBound: { en: "Bound", zh: "已绑定", id: "Terikat" },
  profileCode: { en: "Code", zh: "验证码", id: "Kode" },
  profileEnterSmsCode: { en: "Enter the SMS code", zh: "请输入短信验证码", id: "Masukkan kode SMS" },
  profileSendCode: { en: "Send code", zh: "发送验证码", id: "Kirim kode" },
  profileChannel: { en: "Source", zh: "获知渠道", id: "Sumber" },
  profileSelectChannel: { en: "Select how you found us", zh: "请选择获知渠道", id: "Pilih dari mana Anda tahu" },
  profileTeamSize: { en: "Team size", zh: "团队规模", id: "Ukuran tim" },
  profileSelectTeamSize: { en: "Select a team size", zh: "请选择规模", id: "Pilih ukuran tim" },
  profileSubmit: { en: "Submit profile", zh: "提交资料", id: "Kirim profil" },
  profilePhoneTaken: {
    en: "This number is linked to another account. Use a different one.",
    zh: "该手机号已绑定其他账号，请更换手机号",
    id: "Nomor ini terikat ke akun lain. Gunakan nomor lain.",
  },
  profileSubmittedOk: {
    en: "Profile submitted. 200 trial Dream Beans will be issued after approval.",
    zh: "资料已提交，审核通过后将发放 200 试用梦想豆",
    id: "Profil terkirim. 200 Kacang Impian uji coba diberikan setelah disetujui.",
  },
  profileIdentityEcommerce: { en: "E-commerce creator", zh: "电商达人", id: "Kreator e-commerce" },
  profileIdentityMcn: { en: "MCN agency", zh: "MCN机构", id: "Agensi MCN" },
  profileIdentityBrand: { en: "Brand", zh: "品牌方", id: "Merek" },
  profileIdentityOther: { en: "Other", zh: "其他", id: "Lainnya" },
  profileChannelFriend: { en: "Friend referral", zh: "朋友推荐", id: "Rekomendasi teman" },
  profileChannelWechat: { en: "Official account", zh: "公众号", id: "Akun resmi" },
  profileChannelShortVideo: { en: "Short video", zh: "短视频", id: "Video pendek" },
  profileChannelSearch: { en: "Search engine", zh: "搜索引擎", id: "Mesin pencari" },
  profileChannelOther: { en: "Other", zh: "其他", id: "Lainnya" },
  profileScale1To10: { en: "1-10 people", zh: "1-10人", id: "1-10 orang" },
  profileScale11To50: { en: "11-50 people", zh: "11-50人", id: "11-50 orang" },
  profileScale51To200: { en: "51-200 people", zh: "51-200人", id: "51-200 orang" },
  profileScale200Plus: { en: "200+ people", zh: "200人以上", id: "200+ orang" },

  // ===== 梦想豆余额不足弹窗 =====
  powerInsufficientTitle: { en: "Not enough Dream Beans", zh: "梦想豆余额不足", id: "Saldo Kacang Impian tidak cukup" },
  powerInsufficientBody: {
    en: "You don't have enough Dream Beans to unlock this service. Top up soon so it doesn't interrupt your experience.",
    zh: "当前梦想豆数量不足以解锁本次服务。为了不影响您的体验，建议及时补充。",
    id: "Jumlah Kacang Impian saat ini tidak cukup untuk membuka layanan ini. Isi ulang agar pengalaman Anda tidak terganggu.",
  },
  powerInsufficientLater: { en: "Not now", zh: "暂不处理", id: "Nanti saja" },

  // ===== 我的分佣（原型 screen-commission） =====
  commissionTitle: { en: "My Commission", zh: "我的分佣", id: "Komisi Saya" },
  /** 「我的」页分佣入口行右侧的总数文案（待结算 + 已结算，单位：颗） */
  commissionRowTotal: { en: "{n} Dream Beans", zh: "共 {n} 豆", id: "{n} kacang" },
  commissionMyInviteCode: { en: "My invite code", zh: "我的邀请码", id: "Kode undangan saya" },
  commissionCopyInvite: { en: "Copy invite code", zh: "复制邀请码", id: "Salin kode undangan" },
  commissionScanClick: { en: "Scans / clicks", zh: "扫码 / 点击", id: "Pindai / klik" },
  commissionBoundFans: { en: "Bound fans", zh: "绑定粉丝", id: "Pengikut terikat" },
  /** 邀请码卡片计数①：直接绑定的用户（原型「L1 关联用户」） */
  commissionLinkedL1: { en: "L1 referrals", zh: "L1 关联用户", id: "Pengguna terkait L1" },
  /** 邀请码卡片计数②：我的一级粉丝再带来的人（原型「L2 关联用户」） */
  commissionLinkedL2: { en: "L2 referrals", zh: "L2 关联用户", id: "Pengguna terkait L2" },
  commissionDealCount: { en: "Total orders", zh: "累计成交", id: "Total transaksi" },
  commissionOverview: { en: "Commission overview", zh: "佣金概览", id: "Ringkasan komisi" },
  commissionPending: { en: "Pending", zh: "待结算", id: "Menunggu" },
  commissionPendingBadge: {
    en: "Awaiting confirmation",
    zh: "未确认到账",
    id: "Menunggu konfirmasi",
  },
  commissionSettled: { en: "Settled", zh: "已结算", id: "Selesai" },
  commissionSettledBadge: { en: "Dream Beans credited", zh: "返佣豆已入账", id: "Kacang sudah masuk" },
  /**
   * 「待结算」下方的小字：2026-10-20 起计佣前移到下单，
   * 待结算数字里**包含订单还没付款的预估**，必须显式说明。
   */
  commissionPendingUnpaidHint: {
    en: "Includes {n} beans from unpaid orders",
    zh: "含未付款订单 {n} 豆",
    id: "Termasuk {n} kacang dari pesanan belum dibayar",
  },
  commissionSettleMode: { en: "Settlement", zh: "结算形式", id: "Bentuk penyelesaian" },
  commissionSettleModeBean: {
    // 只留「梦想豆」—— 括号里的「非现金、暂不支持提现」按产品要求去掉
    en: "Dream Beans",
    zh: "梦想豆",
    id: "Kacang Impian",
  },
  /** 佣金数量单位（概览里的「X 豆」） */
  commissionBeanUnit: { en: "Dream Beans", zh: "豆", id: "kacang" },
  commissionRateL1: { en: "Direct (L1) rate", zh: "直推比例", id: "Rasio langsung (L1)" },
  commissionRateL2: { en: "Second level (L2) rate", zh: "二代比例", id: "Rasio level 2 (L2)" },
  commissionCoverBiz: { en: "Covered products", zh: "覆盖商品", id: "Produk tercakup" },
  commissionBeanRate: { en: "Bean conversion", zh: "返佣豆折算", id: "Konversi kacang" },
  commissionBeanHint: {
    en: "Settled in Dream Beans: ¥1 = {n} beans",
    zh: "结算为梦想豆：1 元 = {n} 颗",
    id: "Diselesaikan dalam Kacang Impian: ¥1 = {n} kacang",
  },
  commissionLoading: { en: "Loading…", zh: "加载中…", id: "Memuat…" },
  commissionDetail: { en: "Commission details", zh: "佣金明细", id: "Rincian komisi" },
  commissionColTime: { en: "Time", zh: "时间", id: "Waktu" },
  commissionColSource: { en: "Source fan", zh: "来源粉丝", id: "Pengikut sumber" },
  commissionColLevel: { en: "Level", zh: "层级", id: "Level" },
  commissionColOrder: { en: "Order", zh: "订单", id: "Pesanan" },
  // 2026-10-23 口径：佣金明细只展示豆，两列金额统一按发豆口径换算（单位写在表头）
  commissionColBase: { en: "Order value (Dream Beans)", zh: "计佣基数（豆）", id: "Nilai dasar (kacang)" },
  commissionColAmount: { en: "Commission (Dream Beans)", zh: "佣金（豆）", id: "Komisi (kacang)" },
  commissionColStatus: { en: "Status", zh: "状态", id: "Status" },
  commissionStatusPending: { en: "Pending", zh: "待结算", id: "Menunggu" },
  commissionStatusSettled: { en: "Settled", zh: "已结算", id: "Selesai" },
  commissionStatusCanceled: { en: "Revoked", zh: "已撤销", id: "Dibatalkan" },
  commissionBizMember: { en: "Membership", zh: "会员费", id: "Keanggotaan" },
  commissionBizRecharge: { en: "Bean top-up", zh: "豆充值", id: "Isi ulang kacang" },
  commissionFanFallback: { en: "Fan #{id}", zh: "粉丝 #{id}", id: "Pengikut #{id}" },
  commissionEmpty: { en: "No commission records yet", zh: "暂无佣金记录", id: "Belum ada catatan komisi" },
  commissionReversed: { en: "Reversed −{amt}", zh: "已冲抵 −{amt}", id: "Dibalik −{amt}" },
  commissionReversedHint: {
    en: "This settled commission was reversed by an order refund; it will be deducted from your next settlement.",
    zh: "该笔已结算佣金因订单退款记负数冲抵，下期结算先抵扣。",
    id: "Komisi yang sudah diselesaikan ini dibalik karena pengembalian dana; akan dipotong dari penyelesaian berikutnya.",
  },
  // ---- 我的推荐（下级名单，推荐官漏斗第二/三环）----
  commissionFansTitle: { en: "My referrals", zh: "我的推荐", id: "Rujukan saya" },
  commissionFansHint: {
    // 概览里的计数已改名为「L1 关联用户」，这里的口径说明跟着换词，否则指向一个不存在的指标
    en: "Direct referrals only (same scope as “L1 referrals”)",
    zh: "仅统计直推粉丝，与「L1 关联用户」同口径",
    id: "Hanya rujukan langsung (cakupan sama dengan “Pengguna terkait L1”)",
  },
  commissionColFan: { en: "Fan", zh: "粉丝", id: "Pengikut" },
  commissionColBindTime: { en: "Joined at", zh: "绑定时间", id: "Waktu bergabung" },
  commissionFansDeals: { en: "Orders", zh: "成交", id: "Transaksi" },
  commissionFansDealUnit: { en: "{n} order(s)", zh: "{n} 单", id: "{n} transaksi" },
  commissionFansNoDeal: { en: "No order yet", zh: "未成交", id: "Belum transaksi" },
  commissionFansEmpty: {
    en: "No one has joined with your invite code yet",
    zh: "还没有人通过你的邀请码注册",
    id: "Belum ada yang bergabung dengan kode undangan Anda",
  },
  commissionLoadFailed: {
    en: "Could not load commission data",
    zh: "加载分佣数据失败",
    id: "Gagal memuat data komisi",
  },
  /**
   * 返佣梦想豆来源（`BeanSourceConstants.COMMISSION`）。
   * ⚠️ 必须与 `localizeProfileLabel.ts` 的 `SOURCE_BY_CODE.COMMISSION` 同时存在，
   * 否则分池卡片会渲染成空白。
   */
  poolCommission: { en: "Commission", zh: "返佣", id: "Komisi" },
  /** 返佣豆入账流水（`PowerRecordTypeConstants.COMMISSION_GRANT = 7`） */
  recordCommissionGift: { en: "Commission beans", zh: "返佣豆", id: "Kacang komisi" },
  /**
   * 退款回收流水（`PowerRecordTypeConstants.REFUND_REVOKE = 8`）：
   * 订单退款成功后收回该订单发放的、尚未被消费的梦想豆 —— 属于**扣减**方向。
   */
  recordRefundRevoke: {
    en: "Refund reclaim",
    zh: "退款回收",
    id: "Penarikan refund",
  },

  // ===== 订单 body 展示（明细「订单」列：body 是后端写死的中文，这里只做展示层翻译） =====
  /** 充豆订单前缀（后端 body 为「影豆充值-标准」） */
  orderBeanTopUp: { en: "Dream Beans top-up", zh: "影豆充值", id: "Isi ulang Dream Beans" },
  orderPeriodMonth: { en: "Monthly", zh: "按月", id: "Bulanan" },
  orderPeriodOnce: { en: "One-time", zh: "一次性", id: "Sekali" },
  orderPeriodQuarter: { en: "Quarterly", zh: "按季", id: "Triwulanan" },
  orderPeriodYear: { en: "Yearly", zh: "按年", id: "Tahunan" },

  // ===== 邀请海报（我的分佣页 · 原型 posterMask 弹窗预览 + 下载，无模拟扫码） =====
  commissionPosterBtn: {
    en: "Generate poster",
    zh: "生成邀请海报",
    id: "Buat poster undangan",
  },
  /** 弹窗标题 */
  commissionPosterTitle: { en: "Invite friends", zh: "邀请好友", id: "Undang teman" },
  /** 弹窗底部按钮：复制注册链接（与海报二维码同一条 ?invite= 落地链接） */
  commissionCopyLink: {
    en: "Copy invite link",
    zh: "复制注册链接",
    id: "Salin tautan undangan",
  },
  /** 弹窗副标题（原型 modal .sub） */
  commissionPosterModalSub: {
    en: "Share your exclusive poster — friends register with your invite code and you both earn rewards",
    zh: "分享专属海报，好友注册时填写你的邀请码，双方都能获得奖励",
    id: "Bagikan poster eksklusif — teman mendaftar dengan kode undanganmu, keduanya mendapat hadiah",
  },
  /** 海报主标题（洋红实色大字，设计稿「好友邀请」） */
  commissionPosterHeadTitle: {
    en: "Friend Invitation",
    zh: "好友邀请",
    id: "Undangan teman",
  },
  /** 海报副标题（粉渐变字） */
  commissionPosterHeadSub: {
    en: "Sign Up to Unlock New AI Video Creation",
    zh: "注册解锁AI视频创作新体验",
    id: "Daftar dan buka pengalaman baru berkarya video AI",
  },
  /** 海报说明文案（标题组最下一行小字） */
  commissionPosterHeadTag: {
    en: "No filming needed. Let AI generate marketing videos in seconds.",
    zh: "不用拍摄，AI帮你快速生成营销短视频",
    id: "Tidak perlu syuting, AI buat video marketing pendek secara instan",
  },
  /** 海报邀请码标签（白卡内、二维码上方，两侧渐变装饰条夹字） */
  commissionPosterCodeOwnerLabel: {
    en: "My Invite Code",
    zh: "我的邀请码",
    id: "Kode undangan saya",
  },
  /** 海报二维码下方扫码提示 */
  commissionPosterHint: {
    en: "Scan to Register",
    zh: "扫码立即注册",
    id: "Scan QR untuk daftar sekarang",
  },

  // ===== 一级导航（管理员） =====
  navTraining: { en: "Training", zh: "培训管理", id: "Pelatihan" },
  navCommission: { en: "Commission", zh: "分佣", id: "Komisi" },
  navBenefits: { en: "Benefits", zh: "权益", id: "Manfaat" },

  // ===== 我的会员（原型 screen-member） =====
  memberTitle: { en: "My membership", zh: "我的会员", id: "Keanggotaan saya" },
  memberCurrentTier: { en: "Current tier", zh: "当前档位", id: "Paket saat ini" },
  memberActivateAt: { en: "Active since", zh: "生效时间", id: "Aktif sejak" },
  memberExpireAt: { en: "Expires", zh: "到期时间", id: "Berakhir" },
  memberRemainDays: { en: "Days left", zh: "剩余", id: "Sisa hari" },
  memberSeats: { en: "Sub-accounts", zh: "子账户", id: "Sub-akun" },
  memberPool: { en: "Shared bean pool", zh: "共享豆余额", id: "Kumpulan kacang bersama" },
  memberBenefitCount: { en: "Active benefits", zh: "生效权益项", id: "Manfaat aktif" },
  memberBenefitList: { en: "Current benefits", zh: "当前权益清单", id: "Daftar manfaat saat ini" },
  memberColBenefit: { en: "Benefit", zh: "权益项", id: "Manfaat" },
  memberColSpec: { en: "Spec", zh: "规格", id: "Spesifikasi" },
  memberColPeriod: { en: "Period", zh: "期限", id: "Periode" },
  memberColDeliver: { en: "Delivery", zh: "交付方式", id: "Pengiriman" },
  memberColStatus: { en: "Status", zh: "状态", id: "Status" },
  memberExpireNote: {
    en: "When membership expires, the tier drops to Guest",
    zh: "会员到期时：会员等级降为游客",
    id: "Saat keanggotaan berakhir: level menjadi Tamu",
  },
  memberPermanent: { en: "Permanent", zh: "永久", id: "Permanen" },
  memberNoBenefits: { en: "No active benefits", zh: "暂无生效权益", id: "Belum ada manfaat aktif" },
  memberLoading: { en: "Loading membership…", zh: "加载会员信息…", id: "Memuat keanggotaan…" },
  memberLoadFailed: {
    en: "Could not load membership data",
    zh: "加载会员信息失败",
    id: "Gagal memuat data keanggotaan",
  },
  /** 档位动作用语：购买 / 升级 / 续费 / 当前档 / 不可降级 */
  memberActionBuy: { en: "Buy", zh: "购买", id: "Beli" },
  memberActionUpgrade: { en: "Upgrade", zh: "升级", id: "Tingkatkan" },
  memberActionRenewUpgrade: { en: "Renew / Upgrade", zh: "续费 / 升级", id: "Perpanjang / Upgrade" },
  /** 「我的」页会员卡 meta 行（原型 jsHeroMeta 拼接式） */
  memberValidUntilMeta: { en: "Valid until {date}", zh: "有效期至 {date}", id: "Berlaku hingga {date}" },
  memberBenefitItems: { en: "{n} benefits", zh: "权益 {n} 项", id: "{n} manfaat" },
  memberActionRenew: { en: "Renew", zh: "续费", id: "Perpanjang" },
  memberActionCurrent: { en: "Current plan", zh: "当前档位", id: "Paket saat ini" },
  memberActionLower: { en: "Unavailable", zh: "不可降级", id: "Tidak tersedia" },
  /** 子账户管理页入口（Me 页一行） */
  meRowSeats: { en: "Sub-accounts", zh: "子账户管理", id: "Kelola sub-akun" },

  // ===== 子账户管理（原型 screen-seats） =====
  seatsTitle: { en: "Sub-accounts", zh: "子账户管理", id: "Kelola sub-akun" },
  seatsDesc: {
    en: "Visible for Professional / Professional Live only — 1 admin + up to 2 sub-accounts, benefits and beans fully shared",
    zh: "仅 专业版 / 专业直播版可见 —— 1 个管理员 + 最多 2 个子账户，权益与豆全共享",
    id: "Hanya untuk Professional / Professional Live — 1 admin + maksimal 2 sub-akun, manfaat & kacang dibagikan",
  },
  seatsAccountList: { en: "Accounts", zh: "账号列表", id: "Daftar akun" },
  seatsColAccount: { en: "Account", zh: "账号", id: "Akun" },
  seatsColRole: { en: "Role", zh: "角色", id: "Peran" },
  seatsColMember: { en: "Member", zh: "会员", id: "Anggota" },
  seatsColAction: { en: "Action", zh: "操作", id: "Aksi" },
  seatsHintUsed: { en: "{used} / {total} used", zh: "{used} / {total} 已用", id: "{used} / {total} terpakai" },
  seatsHintLeft: { en: "· {n} left", zh: "· 还可加 {n}", id: "· sisa {n}" },
  seatsHintFull: { en: "· full", zh: "· 已满", id: "· penuh" },
  seatsAddBtn: { en: "Add sub-account", zh: "添加子账户", id: "Tambah sub-akun" },
  seatsSeatFullUpgrade: { en: "Seats full · upgrade", zh: "席位已满 · 升级扩容", id: "Kursi penuh · tingkatkan" },
  seatsAddHintLeft: {
    en: "This tier caps at {total} accounts — you can add {n} more",
    zh: "本档位封顶 {total} 个账号，还可添加 {n} 个",
    id: "Paket ini maksimal {total} akun — bisa tambah {n} lagi",
  },
  seatsAddHintFull: {
    en: "Limit reached: {total} accounts per tier (1 admin + {sub} sub-accounts)",
    zh: "已达上限：本档位封顶 {total} 个账号（1 管理员 + {sub} 子账户）",
    id: "Batas tercapai: {total} akun per paket (1 admin + {sub} sub-akun)",
  },
  seatsEmpty: {
    en: "No sub-accounts yet — use “Add sub-account” below",
    zh: "还没有子账户，点下方「+ 添加子账户」",
    id: "Belum ada sub-akun — gunakan “Tambah sub-akun” di bawah",
  },
  /* 添加子账户弹窗（原型 screen-seats）—— 页内弹窗，不再跳转到其他页面 */
  seatsAddTitle: { en: "Add sub-account", zh: "添加子账户", id: "Tambah sub-akun" },
  seatsAddSub: {
    en: "A sub-account is a normal account — not a member, cannot top up beans; its usage deducts the admin's shared pool.",
    zh: "子账户为普通账号 —— 不是会员、不能充豆，消费扣管理员的共享池。",
    id: "Sub-akun adalah akun biasa — bukan anggota, tidak bisa mengisi kacang; pemakaiannya memotong kumpulan bersama admin.",
  },
  seatsAddUsername: { en: "Account", zh: "账号", id: "Akun" },
  seatsAddUsernamePh: {
    en: "6–11 letters/digits, must contain both (e.g. editor03)",
    zh: "6–11 位字母数字，须同时含字母和数字（例：editor03）",
    id: "6–11 huruf/angka, harus keduanya (mis. editor03)",
  },
  seatsAddPassword: { en: "Initial password", zh: "初始密码", id: "Kata sandi awal" },
  seatsAddPasswordPh: {
    en: "6–16 letters/digits, must contain both",
    zh: "6–16 位字母数字，须同时含字母和数字",
    id: "6–16 huruf/angka, harus keduanya",
  },
  seatsAddNick: {
    en: "Nickname (optional, defaults to the account)",
    zh: "昵称（选填，默认与账号同名）",
    id: "Nama panggilan (opsional, default sama dengan akun)",
  },
  seatsAddNickPh: { en: "e.g. Editor-Amin", zh: "例：剪辑-阿明", id: "mis. Editor-Amin" },
  seatsAddRequired: { en: "*Required", zh: "*必填", id: "*Wajib" },
  seatsAddConfirm: { en: "Add", zh: "确认添加", id: "Tambahkan" },
  seatsAddUsernameInvalid: {
    en: "Account must be 6–11 characters and contain both letters and digits",
    zh: "账号须为 6–11 位，且同时包含字母和数字",
    id: "Akun harus 6–11 karakter dan mengandung huruf serta angka",
  },
  seatsAddPasswordInvalid: {
    en: "Password must be 6–16 characters and contain both letters and digits",
    zh: "密码须为 6–16 位，且同时包含字母和数字",
    id: "Kata sandi harus 6–16 karakter dan mengandung huruf serta angka",
  },
  seatsAddSeatFull: {
    en: "No seat left — upgrade your tier to add more",
    zh: "席位已满，升级档位可扩容",
    id: "Kursi penuh — tingkatkan paket untuk menambah",
  },
  seatsAddSuccess: { en: "Sub-account created: {name}", zh: "已创建子账户 {name}", id: "Sub-akun dibuat: {name}" },
  seatsRoleAdmin: { en: "Admin", zh: "管理员", id: "Admin" },
  seatsRoleSub: { en: "Sub-account", zh: "子账户", id: "Sub-akun" },
  seatsSelf: { en: "You", zh: "本人", id: "Anda" },
  seatsMemberNo: { en: "Not a member", zh: "非会员", id: "Bukan anggota" },
  seatsMemberYes: { en: "Yes", zh: "是", id: "Ya" },
  seatsDisabled: { en: "Disabled", zh: "已停用", id: "Dinonaktifkan" },
  seatsResetPwd: { en: "Reset password", zh: "重置密码", id: "Atur ulang sandi" },
  seatsDisable: { en: "Disable", zh: "停用", id: "Nonaktifkan" },
  seatsEnable: { en: "Enable", zh: "启用", id: "Aktifkan" },
  seatsResetSuccess: { en: "Password reset for {name}", zh: "已重置 {name} 的密码", id: "Sandi {name} diatur ulang" },
  seatsDisabledSuccess: { en: "Disabled {name}", zh: "已停用 {name}", id: "{name} dinonaktifkan" },
  seatsEnabledSuccess: { en: "Enabled {name}", zh: "已启用 {name}", id: "{name} diaktifkan" },
  /** 停用不占席位 ⇒ 停用期间空位可能被别人用掉，启用前要提示先腾位 */
  seatsEnableSeatFull: {
    en: "No seat left — disable another sub-account first",
    zh: "席位已满，请先停用其他子账户后再启用",
    id: "Kursi penuh — nonaktifkan sub-akun lain dulu",
  },
  seatsResetTitle: { en: "Reset password · {name}", zh: "重置密码 · {name}", id: "Atur ulang sandi · {name}" },
  seatsResetNewPwd: { en: "New password", zh: "新密码", id: "Sandi baru" },
  seatsResetConfirmPwd: { en: "Confirm new password", zh: "确认新密码", id: "Konfirmasi sandi baru" },
  seatsResetConfirmPh: { en: "Repeat the new password", zh: "再次输入新密码", id: "Ulangi sandi baru" },
  seatsResetConfirm: { en: "Confirm reset", zh: "确认重置", id: "Konfirmasi" },
  seatsResetMismatch: { en: "The two passwords do not match", zh: "两次输入的密码不一致", id: "Kata sandi tidak sama" },

  // ===== 权益项名称 / 期限 / 交付方式（原型 screen-member · 权益清单） =====
  benefitBeanQuota: { en: "Bean quota", zh: "梦想豆额度", id: "Kuota kacang" },
  benefitComputeQuota: {
    en: "Video / image / HD render compute",
    zh: "生视频 / 生图 / 高清渲染算力",
    id: "Komputasi video / gambar / HD",
  },
  benefitToolLicense: { en: "Account validity", zh: "账号有效期", id: "Masa berlaku akun" },
  benefitAccountSeat: {
    en: "Team collaborators",
    zh: "团队协同人数",
    id: "Jumlah kolaborator tim",
  },
  benefitAiLiveOps: { en: "AI live ops", zh: "AI 直播代运营", id: "Operasional live AI" },
  benefitCourseOnline: { en: "Online course", zh: "线上课件", id: "Materi online" },
  benefitTutorialVideo: { en: "Tutorial videos", zh: "教学视频", id: "Video tutorial" },
  benefitMeetingPass: {
    en: "On-site industry meeting access",
    zh: "现场产业会议到场资格",
    id: "Akses pertemuan industri",
  },
  benefitOfflineTeaching: {
    en: "Offline coaching",
    zh: "线下带教",
    id: "Pendampingan offline",
  },
  benefitOnlineCoaching: {
    en: "Online coaching / dedicated contact",
    zh: "线上陪跑 / 专人对接",
    id: "Pendampingan online",
  },
  benefitMonthlyReview: {
    en: "Monthly review + prompt updates",
    zh: "月度复盘 + 提示词更新",
    id: "Review bulanan",
  },
  benefitTokenQuota: { en: "Token quota", zh: "Token 额度", id: "Kuota token" },
  benefitUnknown: { en: "Benefit", zh: "权益", id: "Manfaat" },

  /** 权益实例状态（nw_user_entitlement.status） */
  entitlementStatusActive: { en: "Active", zh: "有效", id: "Aktif" },
  entitlementStatusUsedUp: { en: "Used up", zh: "已用完", id: "Habis" },
  entitlementStatusExpired: { en: "Expired", zh: "已过期", id: "Kedaluwarsa" },
  entitlementStatusRevoked: { en: "Revoked", zh: "已撤回", id: "Ditarik" },
  entitlementStatusFrozen: { en: "Frozen", zh: "冻结", id: "Dibekukan" },

  /** 发放方式（deliver_mode） */
  deliverModeInstant: { en: "Instant", zh: "即时", id: "Langsung" },
  deliverModeMonthly: { en: "Monthly", zh: "按月", id: "Bulanan" },
  deliverModeScheduled: { en: "Scheduled", zh: "定时", id: "Terjadwal" },
  deliverModeOffline: { en: "Offline redemption", zh: "线下核销", id: "Penukaran offline" },
  deliverModeUnknown: { en: "—", zh: "—", id: "—" },

  /** 共享池「会员到期」口径说明（screen-seats 表格列） */
  poolSourceGift: { en: "Gift", zh: "赠送", id: "Hadiah" },
  poolSourceMember: { en: "Membership grant", zh: "会员赠送", id: "Pemberian keanggotaan" },
  poolSourceRecharge: { en: "Top-up", zh: "充值", id: "Isi ulang" },
  poolSourceCommission: { en: "Commission", zh: "返佣", id: "Komisi" },
  poolSourceLegacy: { en: "Legacy", zh: "历史", id: "Lama" },
  poolSourceUnknown: { en: "Other", zh: "其他", id: "Lainnya" },

  seatsLoading: { en: "Loading…", zh: "加载中…", id: "Memuat…" },
  seatsLoadFailed: { en: "Could not load data", zh: "加载失败", id: "Gagal memuat data" },
  poolSourceAdminRecharge: { en: "Admin top-up", zh: "管理员充值", id: "Isi ulang admin" },

  // ===== 我的会员页补充文案（原型 screen-member） =====
  memberRemainDaysText: { en: "{days} days left", zh: "剩余 {days} 天", id: "Sisa {days} hari" },
  memberBeanBalance: { en: "Bean balance", zh: "梦想豆余额", id: "Saldo kacang" },
  memberToolLicenseRemain: {
    en: "Tool license left",
    zh: "工具授权剩余",
    id: "Sisa lisensi alat",
  },
  memberCollabAccounts: { en: "Collaborators", zh: "协同账号", id: "Kolaborator" },
  memberStatusActive: { en: "Active", zh: "生效中", id: "Aktif" },
  memberStatusInactive: { en: "Inactive", zh: "未生效", id: "Tidak aktif" },
  memberBenefitListSub: { en: "{tier} · {n} items", zh: "{tier} · 共 {n} 项", id: "{tier} · {n} item" },
  /** 权益清单小标题：生效数 / 类目总数（如 Starter · 3 / 5 items） */
  memberBenefitListSubCount: {
    en: "{tier} · {active} / {total} items",
    zh: "{tier} · {active} / {total} 项",
    id: "{tier} · {active} / {total} item",
  },
  /** 当前档位未包含的权益（置灰行右侧文案） */
  memberBenefitLocked: { en: "Not included", zh: "未包含", id: "Tidak termasuk" },
  /** 置灰行的升级提示 */
  memberBenefitLockedHint: {
    en: "Available from {tier}",
    zh: "升级到 {tier} 可用",
    id: "Tersedia mulai {tier}",
  },
  memberNotMember: { en: "Not a member yet", zh: "还不是会员", id: "Belum menjadi anggota" },
  memberNotMemberDesc: {
    en: "Membership is required before you can top up beans. Pick a tier to unlock bean top-ups and all benefits.",
    zh: "会员是「充梦想豆」的前置 —— 先成为会员，才能充值梦想豆。选择档位即可解锁充豆与全部权益。",
    id: "Keanggotaan adalah prasyarat sebelum bisa isi ulang kacang. Pilih paket untuk membuka isi ulang & semua manfaat.",
  },
  memberChooseTier: { en: "Choose a tier", zh: "去选择会员档位", id: "Pilih paket" },
  // 子账户（role=2）：本身不是会员，只共享主账户的权益与豆池，不能自行充值
  memberSubAccountTitle: { en: "Team member account", zh: "成员账号", id: "Akun anggota tim" },
  memberSubAccountSharedBy: {
    en: "Benefits and Dream Beans are shared by {name}. Only the owner account can top up.",
    zh: "会员权益与梦想豆由 {name} 共享，充值请由主账户操作。",
    id: "Manfaat dan Dream Beans dibagikan oleh {name}. Hanya akun pemilik yang dapat mengisi ulang.",
  },
  memberSubAccountSharedGeneric: {
    en: "Benefits and Dream Beans are shared by the owner account. Only the owner can top up.",
    zh: "会员权益与梦想豆由主账户共享，充值请由主账户操作。",
    id: "Manfaat dan Dream Beans dibagikan oleh akun pemilik. Hanya pemilik yang dapat mengisi ulang.",
  },
  memberSubAccountNoRecharge: {
    en: "Sub-accounts cannot top up. Please use the owner account.",
    zh: "子账户不能充值，请使用主账户操作。",
    id: "Akun anggota tidak dapat mengisi ulang. Gunakan akun pemilik.",
  },
  /** 子账户不能自行购买 / 升级会员，档位页与拦截提示共用 */
  memberSubAccountNoUpgrade: {
    en: "Please upgrade membership with the owner account.",
    zh: "请使用主账号升级会员",
    id: "Silakan tingkatkan keanggotaan dengan akun pemilik.",
  },
  beanRechargeOwnerOnly: {
    en: "Dream Beans are topped up by the owner account — you share the same balance.",
    zh: "梦想豆由主账户统一充值，你与主账户共用余额。",
    id: "Dream Beans diisi ulang oleh akun pemilik — Anda berbagi saldo yang sama.",
  },
  memberSharedBeans: { en: "Shared Dream Beans", zh: "共享梦想豆余额", id: "Dream Beans bersama" },
  memberActivateSince: {
    en: "Active {start} · Expires {end}",
    zh: "生效 {start} · 到期 {end}",
    id: "Aktif {start} · Berakhir {end}",
  },
  memberExpireNoteTail: {
    en: ", but each benefit expires on its own schedule ⇒ benefit checks are independent of the tier.",
    zh: "，但权益按自己的到期时间独立过期 ⇒ 权益校验与会员等级无关。",
    id: ", namun setiap manfaat berakhir sesuai jadwalnya sendiri ⇒ pengecekan manfaat tidak bergantung pada level.",
  },
  memberUnitDays: { en: "days", zh: "天", id: "hari" },
  memberUnitItems: { en: "items", zh: "项", id: "item" },
  memberUnitAccounts: { en: "accounts", zh: "个", id: "akun" },
  memberUnitSeats: { en: "seats", zh: "席", id: "kursi" },
  memberUnitBean: { en: "beans", zh: "颗", id: "kacang" },
  memberValFull: { en: "Full", zh: "全面", id: "Penuh" },
  memberSeatsOccupied: {
    en: "{used} seats occupied",
    zh: "已占用 {used} 席",
    id: "{used} kursi terpakai",
  },
  memberSeatHint: { en: "{used} / {total}", zh: "{used} / {total}", id: "{used} / {total}" },
  memberNextGrant: { en: "Next grant {date}", zh: "下次发放 {date}", id: "Pemberian berikutnya {date}" },
  memberGrantedPeriods: {
    en: "{done}/{total} periods granted",
    zh: "已发放 {done}/{total} 期",
    id: "Sudah diberikan {done}/{total} periode",
  },
  memberUsedText: { en: "{used} / {total}", zh: "{used} / {total}", id: "{used} / {total}" },
  memberColRemain: { en: "Remaining", zh: "余量", id: "Sisa" },
  memberSeatFree: { en: "{n} more available", zh: "还可加 {n}", id: "Bisa tambah {n}" },
  memberExpireAtDate: { en: "Expires {date}", zh: "到期 {date}", id: "Berakhir {date}" },
  memberValidityPermanent: { en: "Permanent", zh: "永久", id: "Permanen" },
  memberValidityWithMembership: {
    en: "With membership",
    zh: "随会员期",
    id: "Mengikuti keanggotaan",
  },
  memberValidityDays: { en: "{days} days", zh: "{days} 天", id: "{days} hari" },
  memberDeliverAuto: { en: "Auto", zh: "自动", id: "Otomatis" },
  memberDeliverManual: { en: "Manual", zh: "手动", id: "Manual" },

  // ===== 选择会员档位（原型 screen-tiers） =====
  tierTitle: { en: "Choose your tier", zh: "选择会员", id: "Pilih keanggotaan" },
  /*
   * 档位标签（卡片副标题「／30 天 · 轻量创作者」的后半句）。
   * 2026-10-16 原型定稿：与档位名是两个字段 —— 名字是「入门版 / 专业版 / 专业直播版」，
   * 标签是营销定位语「轻量创作者 / 团队创作者 / 专业创作者」。
   */
  tierTagLight: { en: "Light creator", zh: "轻量创作者", id: "Kreator ringan" },
  tierTagTeam: { en: "Team creator", zh: "团队创作者", id: "Kreator tim" },
  tierTagPro: { en: "Pro creator", zh: "专业创作者", id: "Kreator profesional" },
  tierDesc: {
    en: "Membership is required before you can top up beans. Membership lasts 30 days; each benefit runs on its own schedule (some follow the membership period, some are granted monthly).",
    zh: "会员是「充梦想豆」的前置 —— 先成为会员，才能充值梦想豆。会员有效期 30 天；每项权益按自己的期限执行（有的随会员期、有的按月发放）。",
    id: "Keanggotaan adalah prasyarat sebelum isi ulang kacang. Keanggotaan berlaku 30 hari; setiap manfaat berjalan sesuai jadwalnya sendiri (ada yang mengikuti masa keanggotaan, ada yang diberikan bulanan).",
  },
  tierCode: { en: "Tier {code}", zh: "档位 {code}", id: "Paket {code}" },
  /*
   * 卡片副标题第一句。原型中文写的是「／30 天」，全角斜杠是中文排版的装饰分隔符；
   * 英文/印尼语没有这个习惯，且全角字符在英文界面属于「中文残留」⇒ 这两种语言直接给纯文本。
   */
  tierDays: { en: "{days} days", zh: "／{days} 天", id: "{days} hari" },
  tierIncluded: { en: "Included benefits", zh: "本档权益", id: "Manfaat paket ini" },
  tierIncludesLower: {
    en: "All 50-tier benefits included",
    zh: "含 50 条档全部权益",
    id: "Termasuk semua manfaat paket 50",
  },
  tierIncludesLowerNamed: {
    en: "All {name} benefits included",
    zh: "含 {name} 全部权益",
    id: "Termasuk semua manfaat {name}",
  },
  tierExclusive: { en: "Exclusive to this tier", zh: "本档专属", id: "Khusus paket ini" },
  tierCount: { en: "{n} items", zh: "{n} 项", id: "{n} item" },
  /*
   * 单数形态。中文与印尼语没有单复数变化，但英文有 ——
   * 「Exclusive to this tier · 1 items」是明显错误，故按 n 分流。
   */
  tierCountOne: { en: "{n} item", zh: "{n} 项", id: "{n} item" },
  tierDiffNote: {
    en: "The only difference from the 50-tier: 120 hours of AI live ops.",
    zh: "与 50 条档的差异只有一项：AI 直播代运营 120 小时。",
    id: "Satu-satunya perbedaan dengan paket 50: 120 jam operasional live AI.",
  },
  tierLoading: { en: "Loading tiers…", zh: "加载档位…", id: "Memuat paket…" },
  tierLoadFailed: { en: "Could not load tiers", zh: "加载档位失败", id: "Gagal memuat paket" },
  tierEmpty: { en: "No tiers available", zh: "暂无可选档位", id: "Belum ada paket" },
  tierBuyNow: { en: "Buy / Upgrade", zh: "购买 / 升级", id: "Beli / Tingkatkan" },
  tierBeanSuffix: { en: "beans", zh: "梦想豆", id: "kacang" },
  tierOriginalPrice: { en: "¥{price}", zh: "¥{price}", id: "¥{price}" },
  tierSubscribeFailed: { en: "Order failed", zh: "下单失败", id: "Gagal membuat pesanan" },
  tierLowerDisabled: {
    en: "Downgrade is not allowed while membership is active",
    zh: "会员期内不可降级",
    id: "Tidak bisa turun paket saat keanggotaan aktif",
  },

  // ===== 线下付款 · 待确认页（原型 screen-payment） =====
  payTitle: { en: "Offline payment · awaiting confirmation", zh: "线下付款 · 待确认", id: "Pembayaran offline · menunggu konfirmasi" },
  paySubTitle: {
    en: 'You land here after ordering — contact us first to get the bank details, transfer offline, then tap "I\'ve paid" to notify us',
    zh: "下单后进入此页 —— 先联系客服获取收款账户，线下转账完成后点「我已付款」通知我们",
    id: 'Anda masuk ke halaman ini setelah memesan — hubungi kami dulu untuk info rekening, transfer offline, lalu ketuk "Saya sudah bayar"',
  },
  payOrderSummary: { en: "Order summary", zh: "订单概要", id: "Ringkasan pesanan" },
  paySupportTitle: { en: "Need help? Contact us", zh: "遇到问题？联系客服", id: "Ada masalah? Hubungi kami" },
  payColOrderNo: { en: "Order no.", zh: "订单号", id: "No. pesanan" },
  payColType: { en: "Type", zh: "类型", id: "Tipe" },
  payColAmount: { en: "Amount", zh: "金额", id: "Jumlah" },
  payColCreatedAt: { en: "Ordered at", zh: "下单时间", id: "Waktu pesan" },
  payColChannel: { en: "Channel", zh: "支付渠道", id: "Kanal" },
  payTimeout: { en: "Expires in", zh: "超时", id: "Kedaluwarsa" },
  payRemainTime: { en: "{h} h {m} min left", zh: "剩 {h} 小时 {m} 分", id: "Sisa {h} jam {m} menit" },
  payTimeoutExpired: { en: "Expired", zh: "已超时", id: "Kedaluwarsa" },
  /** 联系段引导：加粗标题段（带步骤编号） */
  payStep1HintBold: { en: "Step 1 — Contact us.", zh: "第 1 步 — 联系我们。", id: "Langkah 1 — Hubungi kami." },
  /** 联系段引导：说明段 */
  payStep1HintRest: {
    en: "Send your order info to our email below; our support team will reply with the bank account for offline transfer.",
    zh: "把下方订单信息发送到客服邮箱，客服会回复线下转账的收款账户。",
    id: "Kirim info pesanan Anda ke email kami di bawah; tim dukungan akan membalas dengan rekening untuk transfer offline.",
  },
  /** 通知段引导：加粗标题段（带步骤编号） */
  payStep2HintBold: {
    en: "Step 2 — After your offline transfer is done, notify us.",
    zh: "第 2 步 — 线下支付完成后，通知我们。",
    id: "Langkah 2 — Setelah transfer offline selesai, beri tahu kami.",
  },
  /** 通知段引导：说明段 */
  payStep2HintRest: {
    en: "Transfer to the account our support gave you by email, include the order no. as the transfer reference, then tap the button.",
    zh: "转账到客服邮件提供的账户，转账时备注订单号，然后点击下方按钮通知我们。",
    id: "Transfer ke rekening yang diberikan tim dukungan via email, cantumkan no. pesanan sebagai berita transfer, lalu ketuk tombol.",
  },
  /** 联系卡说明：把订单号发给客服邮箱换取收款账户 */
  payContactLabel: {
    en: "Email us with your order no. to get the bank account",
    zh: "请将订单号发送到客服邮箱，我们会提供收款账户",
    id: "Kirim no. pesanan Anda ke email kami untuk mendapatkan rekening",
  },
  /** 已通知横幅说明：「已通知」≠「已支付」，财务核验后才发货 */
  payBannerDesc: {
    en: 'We\'ve received your payment notice. Finance will verify the transfer — benefits are delivered after confirmation. "Notified" does not mean "paid & delivered" yet.',
    zh: "我们已收到您的付款通知，财务核验到账后即为您发货。「已通知」不代表「已支付 / 已到账」。",
    id: 'Kami telah menerima pemberitahuan pembayaran Anda. Keuangan akan memverifikasi transfer — manfaat diberikan setelah konfirmasi. "Sudah diberitahu" belum berarti "sudah dibayar & diterima".',
  },
  payNotifyBtn: { en: "I've paid — notify us", zh: "我已付款，点这里通知我们", id: "Saya sudah bayar — beri tahu kami" },
  payNotifiedBtn: { en: "Notified · awaiting confirmation", zh: "已通知 · 等待财务确认", id: "Sudah diberitahu · menunggu konfirmasi" },
  payCopied: { en: "Copied", zh: "已复制", id: "Disalin" },
  payLoading: { en: "Loading…", zh: "加载中…", id: "Memuat…" },
  payLoadFailed: { en: "Could not load the order", zh: "订单加载失败", id: "Gagal memuat pesanan" },
  payNotifyOk: {
    en: "Thanks — we'll confirm shortly",
    zh: "已收到通知，我们会尽快确认到账",
    id: "Terima kasih — kami akan segera konfirmasi",
  },
  payNotifyFailed: {
    en: "Could not send the notification, please retry",
    zh: "通知发送失败，请重试",
    id: "Gagal mengirim notifikasi, coba lagi",
  },
  payStatusPaid: { en: "Paid", zh: "已支付", id: "Dibayar" },
  payStatusRefunded: { en: "Refunded", zh: "已退款", id: "Dikembalikan" },
  payBackToMember: { en: "My membership", zh: "我的会员", id: "Keanggotaan saya" },

  // ==================== 管理端 · 订单记录（Orders）页 ====================
  adminAllTenants: { en: "All tenants", zh: "全部企业", id: "Semua tenant" },
  adminSearchUserPlaceholder: { en: "Search username / nickname", zh: "搜索用户名 / 昵称", id: "Cari nama pengguna" },
  adminDateRangeTo: { en: "to", zh: "至", id: "sampai" },
  adminDateStart: { en: "Start date", zh: "开始日期", id: "Tanggal mulai" },
  adminDateEnd: { en: "End date", zh: "结束日期", id: "Tanggal akhir" },
  adminSearchBtn: { en: "Search", zh: "查询", id: "Cari" },
  adminResetBtn: { en: "Reset", zh: "重置", id: "Reset" },
  adminTabOrders: { en: "Orders", zh: "订单记录", id: "Pesanan" },
  adminLoadingSummary: { en: "Loading summary...", zh: "加载汇总...", id: "Memuat ringkasan..." },
  adminSummaryAmount: { en: "Total paid amount", zh: "成功充值总额", id: "Total pembayaran berhasil" },
  adminSummarySuccessCount: { en: "Successful orders", zh: "成功笔数", id: "Transaksi berhasil" },
  adminSummaryTotalCount: { en: "Total orders", zh: "总订单数", id: "Total pesanan" },
  adminGroupStatsTitle: { en: "Recharge group stats", zh: "充值分组统计", id: "Statistik grup isi ulang" },
  adminGroupByDay: { en: "Daily", zh: "按日", id: "Harian" },
  adminGroupByMonth: { en: "Monthly", zh: "按月", id: "Bulanan" },
  adminGroupByTenant: { en: "By tenant", zh: "按企业", id: "Per tenant" },
  adminLoading: { en: "Loading...", zh: "加载中...", id: "Memuat..." },
  adminNoGroupData: { en: "No group data", zh: "暂无分组数据", id: "Belum ada data grup" },
  adminColTenant: { en: "Tenant", zh: "企业", id: "Tenant" },
  adminColPeriod: { en: "Period", zh: "周期", id: "Periode" },
  adminColSuccessAmount: { en: "Paid amount", zh: "成功金额", id: "Jumlah berhasil" },
  adminLoadingDetail: { en: "Loading details...", zh: "加载明细...", id: "Memuat detail..." },
  adminNoOrders: { en: "No recharge orders", zh: "暂无充值订单", id: "Belum ada pesanan isi ulang" },
  adminColOrderNo: { en: "Order No.", zh: "订单编号", id: "No. Pesanan" },
  adminColInitiator: { en: "Initiator", zh: "发起人", id: "Pemohon" },
  adminColStatus: { en: "Status", zh: "状态", id: "Status" },
  adminColProduct: { en: "Product", zh: "商品描述", id: "Produk" },
  adminColAmount: { en: "Amount", zh: "金额", id: "Jumlah" },
  adminColPayChannel: { en: "Payment method", zh: "支付方式", id: "Metode pembayaran" },
  adminColPayTime: { en: "Paid at", zh: "支付时间", id: "Waktu bayar" },
  adminRechargeFallback: { en: "Recharge", zh: "充值", id: "Isi ulang" },
  /** 订单列表：待支付单「继续支付」按钮，重新拉起 PingPong 收银台 */
  adminRepayAction: { en: "Pay now", zh: "继续支付", id: "Bayar" },
  /** 订单列表：重新拉起收银台失败提示（常见于订单已超时关单） */
  adminRepayFailed: {
    en: "Failed to open the cashier. The order may have expired.",
    zh: "拉起收银台失败，订单可能已超时关闭",
    id: "Gagal membuka kasir. Pesanan mungkin telah kedaluwarsa.",
  },
  adminLoadFailed: { en: "Load failed", zh: "加载失败", id: "Gagal memuat" },
  /** 财务-充值记录：操作列表头（仅线下转账模式显示） */
  adminColAction: { en: "Actions", zh: "操作", id: "Aksi" },
  /** 线下充值：确认到账（唯一的发货闸门） */
  adminConfirmReceipt: { en: "Confirm receipt", zh: "确认到账", id: "Konfirmasi dana masuk" },
  /** 线下充值：驳回（必填原因） */
  adminRejectReceipt: { en: "Reject", zh: "驳回", id: "Tolak" },
  /** 已支付订单：发起退款（撤佣 + 回收权益） */
  adminRefundOrder: { en: "Refund", zh: "退款", id: "Refund" },
  /** 待支付订单上的一枚提示：用户已点过「我已付款」，等运营核销 */
  adminPaidNotified: { en: "Buyer says paid", zh: "用户已声明付款", id: "Pembeli klaim sudah bayar" },
  /** 确认到账二次确认弹窗（标题 / 正文 / 备注框 / 结果） */
  adminConfirmTitle: { en: "Confirm receipt · {orderNo}", zh: "确认到账 · {orderNo}", id: "Konfirmasi dana masuk · {orderNo}" },
  adminConfirmPrompt: {
    en: "Confirm that {amount} for \u201c{item}\u201d has been received? Benefits are delivered immediately and this cannot be undone.",
    zh: "确认「{item}」{amount} 已收到款项？确认后将立即发货，且不可撤销。",
    id: "Konfirmasi bahwa {amount} untuk \u201c{item}\u201d sudah diterima? Manfaat langsung diberikan dan tidak dapat dibatalkan.",
  },
  adminConfirmBeansNote: { en: " ({count} beans credited)", zh: "（到账 {count} 梦想豆）", id: " ({count} biji masuk)" },
  adminConfirmRemarkPlaceholder: {
    en: "Note (optional): transfer reference / receipt details",
    zh: "备注（可选）：转账流水号 / 收款凭证说明",
    id: "Catatan (opsional): nomor referensi transfer / bukti terima",
  },
  adminConfirmSuccess: {
    en: "Receipt confirmed, order fulfilled",
    zh: "已确认到账并完成发货",
    id: "Dana masuk dikonfirmasi, pesanan diproses",
  },
  adminConfirmFailed: { en: "Failed to confirm receipt", zh: "确认到账失败", id: "Gagal mengonfirmasi dana masuk" },
  /** 驳回线下充值（必填原因）*/
  adminRejectTitle: { en: "Reject bank transfer · {orderNo}", zh: "驳回线下充值 · {orderNo}", id: "Tolak transfer bank · {orderNo}" },
  adminRejectPrompt: {
    en: "Reject order \u201c{item}\u201d ({amount})? Once rejected it can no longer be confirmed.",
    zh: "驳回订单「{item}」{amount}？驳回后该订单不可再确认到账。",
    id: "Tolak pesanan \u201c{item}\u201d ({amount})? Setelah ditolak tidak dapat dikonfirmasi lagi.",
  },
  adminRejectReasonPlaceholder: { en: "Reason for rejection (required)", zh: "驳回原因（必填）", id: "Alasan penolakan (wajib)" },
  adminRejectReasonRequired: { en: "Please enter a reason", zh: "请填写驳回原因", id: "Mohon isi alasan penolakan" },
  adminRejectSuccess: { en: "Order rejected", zh: "已驳回该订单", id: "Pesanan ditolak" },
  adminRejectFailed: { en: "Failed to reject order", zh: "驳回失败", id: "Gagal menolak pesanan" },
  /** 财务后台「企业」列：tenant_id 为空（个人账户）时的展示名，后端下发的是中文「个人账户」 */
  adminPersonalAccount: { en: "Personal account", zh: "个人账户", id: "Akun pribadi" },
  /** 线下转账模式下点「继续支付」：线上支付已关闭 */
  adminRepayOnlineDisabled: {
    en: "Online payment is disabled in bank-transfer mode. Please confirm the bank transfer instead.",
    zh: "当前为线下转账模式，线上支付已关闭，请按银行到账确认",
    id: "Pembayaran online dinonaktifkan pada mode transfer bank. Konfirmasi dana masuk saja.",
  },
  adminNoPermission: {
    en: "You do not have access to this page",
    zh: "您没有权限访问该页面",
    id: "Anda tidak memiliki akses ke halaman ini",
  },

  // ==================== 支付状态 / 支付渠道（powerRecord 工具函数三语化用）====================
  payStatusSuccess: { en: "Paid", zh: "支付成功", id: "Berhasil dibayar" },
  payStatusPending: { en: "Pending", zh: "待支付", id: "Menunggu" },
  payStatusFailed: { en: "Failed", zh: "支付失败", id: "Gagal" },
  payStatusTimeout: { en: "Expired", zh: "支付超时", id: "Kedaluwarsa" },
  payStatusRefundFailed: { en: "Refund failed", zh: "退款失败", id: "Gagal refund" },
  payStatusUnknown: { en: "Unknown", zh: "未知", id: "Tidak diketahui" },
  payChannelWx: { en: "WeChat Pay", zh: "微信支付", id: "WeChat Pay" },
  payChannelAlipay: { en: "Alipay", zh: "支付宝", id: "Alipay" },
  payChannelBank: { en: "Bank card", zh: "银行卡", id: "Kartu bank" },
  payChannelOffline: { en: "Offline transfer", zh: "线下转账", id: "Transfer offline" },
  /** 线上收银台渠道（PingPong）：付款页在 online 模式下展示用 */
  payChannelPingPong: { en: "Online payment", zh: "在线支付", id: "Pembayaran online" },
  /** 付款页「类型」列的订单类型名（后端 orderTypeName 是中文快照，非中文环境走这里） */
  payOrderTypeMember: { en: "Membership", zh: "会员", id: "Keanggotaan" },
  payOrderTypeBean: { en: "Dream Beans top-up", zh: "梦想豆充值", id: "Isi ulang Kacang Impian" },
  payOrderTypeGrant: { en: "System grant", zh: "系统赠送", id: "Bonus sistem" },

  // ==================== 管理端 · 退款撤佣页（/admin/refund） ====================
  rfTitle: {
    en: "Refund · revoke commission & reclaim benefits",
    zh: "退款 · 撤佣 + 回收权益",
    id: "Refund · tarik komisi & ambil kembali manfaat",
  },
  rfSubtitle: {
    en: "Manual offline refund — full or proportional revocation in a single transaction",
    zh: "线下人工退款触发 —— 全额撤销 / 部分按比例撤销，同事务串行执行",
    id: "Refund offline manual — pencabutan penuh atau proporsional dalam satu transaksi",
  },
  rfOrderNoPlaceholder: {
    en: "Enter order no., e.g. PO20261011007",
    zh: "输入订单号，如 PO20261011007",
    id: "Masukkan no. pesanan, mis. PO20261011007",
  },
  rfLoadOrder: { en: "Load order", zh: "加载订单", id: "Muat pesanan" },
  rfEmptyHint: {
    en: "Enter an order number and click \u201cLoad order\u201d to preview the refund",
    zh: "输入订单号后点击「加载订单」查看退款撤佣预览",
    id: "Masukkan nomor pesanan lalu klik \u201cMuat pesanan\u201d untuk pratinjau refund",
  },
  rfReasonRefunded: { en: "This order has already been refunded", zh: "订单已退款", id: "Pesanan ini sudah direfund" },
  rfReasonUnpaid: {
    en: "The order is unpaid, so it cannot be refunded",
    zh: "订单未支付，无法退款",
    id: "Pesanan belum dibayar, tidak dapat direfund",
  },
  rfReasonNotRefundable: {
    en: "This order cannot be refunded right now",
    zh: "该订单当前不可退款",
    id: "Pesanan ini tidak dapat direfund saat ini",
  },
  rfSectionOrder: { en: "Order details", zh: "订单信息", id: "Detail pesanan" },
  rfLabelOrderNo: { en: "Order no.", zh: "订单号", id: "No. pesanan" },
  rfLabelUser: { en: "User", zh: "用户", id: "Pengguna" },
  rfLabelType: { en: "Type", zh: "类型", id: "Tipe" },
  rfLabelAmount: { en: "Original amount", zh: "原订单金额", id: "Jumlah awal" },
  rfLabelCreatedAt: { en: "Created at", zh: "下单时间", id: "Waktu pesanan" },
  rfLabelRecommender: { en: "Referrer", zh: "推荐官", id: "Referrer" },
  rfLabelRefunded: { en: "Already refunded", zh: "已退金额", id: "Sudah direfund" },
  rfSectionAmount: { en: "Refund amount", zh: "退款金额", id: "Jumlah refund" },
  rfModeFull: { en: "Full refund", zh: "全额退款", id: "Refund penuh" },
  rfModePartial: { en: "Partial refund", zh: "部分退款", id: "Refund sebagian" },
  rfThisRefund: { en: "Refund now", zh: "本次退款", id: "Refund sekarang" },
  rfFullEquals: { en: "(full = {amount})", zh: "（全额 = {amount}）", id: "(penuh = {amount})" },
  rfCalcRuleBefore: {
    en: "Partial refunds revoke commission proportionally to",
    zh: "部分退款按",
    id: "Refund sebagian mencabut komisi secara proporsional terhadap",
  },
  rfCalcRuleRatio: { en: "refund amount / order amount", zh: "退款金额 / 订单金额", id: "jumlah refund / jumlah pesanan" },
  rfCalcRuleAfter: {
    en: "; beans already consumed are not clawed back.",
    zh: "已消耗部分不追溯。",
    id: "; biji yang sudah terpakai tidak ditarik kembali.",
  },
  rfSectionRevoke: { en: "What will be revoked", zh: "将撤销 / 回收", id: "Yang akan dicabut" },
  rfSameTx: { en: "Same transaction, 3 steps", zh: "同事务，3 步串行", id: "Satu transaksi, 3 langkah" },
  rfStep1: { en: "Revoke membership / deduct beans", zh: "撤销会员 / 扣减豆", id: "Cabut keanggotaan / potong biji" },
  rfStep1Li1: {
    en: "· Membership order → membership expires / downgraded to guest / benefits frozen (permanent benefits are not rolled back)",
    zh: "· 会员订单 → 会员失效 / 降为游客 / 冻结权益（永久权益不回滚）",
    id: "· Pesanan keanggotaan → keanggotaan berakhir / kembali ke tamu / manfaat dibekukan (manfaat permanen tidak ditarik)",
  },
  rfStep1Li2: {
    en: "· Beans top-up → beans rolled back per the original order (team shared pool included)",
    zh: "· 豆充值 → 按原订单回滚梦想豆（含团队共享池）",
    id: "· Isi ulang biji → biji dikembalikan sesuai pesanan asal (termasuk pool bersama tim)",
  },
  rfStep1Li3Strong: { en: "Beans already spent are not clawed back", zh: "已用豆不追溯", id: "Biji yang sudah terpakai tidak ditarik" },
  rfStep1Li3After: {
    en: " (what the user has already consumed is not recovered)",
    zh: "（用户已经消费过的豆不追回）",
    id: " (yang sudah dipakai pengguna tidak diambil kembali)",
  },
  rfStep2: { en: "Revoke commission (L1 + L2)", zh: "撤销佣金（L1 + L2）", id: "Cabut komisi (L1 + L2)" },
  rfColLevel: { en: "Level", zh: "层级", id: "Level" },
  rfColBeneficiary: { en: "Beneficiary", zh: "受益人", id: "Penerima" },
  rfColOriginal: { en: "Original commission", zh: "原佣金", id: "Komisi awal" },
  rfColStatus: { en: "Status", zh: "状态", id: "Status" },
  rfColRevokeNow: { en: "Revoke now", zh: "本次撤", id: "Dicabut sekarang" },
  rfNoCommission: {
    en: "No commission records for this order (self-purchase or no referral chain)",
    zh: "该订单没有计佣记录（可能买家自购 / 无推荐链）",
    id: "Tidak ada catatan komisi untuk pesanan ini (beli sendiri / tanpa rantai referral)",
  },
  rfRevokeTotal: { en: "Total commission revoked", zh: "本次撤佣合计", id: "Total komisi dicabut" },
  rfStatusPending: { en: "Pending settlement", zh: "待结算", id: "Menunggu penyelesaian" },
  rfStatusSettled: { en: "Settled", zh: "已结算", id: "Sudah diselesaikan" },
  rfStatusCanceled: { en: "Revoked", zh: "已撤销", id: "Sudah dicabut" },
  rfActOffset: { en: "Offset against next payout", zh: "负数冲抵下期", id: "Kompensasi pembayaran berikutnya" },
  rfActRevoked: { en: "Already revoked", zh: "已撤销", id: "Sudah dicabut" },
  rfActDirect: { en: "Revoked directly", zh: "直接撤销", id: "Dicabut langsung" },
  rfStep3: { en: "Audit trail", zh: "留痕", id: "Jejak audit" },
  rfStep3Li1: { en: "· Refund reason is recorded", zh: "· 记录退款原因", id: "· Alasan refund dicatat" },
  rfStep3Li2: {
    en: "· Operator and refund time recorded ⇒ auditable",
    zh: "· 记录操作人与退款时间 ⇒ 审计可查",
    id: "· Operator dan waktu refund dicatat ⇒ dapat diaudit",
  },
  rfStep3Li3: {
    en: "· Order marked \u201cRefunded\u201d, with a reversing ledger entry",
    zh: "· 订单状态置为「已退款」，写入一条反向流水",
    id: "· Pesanan ditandai \u201cDirefund\u201d, dengan entri pembalik",
  },
  rfStep3Li4: {
    en: "· No gateway involved ⇒ no refund callback polling",
    zh: "· 不接网关 ⇒ 无需退款回调轮询",
    id: "· Tanpa gateway ⇒ tanpa polling callback refund",
  },
  rfReasonTitle: { en: "Refund reason", zh: "退款原因", id: "Alasan refund" },
  rfRequired: { en: "*Required", zh: "*必填", id: "*Wajib" },
  rfReasonPlaceholder: {
    en: "e.g. User requested a refund within 24h; bank transfer returned to origin",
    zh: "如：用户 24h 内申请退款，线下银行转账原路退回",
    id: "mis. Pengguna meminta refund dalam 24 jam; transfer bank dikembalikan ke asal",
  },
  rfConfirmBtn: { en: "⚠ Confirm refund (irreversible)", zh: "⚠ 确认退款（不可逆）", id: "⚠ Konfirmasi refund (tidak dapat dibatalkan)" },
  rfOperatorNote: {
    en: "Operator: current admin · Time: recorded on submit",
    zh: "操作人：当前管理员 · 时间：提交时自动记录",
    id: "Operator: admin saat ini · Waktu: dicatat saat dikirim",
  },
  rfErrNoOrderNo: { en: "Please enter an order no.", zh: "请输入订单号", id: "Mohon masukkan no. pesanan" },
  rfErrLoad: {
    en: "Load failed: order not found or access denied",
    zh: "加载失败：订单不存在或无权访问",
    id: "Gagal memuat: pesanan tidak ditemukan atau akses ditolak",
  },
  rfErrAmountRequired: { en: "Please enter a refund amount", zh: "请填写退款金额", id: "Mohon isi jumlah refund" },
  rfErrAmountPositive: { en: "Refund amount must be greater than 0", zh: "退款金额必须大于 0", id: "Jumlah refund harus lebih dari 0" },
  rfErrAmountExceed: {
    en: "Cannot exceed the order amount {amount}",
    zh: "不能超过订单金额 {amount}",
    id: "Tidak boleh melebihi jumlah pesanan {amount}",
  },
  rfConfirmBoxTitle: { en: "Confirm refund", zh: "确认退款", id: "Konfirmasi refund" },
  rfConfirmBoxMsg: {
    en: "Refund {amount}, revoke commission and reclaim benefits / beans.\nThis cannot be undone. Continue?",
    zh: "将退款 {amount}，并撤销佣金、回收权益 / 梦想豆。\n此操作不可逆，确认继续？",
    id: "Refund {amount}, cabut komisi dan ambil kembali manfaat / biji.\nTindakan ini tidak dapat dibatalkan. Lanjutkan?",
  },
  rfSuccess: {
    en: "Refund completed — commission revoked, benefits reclaimed",
    zh: "退款已完成，佣金已撤销、权益已回收",
    id: "Refund selesai — komisi dicabut, manfaat diambil kembali",
  },
  rfErrSubmit: {
    en: "Refund failed: check the order status or try again later",
    zh: "退款失败：请检查订单状态或稍后重试",
    id: "Refund gagal: periksa status pesanan atau coba lagi nanti",
  },

  /** 灵感中心：首屏标题里做渐变的前半句 */
  inspHeroAccent: { en: "Viral ideas", zh: "爆款灵感", id: "Ide viral" },
  /** 灵感中心：首屏标题后半句，含逗号 */
  inspHeroRest: { en: ", all in one place", zh: "，一站汇集", id: ", semua di satu tempat" },
  inspHeroDesc: {
    en: "Curated high-performing and shoppable videos from TikTok, Instagram, and YouTube. Find ideas and spark new directions.",
    zh: "精选 TikTok、Instagram、YouTube 高点赞爆款与带货视频，在这里汲取创意，打开脑洞。",
    id: "Video viral dan video jualan pilihan dari TikTok, Instagram, dan YouTube. Temukan ide dan buka inspirasi baru di sini.",
  },
  inspStatVideos: { en: "Curated videos", zh: "精选视频", id: "Video pilihan" },
  inspStatDaily: { en: "Daily", zh: "每日", id: "Harian" },
  inspStatUpdating: { en: "Updates", zh: "持续更新", id: "Diperbarui" },
  inspLoading: { en: "Loading inspiration videos", zh: "正在加载灵感视频", id: "Memuat video inspirasi" },
  inspEmpty: { en: "No videos on {date}", zh: "{date} 没有视频", id: "Tidak ada video pada {date}" },
  inspViewLast30: { en: "View last 30 days", zh: "查看近 30 天", id: "Lihat 30 hari terakhir" },
  inspLoadedAll: { en: "All {n} videos loaded", zh: "已加载全部 {n} 个视频", id: "Semua {n} video telah dimuat" },
  inspResourcePreparing: {
    en: "This video is still being prepared. Try again later.",
    zh: "该视频资源还在准备中，请稍后再试",
    id: "Video ini masih disiapkan. Coba lagi nanti.",
  },
  inspReplicatePrepareFailed: { en: "Couldn't prepare the remix", zh: "复刻准备失败", id: "Gagal menyiapkan remix" },
  inspPreviewLoading: { en: "Loading preview...", zh: "加载预览中...", id: "Memuat pratinjau..." },
  inspPreviewUnavailable: { en: "Preview isn't available yet", zh: "视频暂不可预览", id: "Pratinjau belum tersedia" },
  inspDetailTitle: { en: "Details", zh: "详细信息", id: "Detail" },
  inspPublishedAt: { en: "Published {date}", zh: "发布于 {date}", id: "Terbit {date}" },
  inspUnknownAuthor: { en: "Unknown creator", zh: "未知作者", id: "Kreator tidak dikenal" },
  inspFansCount: { en: "{n} followers", zh: "{n} 粉丝", id: "{n} pengikut" },
  inspCreatorLevel: { en: "Creator Lv.{n}", zh: "达人Lv.{n}", id: "Kreator Lv.{n}" },
  inspPlayCount: { en: "Views", zh: "播放量", id: "Tayangan" },
  inspLikeCount: { en: "Likes", zh: "点赞量", id: "Suka" },
  inspDuration: { en: "Duration", zh: "时长", id: "Durasi" },
  inspAspectRatio: { en: "Aspect ratio", zh: "宽高比例", id: "Rasio aspek" },
  inspOpenOn: { en: "Open on {platform}", zh: "在{platform}打开", id: "Buka di {platform}" },
  inspReplicateUnavailable: {
    en: "Still preparing. Remix isn't available yet.",
    zh: "资源准备中，暂不可复刻",
    id: "Masih disiapkan. Remix belum tersedia.",
  },
  inspReplicate: { en: "Remix", zh: "复刻", id: "Remix" },
  inspViewVideo: { en: "View {title}", zh: "查看{title}", id: "Lihat {title}" },
  inspGoodsBadge: { en: "Shop", zh: "带货", id: "Jualan" },
  inspPlayback: { en: "Playback progress", zh: "播放进度", id: "Progres pemutaran" },
  inspSyncEntry: { en: "Viral library sync", zh: "爆款库同步", id: "Sinkron pustaka viral" },
  inspDateLabel: { en: "Publish date: {range}", zh: "发布日期：{range}", id: "Tanggal terbit: {range}" },
  inspPickDate: { en: "Choose a publish date", zh: "选择发布日期", id: "Pilih tanggal terbit" },
  inspPrevMonth: { en: "Previous month", zh: "上个月", id: "Bulan sebelumnya" },
  inspNextMonth: { en: "Next month", zh: "下个月", id: "Bulan berikutnya" },
  inspPickEnd: { en: "{start} – Choose end date", zh: "{start} – 选择结束日期", id: "{start} – Pilih tanggal akhir" },
  inspPresetToday: { en: "Today", zh: "今天", id: "Hari ini" },
  inspPreset7: { en: "Last 7 days", zh: "近 7 天", id: "7 hari" },
  inspPreset30: { en: "Last 30 days", zh: "近 30 天", id: "30 hari" },
  inspPreset90: { en: "Last 90 days", zh: "近 90 天", id: "90 hari" },
  /** 日历星期，周一开头 */
  inspWdMon: { en: "Mon", zh: "一", id: "Sen" },
  inspWdTue: { en: "Tue", zh: "二", id: "Sel" },
  inspWdWed: { en: "Wed", zh: "三", id: "Rab" },
  inspWdThu: { en: "Thu", zh: "四", id: "Kam" },
  inspWdFri: { en: "Fri", zh: "五", id: "Jum" },
  inspWdSat: { en: "Sat", zh: "六", id: "Sab" },
  inspWdSun: { en: "Sun", zh: "日", id: "Min" },
  inspLoadBeautyFailed: { en: "Couldn't load the beauty board", zh: "加载美妆个护榜单失败", id: "Gagal memuat papan kecantikan" },
  inspLoadBoardFailed: { en: "Couldn't load the inspiration board", zh: "加载灵感榜单失败", id: "Gagal memuat papan inspirasi" },
  inspLoadDetailFailed: { en: "Couldn't load video details", zh: "加载视频详情失败", id: "Gagal memuat detail video" },
  /** 爆款库接口：视频还不能预览 */
  inspErrVideoNotReady: {
    en: "The video is not ready for preview yet. Please try again later.",
    zh: "视频暂不可预览，请稍后重试",
    id: "Video belum bisa dipratinjau. Coba lagi nanti.",
  },
  /** 爆款库接口：同步进行中 */
  inspErrDataUpdating: {
    en: "Data is updating. Please refresh later.",
    zh: "数据更新中，请稍后刷新",
    id: "Data sedang diperbarui. Segarkan nanti.",
  },
  inspErrSyncOk: { en: "Sync succeeded", zh: "同步成功", id: "Sinkron berhasil" },
  /** 爆款库接口：更新失败，页面数据可能过期 */
  inspErrUpdateStale: {
    en: "The update failed. Shown data may be out of date.",
    zh: "数据更新异常，当前展示数据可能不是最新",
    id: "Pembaruan gagal. Data yang ditampilkan mungkin belum terbaru.",
  },
  inspErrDownloadMissing: {
    en: "Missing video download URL",
    zh: "缺少视频下载链接（item_url）",
    id: "URL unduhan video tidak ada",
  },
  inspErrDownloadFailed: { en: "Download failed", zh: "下载失败", id: "Unduhan gagal" },
  inspErrSyncRunning: {
    en: "A sync job is already running",
    zh: "已有同步任务运行中",
    id: "Tugas sinkron sudah berjalan",
  },
  inspErrSyncSkipped: { en: "Sync is already running", zh: "同步任务已在运行", id: "Sinkronisasi sudah berjalan" },
  inspErrSyncRetry: {
    en: "Sync failed. Please try again later.",
    zh: "同步失败，请稍后重试",
    id: "Sinkron gagal. Coba lagi nanti.",
  },
  inspErrNoSync: { en: "No sync records yet", zh: "暂无同步记录", id: "Belum ada catatan sinkron" },
  inspErrTikHubToken: {
    en: "TikHub API token is missing or invalid. Check tikhub.api-token.",
    zh: "TikHub API Token 无效或未配置，请检查 tikhub.api-token",
    id: "Token API TikHub tidak ada atau tidak valid. Periksa tikhub.api-token.",
  },
  inspErrYoutubeNoMux: {
    en: "YouTube source has no mp4 video and audio to combine",
    zh: "YouTube 播放源没有可封装的 mp4 音视频",
    id: "Sumber YouTube tidak punya video dan audio mp4 untuk digabung",
  },
  inspErrYoutubeDownload: { en: "YouTube download failed", zh: "YouTube 成片下载失败", id: "Unduhan YouTube gagal" },
  inspErrYoutubeId: { en: "Invalid YouTube video id", zh: "YouTube videoId 无效", id: "ID video YouTube tidak valid" },
  inspErrYoutubeNoSource: {
    en: "No YouTube playback source is available",
    zh: "没有可用的 YouTube 播放源",
    id: "Tidak ada sumber pemutaran YouTube",
  },
  inspErrPlaybackEmpty: {
    en: "Playback source returned an empty response",
    zh: "播放源响应为空",
    id: "Sumber pemutaran mengembalikan respons kosong",
  },
  inspErrMediaSmall: { en: "Media file is too small", zh: "媒体文件过小", id: "File media terlalu kecil" },

  // ==== 资源库（/resource）三语文案分片（2026-10-27 深度三语化）====
  ...resPartPage,
  ...resPartUpload,
  ...resPartGallery,
  ...resPartPreview,
  ...resPartProduct,
  ...resPartDh,
};
